import {
  resolveArea
} from "./chunk-KK3XTKUJ.js";
import {
  CODES,
  EXPLAIN_URL_BASE,
  FactIndex,
  asComponentId,
  bridgeSourceViolation,
  bridgeSourceViolations,
  byCode,
  byRuleId,
  canonicalJson,
  compileComponentFacts,
  compileGlobalGovernanceFacts,
  componentGovernanceRecordSchema,
  componentGovernanceRecordsSchema,
  componentId,
  explainUrlForCode,
  factId,
  g,
  globalGovernanceRecordSchema,
  globalJsxGovernanceRecordSchema,
  globalStyleGovernanceRecordSchema,
  governanceConfigSchema,
  governanceSeveritySchema,
  hash64Hex,
  makeA11yNameRequiredFact,
  makeCanonicalCandidateFact,
  makeCanonicalMappingFact,
  makeCanonicalReplacementFact,
  makeClassNameDynamicFact,
  makeClassNameLiteralFact,
  makeComponentCapabilityFact,
  makeComponentMetadataFact,
  makeJsxComponentPreferredFact,
  makeJsxImportPathPreferredFact,
  makeJsxInlineStyleForbiddenRawFact,
  makeJsxUnknownPropsForbiddenFact,
  makePropMetadataFact,
  makePropValueAvoidedFact,
  makePropValueForbiddenFact,
  makeScaleFact,
  makeScaleValueFact,
  makeStructuralConfidenceFact,
  makeStyleCssVarsMustBeDefinedFact,
  makeStyleDeclarationFact,
  makeStyleFontSizeScaleFact,
  makeStylePropertyScaleFact,
  makeStyleRawColorForbiddenFact,
  makeStyleUnsupportedFact,
  makeTailwindClassFact,
  makeTailwindPaletteAllowFact,
  makeTailwindPaletteDenyFact,
  makeTailwindTokenResolvedFact,
  makeTailwindUnknownClassEnabledFact,
  makeThemeDeclarationFact,
  makeTokenDefinitionFact,
  makeUsageComponentFact,
  makeUsageImportFact,
  makeUsageInlineStyleFact,
  makeUsageNodeFact,
  makeUsagePropResolvedFact,
  makeUsageTextChildFact,
  matchesGlob,
  normalizeGovernanceConfig,
  resolveComponentGovernance,
  scaleGovernanceRecordSchema
} from "./chunk-O4IYGCB4.js";
import {
  agentFormatSchema,
  factEvidenceSchema,
  factLocationSchema,
  findingFixSchema,
  findingReplaceClassTokenFixSchema,
  findingReplaceComponentFixSchema,
  findingReplaceImportFixSchema,
  findingReplacePropValueFixSchema,
  findingReplaceStyleValueFixSchema,
  findingSchema,
  fixSchema,
  governanceVerdictMetadataSchema,
  governanceVerdictSchema,
  normalizeFinding,
  normalizeSeverity,
  normalizeViolation,
  suppressionDirectiveSchema,
  validatorResultSchema,
  violationSchema
} from "./chunk-R3X7UBHU.js";
import {
  SEVERITIES,
  SEVERITY_RANK,
  SEVERITY_WEIGHTS,
  compareSeverity,
  maxSeverity,
  severityLevel,
  severitySchema,
  sortBySeverity
} from "./chunk-V4VQB57N.js";
import {
  generateContext
} from "./chunk-X34IA4LR.js";
import {
  ComponentGraphEngine
} from "./chunk-SH4KPIYH.js";

// src/constants.ts
var BRAND = {
  /** Display name (e.g., "Fragments") */
  name: "Fragments",
  /** Lowercase name for file paths and CLI (e.g., "fragments") */
  nameLower: "fragments",
  /** File extension for fragment definition files — V2 canonical format */
  fileExtension: ".contract.json",
  /** Legacy file extension for segments (still supported for migration) */
  legacyFileExtension: ".segment.tsx",
  /** JSON file extension for compiled output */
  jsonExtension: ".fragment.json",
  /** Default output file name (e.g., "fragments.json") */
  outFile: "fragments.json",
  /** Config file name (e.g., "fragments.config.ts") */
  configFile: "fragments.config.ts",
  /** Legacy config file name (still supported for migration) */
  legacyConfigFile: "segments.config.ts",
  /** CLI command name (e.g., "fragments") */
  cliCommand: "fragments",
  /** Package scope (e.g., "@fragments") */
  packageScope: "@fragments",
  /** Directory for storing fragments, registry, and cache */
  dataDir: ".fragments",
  /** Components subdirectory within .fragments/ */
  componentsDir: "components",
  /** Registry file name */
  registryFile: "registry.json",
  /** Default compiler governance report path */
  governanceFile: ".fragments/governance.json",
  /** Context file name (AI-ready markdown) */
  contextFile: "context.md",
  /** Screenshots subdirectory */
  screenshotsDir: "screenshots",
  /** Cache subdirectory (gitignored) */
  cacheDir: "cache",
  /** Diff output subdirectory (gitignored) */
  diffDir: "diff",
  /** Manifest filename */
  manifestFile: "manifest.json",
  /** Prefix for localStorage keys (e.g., "fragments-") */
  storagePrefix: "fragments-",
  /** Static viewer HTML file name */
  viewerHtmlFile: "fragments-viewer.html",
  /** MCP tool name prefix (e.g., "fragments_") */
  mcpToolPrefix: "fragments_",
  /** File extension for block definition files */
  blockFileExtension: ".block.ts",
  /** @deprecated Use blockFileExtension instead */
  recipeFileExtension: ".recipe.ts",
  /** Vite plugin namespace */
  vitePluginNamespace: "fragments-core-shim"
};
var DEFAULTS = {
  /** Default viewport dimensions */
  viewport: {
    width: 1280,
    height: 800
  },
  /** Default diff threshold (percentage) */
  diffThreshold: 5,
  /** Browser pool size */
  poolSize: 3,
  /** Idle timeout before browser shutdown (ms) - 5 minutes */
  idleTimeoutMs: 5 * 60 * 1e3,
  /** Delay after render before capture (ms) */
  captureDelayMs: 100,
  /** Font loading timeout (ms) */
  fontTimeoutMs: 3e3,
  /** Default theme */
  theme: "light",
  /** Dev server port */
  port: 6006
};

// src/agent-format.ts
function buildAgentFormat(input) {
  const score = scoreFindings(input.findings);
  const agentFindings = input.findings.map((finding) => ({
    code: finding.code,
    ruleId: finding.ruleId,
    filePath: finding.location.file,
    line: finding.location.line,
    col: finding.location.column,
    severity: finding.severity,
    level: severityLevel(finding.severity),
    message: finding.message,
    evidence: finding.evidence,
    plan: planForFinding(finding)
  }));
  const hasBlocking = input.findings.some((finding) => {
    const level = severityLevel(finding.severity);
    return level === "error" || level === "warn";
  });
  return {
    schemaVersion: "typestyle.agent.v1",
    passed: (input.passed ?? true) && !hasBlocking,
    score,
    summary: agentSummary(input.findings.length, input.filesScanned, score),
    fix_first: agentFindings.filter((finding) => finding.plan.confidence >= 0.8),
    remaining: agentFindings.filter((finding) => finding.plan.confidence < 0.8),
    suppression_template: {
      directive: '// @fragments-expect-error {code} reason="..." expires="YYYY-MM-DD"',
      requiredFields: ["reason"],
      example: '// @fragments-expect-error FUI2001 reason="hero brand red approved" expires="2026-08-01"'
    },
    budget: {
      iteration: input.iteration ?? 0,
      maxIterations: input.maxIterations ?? 3,
      scoreDelta: input.previousScore === void 0 ? 0 : score - input.previousScore
    },
    findings: [...input.findings],
    ...input.suppressions ? {
      suppressions: input.suppressions.map((suppression) => ({
        code: suppression.code,
        reason: suppression.reason,
        ...suppression.expiresOn ? { expiresOn: suppression.expiresOn } : {},
        scope: "line"
      }))
    } : {}
  };
}
function scoreFindings(findings) {
  let penalty = 0;
  for (const finding of findings) {
    const level = severityLevel(finding.severity);
    penalty += level === "error" ? 20 : level === "warn" ? 8 : 2;
  }
  return Math.max(0, 100 - penalty);
}
function agentSummary(count, filesScanned, score) {
  if (count === 0) return `passed with score ${score}; no findings`;
  return `${count} finding${count === 1 ? "" : "s"} across ${filesScanned} file${filesScanned === 1 ? "" : "s"}; score ${score}`;
}
function planForFinding(finding) {
  if (finding.fix?.deterministic === true) {
    return {
      tool: "design_system/conform",
      args: {
        filePath: finding.location.file,
        fingerprint: finding.fingerprint,
        apply: "deterministic"
      },
      confidence: 0.95
    };
  }
  if (finding.code?.startsWith("FUI1") || finding.ruleId.includes("component") || finding.ruleId.includes("canonical")) {
    return {
      tool: "design_system/conform",
      args: { filePath: finding.location.file, apply: "deterministic" },
      confidence: 0.65
    };
  }
  return {
    tool: "design_system/conform",
    args: {
      filePath: finding.location.file,
      property: stringAttribute(finding.attributes?.property) ?? stringAttribute(finding.attributes?.prop) ?? "color",
      value: stringAttribute(finding.attributes?.rawValue) ?? stringAttribute(finding.attributes?.color),
      apply: "deterministic"
    },
    confidence: 0.55
  };
}
function stringAttribute(value) {
  return typeof value === "string" && value.length > 0 ? value : void 0;
}

// src/performance-presets.ts
var PRESETS = {
  strict: { bundleSize: 8 * 1024 },
  // 8KB gzipped
  standard: { bundleSize: 15 * 1024 },
  // 15KB gzipped
  relaxed: { bundleSize: 30 * 1024 }
  // 30KB gzipped
};
var PRESET_NAMES = Object.keys(PRESETS);
function resolvePerformanceConfig(input) {
  if (!input) {
    return { preset: "standard", budgets: PRESETS.standard };
  }
  if (typeof input === "string") {
    const budgets = PRESETS[input];
    if (!budgets) {
      throw new Error(
        `Unknown performance preset "${input}". Available: ${PRESET_NAMES.join(", ")}`
      );
    }
    return { preset: input, budgets };
  }
  const presetName = input.preset ?? "standard";
  const baseBudgets = PRESETS[presetName];
  if (!baseBudgets) {
    throw new Error(
      `Unknown performance preset "${presetName}". Available: ${PRESET_NAMES.join(", ")}`
    );
  }
  return {
    preset: presetName,
    budgets: {
      bundleSize: input.budgets?.bundleSize ?? baseBudgets.bundleSize
    }
  };
}
function classifyComplexity(gzipBytes) {
  if (gzipBytes < 5 * 1024) return "lightweight";
  if (gzipBytes < 15 * 1024) return "moderate";
  return "heavy";
}
function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes}B`;
  const kb = bytes / 1024;
  return kb < 10 ? `${kb.toFixed(1)}KB` : `${Math.round(kb)}KB`;
}
function budgetBar(percent, width = 20) {
  const filled = Math.min(Math.round(percent / 100 * width), width);
  const bar = "\u2588".repeat(filled) + "\u2591".repeat(width - filled);
  return percent > 100 ? `\x1B[31m${bar}\x1B[0m` : `\x1B[32m${bar}\x1B[0m`;
}

// src/bundle.ts
import { z } from "zod";
var bundleSchemaVersionSchema = z.literal(1);
var bundleTargetSchema = z.enum([
  "cursor",
  "agents",
  "claude",
  "copilot"
]);
var bundleArtifactMetadataSchema = z.object({
  kind: z.enum([
    "manifest",
    "tokens",
    "design-system",
    "readme",
    "helper",
    "component"
  ]),
  key: z.string().min(1),
  label: z.string().min(1),
  path: z.string().min(1),
  etag: z.string().min(1),
  updatedAt: z.string().datetime(),
  target: bundleTargetSchema.optional(),
  componentId: z.string().optional(),
  contentType: z.enum(["application/json", "text/markdown"])
});
var bundleManifestRelationSchema = z.object({
  type: z.string().min(1),
  componentId: z.string().min(1),
  name: z.string().min(1)
});
var bundleManifestCompoundChildSchema = z.object({
  componentId: z.string().min(1),
  name: z.string().min(1)
});
var bundleManifestComponentEntrySchema = z.object({
  componentId: z.string().min(1),
  file: z.string().min(1),
  name: z.string().min(1),
  publicSlug: z.string().nullable(),
  tier: z.enum(["core", "composition"]),
  category: z.string().min(1),
  status: z.string().min(1),
  description: z.string(),
  propCount: z.number().int().nonnegative(),
  hasExamples: z.boolean(),
  hasCompoundChildren: z.boolean(),
  relations: z.array(bundleManifestRelationSchema),
  compoundChildren: z.array(bundleManifestCompoundChildSchema)
});
var bundleManifestSchema = z.object({
  schemaVersion: bundleSchemaVersionSchema,
  catalogRevision: z.string().min(1),
  catalogUpdatedAt: z.string().datetime(),
  org: z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    slug: z.string().min(1)
  }),
  designSystem: z.object({
    name: z.string().min(1),
    packageName: z.string().nullable(),
    importPath: z.string().nullable()
  }),
  sourceBinding: z.object({
    bindingId: z.string().min(1),
    projectId: z.string().min(1),
    projectName: z.string().min(1),
    repoFullName: z.string().min(1),
    resolution: z.enum(["explicit", "auto"])
  }),
  totalComponents: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  tokenCategories: z.record(z.string(), z.number().int().nonnegative()),
  components: z.record(z.string(), bundleManifestComponentEntrySchema)
});
var bundleTokenEntrySchema = z.object({
  name: z.string().min(1),
  path: z.array(z.string().min(1)),
  category: z.string().min(1),
  value: z.string(),
  type: z.string().optional(),
  description: z.string().optional()
});
var bundleTokenFileSchema = z.object({
  schemaVersion: bundleSchemaVersionSchema,
  catalogRevision: z.string().min(1),
  catalogUpdatedAt: z.string().datetime(),
  categories: z.record(
    z.string(),
    z.object({
      count: z.number().int().nonnegative(),
      tokens: z.array(bundleTokenEntrySchema)
    })
  ),
  flat: z.array(bundleTokenEntrySchema)
});
var bundleComponentShardSchema = z.object({
  schemaVersion: bundleSchemaVersionSchema,
  catalogRevision: z.string().min(1),
  catalogUpdatedAt: z.string().datetime(),
  componentId: z.string().min(1),
  file: z.string().min(1),
  component: z.object({
    componentKey: z.string().min(1),
    publicRef: z.string().min(1),
    publicId: z.string().nullable(),
    publicSlug: z.string().nullable(),
    isCanonical: z.boolean().optional(),
    name: z.string().min(1),
    tier: z.enum(["core", "composition"]),
    category: z.string().min(1),
    status: z.string().min(1),
    description: z.string(),
    usageGuidance: z.string(),
    dos: z.array(z.string()),
    donts: z.array(z.string()),
    props: z.record(z.string(), z.unknown()),
    relations: z.array(z.record(z.string(), z.unknown())),
    compoundChildren: z.array(bundleManifestCompoundChildSchema),
    examples: z.array(z.record(z.string(), z.unknown())),
    source: z.literal("design-system"),
    sourcePath: z.string().optional(),
    sourceRepoFullName: z.string().optional(),
    parentComponentName: z.string().optional(),
    parentComponentKey: z.string().optional()
  })
});

// src/mcp-snapshot.ts
import { z as z2 } from "zod";
var mcpSnapshotSchemaVersionSchema = z2.literal(1);
var mcpSourceTypeSchema = z2.enum([
  "fragments-json",
  "cloud",
  "auto-extract",
  "custom-json",
  "bundle"
]);
var mcpCapabilitySchema = z2.enum([
  "components",
  "tokens",
  "blocks",
  "graph",
  "performance"
]);
var mcpPerformanceImportSchema = z2.object({
  path: z2.string().min(1),
  bytes: z2.number().nonnegative(),
  percent: z2.number().nonnegative()
});
var mcpPerformanceDataSchema = z2.object({
  bundleSize: z2.number().nonnegative(),
  rawSize: z2.number().nonnegative(),
  complexity: z2.enum(["lightweight", "moderate", "heavy"]),
  budgetPercent: z2.number().nonnegative(),
  overBudget: z2.boolean(),
  measuredAt: z2.string().datetime(),
  imports: z2.array(mcpPerformanceImportSchema).optional()
});
var mcpPerformanceSummarySchema = z2.object({
  preset: z2.string().min(1),
  budget: z2.number().nonnegative(),
  total: z2.number().int().nonnegative(),
  overBudget: z2.number().int().nonnegative(),
  tiers: z2.record(
    z2.enum(["lightweight", "moderate", "heavy"]),
    z2.number().int().nonnegative()
  )
});
var mcpComponentPropSchema = z2.object({
  type: z2.string().min(1),
  description: z2.string().default(""),
  required: z2.boolean().default(false),
  default: z2.unknown().optional(),
  values: z2.array(z2.string()).optional(),
  constraints: z2.array(z2.string()).optional()
});
var mcpComponentExampleSchema = z2.object({
  name: z2.string().min(1),
  description: z2.string().optional(),
  code: z2.string().optional(),
  kind: z2.string().optional()
});
var mcpComponentRelationSchema = z2.object({
  componentName: z2.string().min(1),
  componentId: z2.string().optional(),
  relationship: z2.string().min(1),
  note: z2.string().optional(),
  description: z2.string().optional()
});
var mcpCompoundChildSchema = z2.object({
  name: z2.string().min(1),
  componentId: z2.string().optional(),
  description: z2.string().optional(),
  required: z2.boolean().optional(),
  accepts: z2.array(z2.string()).optional(),
  visibility: z2.enum(["public", "internal"]).optional()
});
var mcpGuidancePatternSchema = z2.object({
  name: z2.string().min(1),
  description: z2.string().optional()
});
var mcpComponentGuidanceSchema = z2.object({
  when: z2.array(z2.string()).default([]),
  whenNot: z2.array(z2.string()).default([]),
  guidelines: z2.array(z2.string()).default([]),
  accessibility: z2.array(z2.string()).default([]),
  usageGuidance: z2.string().optional(),
  dos: z2.array(z2.string()).default([]),
  donts: z2.array(z2.string()).default([]),
  patterns: z2.array(mcpGuidancePatternSchema).default([])
});
var mcpComponentMetadataSchema = z2.object({
  a11yRules: z2.array(z2.string()).default([]),
  scenarioTags: z2.array(z2.string()).default([])
});
var mcpComponentSchema = z2.object({
  id: z2.string().min(1),
  name: z2.string().min(1),
  description: z2.string().default(""),
  category: z2.string().default("uncategorized"),
  status: z2.string().default("stable"),
  tags: z2.array(z2.string()).default([]),
  props: z2.record(z2.string(), mcpComponentPropSchema).default({}),
  propsSummary: z2.array(z2.string()).default([]),
  examples: z2.array(mcpComponentExampleSchema).default([]),
  relations: z2.array(mcpComponentRelationSchema).default([]),
  compoundChildren: z2.array(mcpCompoundChildSchema).default([]),
  guidance: mcpComponentGuidanceSchema.default({
    when: [],
    whenNot: [],
    guidelines: [],
    accessibility: [],
    dos: [],
    donts: [],
    patterns: []
  }),
  sourceType: mcpSourceTypeSchema,
  sourcePath: z2.string().optional(),
  sourceRepoFullName: z2.string().optional(),
  packageName: z2.string().optional(),
  importPath: z2.string().optional(),
  publicRef: z2.string().optional(),
  publicSlug: z2.string().nullable().optional(),
  isCanonical: z2.boolean().optional(),
  tier: z2.string().optional(),
  parentComponentId: z2.string().optional(),
  parentComponentName: z2.string().optional(),
  performance: mcpPerformanceDataSchema.optional(),
  metadata: mcpComponentMetadataSchema.default({
    a11yRules: [],
    scenarioTags: []
  })
});
var mcpBlockSchema = z2.object({
  id: z2.string().min(1),
  name: z2.string().min(1),
  description: z2.string().default(""),
  category: z2.string().default("uncategorized"),
  components: z2.array(z2.string()).default([]),
  tags: z2.array(z2.string()).default([]),
  code: z2.string().default("")
});
var mcpTokenSchema = z2.object({
  name: z2.string().min(1),
  category: z2.string().min(1),
  value: z2.string().optional(),
  description: z2.string().optional(),
  path: z2.array(z2.string()).optional(),
  type: z2.string().optional()
});
var mcpTokenDataSchema = z2.object({
  prefix: z2.string(),
  total: z2.number().int().nonnegative(),
  categories: z2.record(z2.string(), z2.array(mcpTokenSchema)),
  flat: z2.array(mcpTokenSchema).default([])
});
var mcpSnapshotMetadataSchema = z2.object({
  designSystemName: z2.string().optional(),
  packageName: z2.string().optional(),
  importPath: z2.string().optional(),
  revision: z2.string().optional(),
  updatedAt: z2.string().optional()
});
var mcpSnapshotSchema = z2.object({
  schemaVersion: mcpSnapshotSchemaVersionSchema,
  sourceType: mcpSourceTypeSchema,
  sourceLabel: z2.string().min(1),
  capabilities: z2.array(mcpCapabilitySchema),
  metadata: mcpSnapshotMetadataSchema.default({}),
  components: z2.record(z2.string(), mcpComponentSchema),
  blocks: z2.record(z2.string(), mcpBlockSchema).optional(),
  tokens: mcpTokenDataSchema.optional(),
  graph: z2.unknown().optional(),
  performanceSummary: mcpPerformanceSummarySchema.optional(),
  packageMap: z2.record(z2.string(), z2.string()).default({}),
  defaultPackageName: z2.string().optional()
});

// src/tokens/color.ts
var NAMED_COLORS = {
  white: { r: 255, g: 255, b: 255 },
  black: { r: 0, g: 0, b: 0 },
  transparent: { r: 0, g: 0, b: 0 },
  red: { r: 255, g: 0, b: 0 },
  green: { r: 0, g: 128, b: 0 },
  blue: { r: 0, g: 0, b: 255 }
};
function hexToRgb(hex) {
  const match = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!match) return null;
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16)
  };
}
function rgbToHex(r, g2, b) {
  return `#${[r, g2, b].map((x) => Math.round(x).toString(16).padStart(2, "0")).join("")}`;
}
function parseRgb(color) {
  const match = color.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/i
  );
  if (!match) return null;
  return {
    r: parseInt(match[1], 10),
    g: parseInt(match[2], 10),
    b: parseInt(match[3], 10),
    a: match[4] ? parseFloat(match[4]) : void 0
  };
}
function parseColor(color) {
  const hex = hexToRgb(color);
  if (hex) return hex;
  const rgb = parseRgb(color);
  if (rgb) {
    return {
      ...rgb,
      a: rgb.a ?? 1
    };
  }
  return null;
}
function parseColorToRgb(color) {
  const lower = color.toLowerCase().trim();
  const named = NAMED_COLORS[lower];
  if (named) return named;
  const parsed = parseColor(color);
  if (!parsed) return null;
  return {
    r: parsed.r,
    g: parsed.g,
    b: parsed.b
  };
}
function normalizeColor(color) {
  if (color.startsWith("#")) {
    return color.toLowerCase();
  }
  const rgb = parseRgb(color);
  if (rgb) {
    return rgbToHex(rgb.r, rgb.g, rgb.b);
  }
  return color.toLowerCase();
}
function isColorLike(value) {
  return value.startsWith("#") || value.startsWith("rgb") || value.startsWith("hsl") || /^(transparent|white|black|red|blue|green|yellow|orange|purple|pink|gray|grey)$/i.test(
    value
  );
}
function familyDistance(c1, c2) {
  const maxDistance = Math.sqrt(255 ** 2 * 3);
  return Math.sqrt((c1.r - c2.r) ** 2 + (c1.g - c2.g) ** 2 + (c1.b - c2.b) ** 2) / maxDistance;
}
function colorSimilarity(c1, c2) {
  return 1 - familyDistance(c1, c2);
}
function calculateDeltaE(c1, c2) {
  const lab1 = rgbToLab(c1);
  const lab2 = rgbToLab(c2);
  return Math.sqrt(
    Math.pow(lab2.l - lab1.l, 2) + Math.pow(lab2.a - lab1.a, 2) + Math.pow(lab2.b - lab1.b, 2)
  );
}
function nearestByDeltaE(target, candidates, resolveColor, options = {}) {
  const targetRgb = typeof target === "string" ? parseColorToRgb(target) : target;
  if (!targetRgb) return [];
  const threshold = options.threshold ?? Number.POSITIVE_INFINITY;
  const confidenceScale = Number.isFinite(threshold) && threshold > 0 ? threshold : 100;
  const matches = [];
  for (const candidate of candidates) {
    const resolved = resolveColor(candidate);
    const rgb = typeof resolved === "string" ? parseColorToRgb(resolved) : resolved;
    if (!rgb) continue;
    const distance = calculateDeltaE(targetRgb, rgb);
    if (distance <= threshold) {
      matches.push({
        item: candidate,
        distance,
        confidence: Math.max(0, 1 - distance / confidenceScale)
      });
    }
  }
  matches.sort((a, b) => a.distance - b.distance);
  return matches.slice(0, options.limit ?? matches.length);
}
function rgbToLab(rgb) {
  let r = rgb.r / 255;
  let g2 = rgb.g / 255;
  let b = rgb.b / 255;
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g2 = g2 > 0.04045 ? Math.pow((g2 + 0.055) / 1.055, 2.4) : g2 / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
  let x = (r * 0.4124564 + g2 * 0.3575761 + b * 0.1804375) / 0.95047;
  let y = r * 0.2126729 + g2 * 0.7151522 + b * 0.072175;
  let z6 = (r * 0.0193339 + g2 * 0.119192 + b * 0.9503041) / 1.08883;
  x = x > 8856e-6 ? Math.pow(x, 1 / 3) : 7.787 * x + 16 / 116;
  y = y > 8856e-6 ? Math.pow(y, 1 / 3) : 7.787 * y + 16 / 116;
  z6 = z6 > 8856e-6 ? Math.pow(z6, 1 / 3) : 7.787 * z6 + 16 / 116;
  return {
    l: 116 * y - 16,
    a: 500 * (x - y),
    b: 200 * (y - z6)
  };
}

// src/style-comparison.ts
var COLOR_PROPERTIES = /* @__PURE__ */ new Set(["backgroundColor", "borderColor", "color"]);
var NUMERIC_PROPERTIES = /* @__PURE__ */ new Set([
  "borderWidth",
  "borderRadius",
  "fontSize",
  "padding",
  "gap"
]);
var DEFAULT_STYLE_PROPERTIES = [
  "backgroundColor",
  "borderColor",
  "borderWidth",
  "borderRadius",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "lineHeight",
  "letterSpacing",
  "textAlign",
  "boxShadow",
  "padding",
  "gap",
  "opacity"
];
var DEFAULT_ENHANCED_STYLE_PROPERTIES = [
  ...DEFAULT_STYLE_PROPERTIES,
  "color"
];
function normalizeStyleValue(prop, value) {
  let normalized = value.trim().replace(/\s+/g, " ");
  if (prop === "boxShadow" && normalized === "none") {
    normalized = "";
  }
  if (normalized.match(/rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/)) {
    normalized = "transparent";
  }
  return normalized;
}
function compareColors(color1, color2, tolerance, alphaTolerance = 0.05) {
  const rgb1 = parseColor(color1);
  const rgb2 = parseColor(color2);
  if (!rgb1 || !rgb2) {
    return color1 === color2;
  }
  return Math.abs(rgb1.r - rgb2.r) <= tolerance && Math.abs(rgb1.g - rgb2.g) <= tolerance && Math.abs(rgb1.b - rgb2.b) <= tolerance && Math.abs((rgb1.a ?? 1) - (rgb2.a ?? 1)) <= alphaTolerance;
}
function compareNumericValues(value1, value2, tolerance) {
  const num1 = parseFloat(value1);
  const num2 = parseFloat(value2);
  if (isNaN(num1) || isNaN(num2)) {
    return value1 === value2;
  }
  return Math.abs(num1 - num2) <= tolerance;
}
function compareStyleValue(prop, figma2, rendered) {
  const normalizedFigma = normalizeStyleValue(prop, figma2);
  const normalizedRendered = normalizeStyleValue(prop, rendered);
  if (normalizedFigma === normalizedRendered) {
    return true;
  }
  if (COLOR_PROPERTIES.has(prop)) {
    return compareColors(normalizedFigma, normalizedRendered, 5);
  }
  if (NUMERIC_PROPERTIES.has(prop)) {
    return compareNumericValues(normalizedFigma, normalizedRendered, 1);
  }
  return false;
}
function compareStyles(figmaStyles, renderedStyles) {
  const properties = [];
  const cleanFigmaStyles = {};
  const propsToCompare = DEFAULT_STYLE_PROPERTIES;
  for (const prop of propsToCompare) {
    const figmaValue = figmaStyles[prop];
    const renderedValue = renderedStyles[prop];
    if (figmaValue !== void 0) {
      cleanFigmaStyles[prop] = figmaValue;
      const match = compareStyleValue(prop, figmaValue, renderedValue || "");
      properties.push({
        property: prop,
        figma: figmaValue,
        rendered: renderedValue || "(not set)",
        match
      });
    }
  }
  const allMatch = properties.every((p) => p.match);
  return {
    match: allMatch,
    properties,
    figmaStyles: cleanFigmaStyles,
    renderedStyles
  };
}
function compareStylesWithTokens(figmaStyles, renderedStyles, tokenLookup, theme = "default") {
  const properties = [];
  const cleanFigmaStyles = {};
  const propsToCompare = DEFAULT_ENHANCED_STYLE_PROPERTIES;
  for (const prop of propsToCompare) {
    const figmaValue = figmaStyles[prop];
    const renderedValue = renderedStyles[prop];
    if (figmaValue !== void 0) {
      cleanFigmaStyles[prop] = figmaValue;
      const match = compareStyleValue(prop, figmaValue, renderedValue || "");
      const item = {
        property: prop,
        figma: figmaValue,
        rendered: renderedValue || "(not set)",
        match,
        isHardcoded: false
      };
      if (tokenLookup) {
        const figmaTokens = tokenLookup.findByValue(figmaValue, theme);
        const renderedTokens = renderedValue ? tokenLookup.findByValue(renderedValue, theme) : [];
        if (figmaTokens.length > 0) {
          item.figmaToken = figmaTokens[0];
        }
        if (renderedTokens.length > 0) {
          item.renderedToken = renderedTokens[0];
        }
        item.isHardcoded = !!item.figmaToken && !item.renderedToken;
        if (item.isHardcoded && item.figmaToken) {
          const token = tokenLookup.getToken(item.figmaToken);
          if (token) {
            const cssProperty = toCssProperty(prop);
            item.suggestedFix = {
              tokenName: item.figmaToken,
              tokenValue: token.resolvedValue,
              codeFix: `${cssProperty}: var(${item.figmaToken});`,
              confidence: 0.9,
              reason: `Figma uses token ${item.figmaToken} (${token.resolvedValue}). Replace hardcoded value with token for consistency.`
            };
          }
        }
      }
      properties.push(item);
    }
  }
  const allMatch = properties.every((p) => p.match);
  let tokenSummary;
  if (tokenLookup) {
    tokenSummary = tokenLookup.calculateUsageSummary(
      properties.map((p) => ({
        property: p.property,
        figma: p.figma,
        rendered: p.rendered,
        match: p.match
      })),
      theme
    );
  }
  return {
    match: allMatch,
    properties,
    figmaStyles: cleanFigmaStyles,
    renderedStyles,
    tokenSummary
  };
}
function toCssProperty(prop) {
  return prop.replace(/([A-Z])/g, "-$1").toLowerCase();
}
function formatTokenSummary(summary) {
  const lines = [];
  lines.push(`Token Compliance: ${summary.compliancePercent}%`);
  lines.push(
    `${summary.usingTokens}/${summary.totalProperties} properties using tokens`
  );
  if (summary.hardcoded > 0) {
    lines.push(`${summary.hardcoded} hardcoded value(s) detected`);
  }
  if (summary.implicitMatches > 0) {
    lines.push(`${summary.implicitMatches} implicit match(es)`);
  }
  return lines.join("\n");
}
function getComplianceBadge(compliancePercent) {
  if (compliancePercent >= 100) {
    return { label: "Excellent", color: "green" };
  } else if (compliancePercent >= 80) {
    return { label: "Good", color: "blue" };
  } else if (compliancePercent >= 50) {
    return { label: "Fair", color: "yellow" };
  } else {
    return { label: "Poor", color: "red" };
  }
}

// src/schema.ts
import { z as z3 } from "zod";
var figmaStringMappingSchema = z3.object({
  __type: z3.literal("figma-string"),
  figmaProperty: z3.string().min(1)
});
var figmaBooleanMappingSchema = z3.object({
  __type: z3.literal("figma-boolean"),
  figmaProperty: z3.string().min(1),
  valueMapping: z3.object({ true: z3.unknown(), false: z3.unknown() }).optional()
});
var figmaEnumMappingSchema = z3.object({
  __type: z3.literal("figma-enum"),
  figmaProperty: z3.string().min(1),
  valueMapping: z3.record(z3.unknown())
});
var figmaInstanceMappingSchema = z3.object({
  __type: z3.literal("figma-instance"),
  figmaProperty: z3.string().min(1)
});
var figmaChildrenMappingSchema = z3.object({
  __type: z3.literal("figma-children"),
  layers: z3.array(z3.string().min(1))
});
var figmaTextContentMappingSchema = z3.object({
  __type: z3.literal("figma-text-content"),
  layer: z3.string().min(1)
});
var figmaPropMappingSchema = z3.discriminatedUnion("__type", [
  figmaStringMappingSchema,
  figmaBooleanMappingSchema,
  figmaEnumMappingSchema,
  figmaInstanceMappingSchema,
  figmaChildrenMappingSchema,
  figmaTextContentMappingSchema
]);
var fragmentMetaSchema = z3.object({
  name: z3.string().min(1),
  description: z3.string().min(1),
  category: z3.string().min(1),
  tags: z3.array(z3.string()).optional(),
  status: z3.enum(["stable", "beta", "deprecated", "experimental"]).optional(),
  since: z3.string().optional(),
  dependencies: z3.array(
    z3.object({
      name: z3.string().min(1),
      version: z3.string().min(1),
      reason: z3.string().optional()
    })
  ).optional(),
  figma: z3.string().url().optional(),
  figmaProps: z3.record(figmaPropMappingSchema).optional()
});
var fragmentUsageSchema = z3.object({
  when: z3.array(z3.string()),
  whenNot: z3.array(z3.string()),
  guidelines: z3.array(z3.string()).optional(),
  accessibility: z3.array(z3.string()).optional()
});
var propTypeSchema = z3.enum([
  "string",
  "number",
  "boolean",
  "enum",
  "function",
  "node",
  "element",
  "object",
  "array",
  "union",
  "custom"
]);
var propDefinitionSchema = z3.object({
  type: propTypeSchema,
  values: z3.array(z3.string()).readonly().optional(),
  default: z3.unknown().optional(),
  description: z3.string().optional(),
  required: z3.boolean().optional(),
  constraints: z3.array(z3.string()).optional(),
  typeDetails: z3.record(z3.unknown()).optional()
});
var relationshipTypeSchema = z3.enum([
  "alternative",
  "sibling",
  "parent",
  "child",
  "composition",
  "complementary",
  "used-by"
]);
var componentRelationSchema = z3.object({
  component: z3.string().min(1),
  relationship: relationshipTypeSchema,
  note: z3.string().min(1)
});
var fragmentVariantSchema = z3.object({
  name: z3.string().min(1),
  description: z3.string().min(1),
  render: z3.custom(
    (value) => typeof value === "function",
    "Expected a render function"
  ),
  code: z3.string().optional(),
  figma: z3.string().url().optional()
});
var fragmentBanSchema = z3.object({
  pattern: z3.string().min(1),
  message: z3.string().min(1)
});
var fragmentContractSchema = z3.object({
  propsSummary: z3.array(z3.string()).optional(),
  a11yRules: z3.array(z3.string()).optional(),
  bans: z3.array(fragmentBanSchema).optional(),
  scenarioTags: z3.array(z3.string()).optional(),
  performanceBudget: z3.number().positive().optional(),
  compoundChildren: z3.record(
    z3.object({
      required: z3.boolean().optional(),
      accepts: z3.array(z3.string()).optional(),
      description: z3.string().optional()
    })
  ).optional(),
  canonicalUsage: z3.array(z3.string()).optional()
});
var fragmentGeneratedSchema = z3.object({
  source: z3.enum(["storybook", "manual", "ai"]),
  sourceFile: z3.string().optional(),
  confidence: z3.number().min(0).max(1).optional(),
  timestamp: z3.string().datetime().optional()
});
var aiMetadataSchema = z3.object({
  compositionPattern: z3.enum(["compound", "simple", "controlled", "wrapper"]).optional(),
  subComponents: z3.array(z3.string()).optional(),
  requiredChildren: z3.array(z3.string()).optional(),
  commonPatterns: z3.array(z3.string()).optional()
});
var observedUsagePropSchema = z3.object({
  name: z3.string().min(1),
  kind: z3.enum(["static", "dynamic", "spread", "jsx", "boolean", "null"]),
  value: z3.union([z3.string(), z3.number(), z3.boolean(), z3.null()]).optional()
});
var observedComponentUsageSchema = z3.object({
  file: z3.string().min(1),
  line: z3.number().int().positive(),
  column: z3.number().int().nonnegative(),
  props: z3.array(observedUsagePropSchema),
  parentElement: z3.string().optional(),
  conditional: z3.boolean().optional()
});
var blockDefinitionSchema = z3.object({
  name: z3.string().min(1),
  description: z3.string().min(1),
  category: z3.string().min(1),
  components: z3.array(z3.string().min(1)).min(1),
  code: z3.string().min(1),
  tags: z3.array(z3.string()).optional()
});
var fragmentDefinitionSchema = z3.object({
  component: z3.any(),
  // Allow any component type (function, class, forwardRef, etc.)
  meta: fragmentMetaSchema,
  usage: fragmentUsageSchema,
  props: z3.record(propDefinitionSchema),
  relations: z3.array(componentRelationSchema).optional(),
  variants: z3.array(fragmentVariantSchema),
  // Allow empty variants array
  contract: fragmentContractSchema.optional(),
  ai: aiMetadataSchema.optional(),
  usages: z3.array(observedComponentUsageSchema).optional(),
  _generated: fragmentGeneratedSchema.optional()
});
var fragmentsConfigSchema = z3.object({
  include: z3.array(z3.string()).min(1),
  exclude: z3.array(z3.string()).optional(),
  components: z3.array(z3.string()).optional(),
  outFile: z3.string().optional(),
  framework: z3.enum(["react", "vue", "svelte"]).optional(),
  figmaFile: z3.string().url().optional(),
  figmaToken: z3.string().optional(),
  screenshots: z3.object({}).passthrough().optional(),
  service: z3.object({}).passthrough().optional(),
  registry: z3.object({}).passthrough().optional(),
  tokens: z3.object({
    include: z3.array(z3.string()).min(1)
  }).passthrough().optional(),
  snippets: z3.object({
    mode: z3.enum(["warn", "error"]).optional(),
    scope: z3.enum(["snippet", "snippet+render"]).optional(),
    requireFullSnippet: z3.boolean().optional(),
    allowedExternalModules: z3.array(z3.string().min(1)).optional()
  }).optional(),
  performance: z3.union([
    z3.enum(["strict", "standard", "relaxed"]),
    z3.object({
      preset: z3.enum(["strict", "standard", "relaxed"]).optional(),
      budgets: z3.object({
        bundleSize: z3.number().positive().optional()
      }).optional()
    })
  ]).optional(),
  storybook: z3.object({
    exclude: z3.array(z3.string()).optional(),
    include: z3.array(z3.string()).optional(),
    excludeDeprecated: z3.boolean().optional(),
    excludeTests: z3.boolean().optional(),
    excludeSvgIcons: z3.boolean().optional(),
    excludeSubComponents: z3.boolean().optional()
  }).optional(),
  govern: governanceConfigSchema.optional(),
  recognizedClassHelpers: z3.array(z3.string().min(1)).optional()
});
var compositionMetadataSchema = z3.object({
  pattern: z3.enum(["compound", "simple", "controlled", "wrapper"]).optional(),
  subComponents: z3.array(z3.string()).optional(),
  requiredChildren: z3.array(z3.string()).optional(),
  commonPatterns: z3.array(z3.string()).optional()
});
var fragmentProvenanceSchema = z3.object({
  source: z3.enum(["storybook", "manual", "ai", "scan"]),
  sourceFile: z3.string().optional(),
  confidence: z3.number().min(0).max(1).optional(),
  timestamp: z3.string().datetime().optional(),
  autoFields: z3.array(z3.string()).optional(),
  humanFields: z3.array(z3.string()).optional()
});
var fragmentDefinitionV2Schema = z3.object({
  component: z3.any(),
  meta: fragmentMetaSchema,
  guidance: fragmentUsageSchema,
  props: z3.record(propDefinitionSchema),
  relations: z3.array(componentRelationSchema).optional(),
  examples: z3.array(fragmentVariantSchema),
  composition: compositionMetadataSchema.optional(),
  contract: fragmentContractSchema.optional(),
  _provenance: fragmentProvenanceSchema.optional()
});
var governedFragmentDefinitionSchema = z3.object({
  component: z3.any(),
  meta: fragmentMetaSchema,
  guidance: fragmentUsageSchema,
  props: z3.record(propDefinitionSchema).optional(),
  relations: z3.array(componentRelationSchema).optional(),
  examples: z3.array(fragmentVariantSchema).optional(),
  composition: compositionMetadataSchema.optional(),
  contract: fragmentContractSchema.optional(),
  _provenance: fragmentProvenanceSchema.optional(),
  govern: z3.function().optional(),
  governance: z3.array(componentGovernanceRecordSchema).optional()
});
var recipeDefinitionSchema = blockDefinitionSchema;

// src/config.ts
function formatZodErrors(errors) {
  return errors.map((error) => `  - ${error.path.join(".")}: ${error.message}`).join("\n");
}
function defineConfig(config) {
  const normalized = normalizeGovernanceConfig(config);
  const result = fragmentsConfigSchema.safeParse(normalized);
  if (!result.success) {
    throw new Error(`Invalid fragments config:
${formatZodErrors(result.error.errors)}`);
  }
  return result.data;
}

// src/defineFragment.ts
function isGovernedDefinition(def) {
  return "govern" in def || "governance" in def;
}
function isV2Definition(def) {
  return !isGovernedDefinition(def) && ("guidance" in def || "examples" in def);
}
function normalizeToV1(def) {
  let ai;
  if (def.composition) {
    ai = {
      compositionPattern: def.composition.pattern,
      subComponents: def.composition.subComponents,
      requiredChildren: def.composition.requiredChildren,
      commonPatterns: def.composition.commonPatterns
    };
  }
  let generated;
  if (def._provenance) {
    generated = {
      source: def._provenance.source === "scan" ? "ai" : def._provenance.source,
      sourceFile: def._provenance.sourceFile,
      confidence: def._provenance.confidence,
      timestamp: def._provenance.timestamp
    };
  }
  return {
    component: def.component,
    meta: def.meta,
    usage: def.guidance,
    props: def.props,
    relations: def.relations,
    variants: def.examples,
    contract: def.contract,
    ai,
    _generated: generated
  };
}
function defineFragment(definition) {
  if (process.env.NODE_ENV !== "production") {
    const governed = isGovernedDefinition(definition);
    const v2 = isV2Definition(definition);
    const schema = governed ? governedFragmentDefinitionSchema : v2 ? fragmentDefinitionV2Schema : fragmentDefinitionSchema;
    const result = schema.safeParse(definition);
    if (!result.success) {
      const name = definition.meta?.name || "unknown";
      const errors = result.error.errors.map((e) => `  - ${e.path.join(".")}: ${e.message}`).join("\n");
      throw new Error(`Invalid fragment definition for "${name}":
${errors}`);
    }
  }
  if (isGovernedDefinition(definition)) {
    const governance = resolveComponentGovernance(definition);
    return {
      ...definition,
      governance
    };
  }
  return definition;
}
function compileFragment(definition, filePath) {
  const v1 = isGovernedDefinition(definition) ? {
    component: definition.component,
    meta: definition.meta,
    usage: definition.guidance,
    props: definition.props ?? {},
    relations: definition.relations,
    variants: definition.examples ?? [],
    contract: definition.contract,
    _generated: definition._provenance ? {
      source: definition._provenance.source === "scan" ? "ai" : definition._provenance.source,
      sourceFile: definition._provenance.sourceFile,
      confidence: definition._provenance.confidence,
      timestamp: definition._provenance.timestamp
    } : void 0
  } : isV2Definition(definition) ? normalizeToV1(definition) : definition;
  return {
    filePath,
    meta: v1.meta,
    ...isGovernedDefinition(definition) && { guidance: definition.guidance },
    usage: v1.usage,
    props: v1.props,
    ...isGovernedDefinition(definition) && {
      governance: resolveComponentGovernance(definition)
    },
    relations: v1.relations,
    variants: v1.variants.map((v) => ({
      name: v.name,
      description: v.description,
      code: v.code,
      figma: v.figma
    })),
    contract: v1.contract,
    ai: v1.ai,
    _generated: v1._generated
  };
}
function defineBlock(definition) {
  if (process.env.NODE_ENV !== "production") {
    const result = blockDefinitionSchema.safeParse(definition);
    if (!result.success) {
      const errors = result.error.errors.map((e) => `  - ${e.path.join(".")}: ${e.message}`).join("\n");
      throw new Error(`Invalid block definition for "${definition.name || "unknown"}":
${errors}`);
    }
  }
  return definition;
}
var defineRecipe = defineBlock;
function compileBlock(definition, filePath) {
  return {
    filePath,
    name: definition.name,
    description: definition.description,
    category: definition.category,
    components: definition.components,
    code: definition.code,
    tags: definition.tags
  };
}
var compileRecipe = compileBlock;

// src/storyAdapter.ts
import { createElement } from "react";

// src/storybook-csf.ts
import {
  toId as storybookToId,
  storyNameFromExport as storybookStoryNameFromExport,
  isExportStory as storybookIsExportStory
} from "@storybook/csf";
var toId = (...args) => storybookToId(...args);
var storyNameFromExport = (...args) => storybookStoryNameFromExport(...args);
var isExportStory = (...args) => storybookIsExportStory(...args);

// src/storyAdapter.ts
var globalPreviewConfig = {};
function setPreviewConfig(config) {
  globalPreviewConfig = config;
}
function getPreviewConfig() {
  return globalPreviewConfig;
}
function storyModuleToFragment(storyModule, filePath) {
  const meta = storyModule.default;
  const component = meta.component;
  if (!component) {
    return null;
  }
  const componentName = extractComponentName(meta, filePath);
  const category = extractCategory(meta.title);
  const props = convertArgTypes(meta.argTypes ?? {}, globalPreviewConfig.argTypes);
  const variants = extractVariants(storyModule, component, meta);
  const figmaUrl = extractFigmaUrl(meta.parameters);
  const fragmentMeta = {
    name: componentName,
    description: meta.parameters?.docs?.description?.component ?? `${componentName} component`,
    category,
    tags: meta.tags?.filter((t) => t !== "autodocs"),
    status: "stable",
    figma: figmaUrl
  };
  const usage = {
    when: [`Use ${componentName} for its intended purpose`],
    whenNot: ["When a more specific component is available"]
  };
  return {
    component,
    meta: fragmentMeta,
    usage,
    props,
    variants
  };
}
function extractComponentName(meta, filePath) {
  if (meta.title) {
    const parts = meta.title.split("/");
    return parts[parts.length - 1];
  }
  if (meta.component?.displayName) {
    return meta.component.displayName;
  }
  if (meta.component?.name && meta.component.name !== "Component") {
    return meta.component.name;
  }
  const match = filePath.match(/([^/\\]+)\.stories\.(tsx?|jsx?)$/);
  return match?.[1] ?? "Unknown";
}
function extractCategory(title) {
  if (!title) return "general";
  const parts = title.split("/");
  if (parts.length >= 3) {
    return parts[parts.length - 2].toLowerCase();
  }
  return "general";
}
function extractFigmaUrl(parameters) {
  if (!parameters) return void 0;
  const design = parameters.design;
  if (design?.url && typeof design.url === "string") {
    return design.url;
  }
  if (typeof parameters.figma === "string") {
    return parameters.figma;
  }
  return void 0;
}
function convertArgTypes(argTypes, globalArgTypes) {
  const props = {};
  const mergedArgTypes = { ...globalArgTypes, ...argTypes };
  for (const [name, argType] of Object.entries(mergedArgTypes)) {
    if (argType.table?.disable) continue;
    if (argType.control === false && argType.action) continue;
    const { controlType, controlOptions } = extractControlInfo(argType);
    props[name] = {
      type: inferPropType(argType),
      description: argType.description ?? `${name} prop`,
      ...argType.options && { values: argType.options },
      ...argType.table?.defaultValue && {
        default: argType.table.defaultValue.summary
      },
      ...argType.defaultValue !== void 0 && {
        default: argType.defaultValue
      },
      ...argType.type?.required && { required: true },
      ...controlType && { controlType },
      ...controlOptions && Object.keys(controlOptions).length > 0 && { controlOptions }
    };
  }
  return props;
}
function extractControlInfo(argType) {
  if (argType.control === void 0 || argType.control === false) {
    return {};
  }
  const control = typeof argType.control === "string" ? { type: argType.control } : argType.control;
  const validControlTypes = [
    "text",
    "number",
    "range",
    "boolean",
    "select",
    "multi-select",
    "radio",
    "inline-radio",
    "check",
    "inline-check",
    "object",
    "file",
    "color",
    "date"
  ];
  const controlType = validControlTypes.includes(control.type) ? control.type : void 0;
  const controlOptions = {};
  if (control.min !== void 0) controlOptions.min = control.min;
  if (control.max !== void 0) controlOptions.max = control.max;
  if (control.step !== void 0) controlOptions.step = control.step;
  if (control.presetColors) controlOptions.presetColors = control.presetColors;
  return {
    controlType,
    controlOptions: Object.keys(controlOptions).length > 0 ? controlOptions : void 0
  };
}
function inferPropType(argType) {
  if (argType.action) return "function";
  if (argType.options?.length) return "enum";
  if (argType.type?.name) {
    const typeMap = {
      string: "string",
      number: "number",
      boolean: "boolean",
      object: "object",
      array: "array",
      function: "function"
    };
    const mapped = typeMap[argType.type.name];
    if (mapped) return mapped;
  }
  const control = typeof argType.control === "string" ? argType.control : argType.control ? argType.control.type : void 0;
  if (control) {
    const controlMap = {
      // Text controls
      text: "string",
      // Number controls
      number: "number",
      range: "number",
      // Boolean controls
      boolean: "boolean",
      check: "boolean",
      "inline-check": "boolean",
      // Enum/selection controls
      select: "enum",
      "multi-select": "enum",
      radio: "enum",
      "inline-radio": "enum",
      // Object controls
      object: "object",
      file: "object",
      // Special string controls
      color: "string",
      date: "string"
    };
    const mapped = controlMap[control];
    if (mapped) return mapped;
  }
  return "string";
}
function isStory(value) {
  if (typeof value === "object" && value !== null) {
    const obj = value;
    if ("args" in obj || "render" in obj || "play" in obj) return true;
  }
  if (typeof value === "function") {
    const fn = value;
    if ("args" in fn) return true;
  }
  return false;
}
function extractVariants(storyModule, component, meta) {
  const variants = [];
  for (const [exportName, exportValue] of Object.entries(storyModule)) {
    if (exportName === "default") continue;
    if (!isExportStory(exportName, meta)) continue;
    if (!isStory(exportValue)) continue;
    const story = exportValue;
    const storyName = typeof story === "object" && story.name || typeof story === "object" && story.storyName || typeof story === "function" && story.storyName || storyNameFromExport(exportName);
    const storyId = toId(meta.title || "Unknown", exportName);
    let description = `${storyName} variant`;
    if (typeof story === "object" && story.parameters?.docs?.description?.story) {
      description = story.parameters.docs.description.story;
    }
    const storyPlayFn = typeof story === "object" ? story.play : story.play;
    const hasPlayFunction = !!storyPlayFn;
    const wrappedPlay = storyPlayFn ? async (context) => {
      const args = {
        ...globalPreviewConfig.args,
        ...meta.args,
        ...typeof story === "function" ? story.args : story.args
      };
      const fullContext = buildStoryContext(meta, story, args, storyId, storyName);
      const playContext = {
        ...fullContext,
        canvasElement: context.canvasElement,
        args: context.args,
        step: context.step
      };
      await storyPlayFn(playContext);
    } : void 0;
    const storyTags = typeof story === "object" ? story.tags : void 0;
    const loaders = collectLoaders(meta, story);
    const variantArgs = {
      ...globalPreviewConfig.args,
      ...meta.args,
      ...typeof story === "function" ? story.args : story.args
    };
    const hasArgs = Object.keys(variantArgs).length > 0;
    variants.push({
      name: storyName,
      description,
      render: createRenderFunction(story, component, meta, storyId, storyName),
      // Store Storybook-specific metadata
      ...hasPlayFunction && { hasPlayFunction: true },
      ...wrappedPlay && { play: wrappedPlay },
      ...storyId && { storyId },
      ...storyTags && { tags: storyTags },
      ...loaders.length > 0 && { loaders },
      ...hasArgs && { args: variantArgs }
    });
  }
  return variants;
}
function collectLoaders(meta, story) {
  const allLoaders = [
    ...globalPreviewConfig.loaders ?? [],
    ...meta.loaders ?? [],
    ...typeof story === "function" ? story.loaders ?? [] : story.loaders ?? []
  ];
  if (allLoaders.length === 0) {
    return [];
  }
  return allLoaders.map((loader) => {
    return async () => {
      const minimalContext = {
        args: {},
        argTypes: {},
        globals: {},
        parameters: {},
        id: "",
        kind: meta.title || "Unknown",
        name: "",
        story: "",
        viewMode: "story",
        loaded: {},
        abortSignal: new AbortController().signal,
        componentId: "",
        title: meta.title || "Unknown"
      };
      return loader(minimalContext);
    };
  });
}
function buildStoryContext(meta, story, args, storyId, storyName, loadedData) {
  const mergedArgs = {
    ...globalPreviewConfig.args,
    ...meta.args,
    ...typeof story === "object" ? story.args : story.args,
    ...args
  };
  const mergedArgTypes = {
    ...globalPreviewConfig.argTypes,
    ...meta.argTypes,
    ...typeof story === "object" ? story.argTypes : story.argTypes
  };
  const mergedParameters = {
    ...globalPreviewConfig.parameters,
    ...meta.parameters,
    ...typeof story === "object" ? story.parameters : story.parameters
  };
  return {
    args: mergedArgs,
    argTypes: mergedArgTypes ?? {},
    globals: {},
    parameters: mergedParameters ?? {},
    id: storyId,
    kind: meta.title || "Unknown",
    name: storyName,
    story: storyName,
    viewMode: "story",
    loaded: loadedData ?? {},
    abortSignal: new AbortController().signal,
    componentId: toId(meta.title || "Unknown", ""),
    title: meta.title || "Unknown"
  };
}
function createRenderFunction(story, component, meta, storyId, storyName) {
  return (options) => {
    const args = {
      ...globalPreviewConfig.args,
      ...meta.args,
      ...typeof story === "function" ? story.args : story.args,
      ...options?.args
      // Runtime overrides from viewer props panel
    };
    const loadedData = options?.loadedData;
    const context = buildStoryContext(meta, story, args, storyId, storyName, loadedData);
    let renderFn;
    if (typeof story === "function") {
      renderFn = () => story(args);
    } else if (story.render) {
      renderFn = () => story.render.length >= 2 ? story.render(args, context) : story.render(args);
    } else if (meta.render) {
      renderFn = () => meta.render.length >= 2 ? meta.render(args, context) : meta.render(args);
    } else {
      renderFn = () => createElement(component, args);
    }
    const allDecorators = [
      ...globalPreviewConfig.decorators ?? [],
      ...meta.decorators ?? [],
      ...typeof story === "function" ? story.decorators ?? [] : story.decorators ?? []
    ].reverse();
    if (allDecorators.length > 0) {
      return applyDecorators(renderFn, allDecorators, context);
    }
    return renderFn();
  };
}
function applyDecorators(renderFn, decorators, context) {
  let storyFn = renderFn;
  for (const decorator of decorators) {
    const wrappedFn = storyFn;
    storyFn = () => decorator(wrappedFn, context);
  }
  return storyFn();
}

// src/storyFilters.ts
var EXCLUDED_TAGS = /* @__PURE__ */ new Set(["hidden", "internal", "no-fragment"]);
var SVG_ICON_RE = /^Svg[A-Z]/;
var TEST_TITLE_RE = /\/tests?$/i;
var TEST_FILE_RE = /\.test\.stories\./;
var DEPRECATED_TITLE_RE = /\bDeprecated\b/i;
function checkStoryExclusion(opts) {
  const { config } = opts;
  if (isForceIncluded(opts.componentName, config)) {
    return { excluded: false };
  }
  if (isConfigExcluded(opts.componentName, config)) {
    return {
      excluded: true,
      reason: "config-excluded",
      detail: `'${opts.componentName}' matches storybook.exclude pattern`
    };
  }
  if (config.excludeDeprecated !== false && opts.storybookTitle && DEPRECATED_TITLE_RE.test(opts.storybookTitle)) {
    return {
      excluded: true,
      reason: "deprecated",
      detail: `Title "${opts.storybookTitle}" contains "Deprecated"`
    };
  }
  if (config.excludeTests !== false) {
    if (opts.storybookTitle && TEST_TITLE_RE.test(opts.storybookTitle)) {
      return {
        excluded: true,
        reason: "test-story",
        detail: `Title "${opts.storybookTitle}" ends with /test(s)`
      };
    }
    if (TEST_FILE_RE.test(opts.filePath)) {
      return {
        excluded: true,
        reason: "test-story",
        detail: `File path matches *.test.stories.*`
      };
    }
  }
  if (config.excludeSvgIcons !== false) {
    const names = [opts.componentName, opts.componentDisplayName, opts.componentFunctionName].filter(Boolean);
    for (const name of names) {
      if (SVG_ICON_RE.test(name)) {
        return {
          excluded: true,
          reason: "svg-icon",
          detail: `Component name "${name}" matches Svg[A-Z] pattern`
        };
      }
    }
  }
  if (opts.tags?.length) {
    const hit = opts.tags.find((t) => EXCLUDED_TAGS.has(t));
    if (hit) {
      return {
        excluded: true,
        reason: "tag-excluded",
        detail: `Tag "${hit}" is in the exclusion set`
      };
    }
  }
  if (opts.variantCount === 0) {
    return {
      excluded: true,
      reason: "empty-variants",
      detail: "Zero renderable story exports"
    };
  }
  return { excluded: false };
}
function detectSubComponentPaths(storyFiles) {
  const byDir = /* @__PURE__ */ new Map();
  for (const file of storyFiles) {
    const parts = file.relativePath.split("/");
    if (parts.length < 2) continue;
    const fileName = parts[parts.length - 1];
    const baseMatch = fileName.match(/^([^.]+)\.stories\./);
    if (!baseMatch) continue;
    const dir = parts.slice(0, -1).join("/");
    const baseName = baseMatch[1];
    if (!byDir.has(dir)) byDir.set(dir, []);
    byDir.get(dir).push({ relativePath: file.relativePath, baseName });
  }
  const subComponentMap = /* @__PURE__ */ new Map();
  for (const [dir, files] of byDir) {
    if (files.length <= 1) continue;
    const dirName = dir.split("/").pop();
    const primary = files.find((f) => f.baseName === dirName);
    if (!primary) continue;
    for (const file of files) {
      if (file.relativePath === primary.relativePath) continue;
      subComponentMap.set(file.relativePath, primary.baseName);
    }
  }
  return subComponentMap;
}
function isForceIncluded(name, config) {
  if (!config.include?.length) return false;
  return config.include.some((pattern) => matchesPattern(name, pattern));
}
function isConfigExcluded(name, config) {
  if (!config.exclude?.length) return false;
  return config.exclude.some((pattern) => matchesPattern(name, pattern));
}
function matchesPattern(name, pattern) {
  if (!pattern.includes("*")) {
    return name === pattern;
  }
  const parts = pattern.split("*");
  if (parts.length === 2) {
    const [prefix, suffix] = parts;
    if (prefix && suffix) return name.startsWith(prefix) && name.endsWith(suffix);
    if (prefix) return name.startsWith(prefix);
    if (suffix) return name.endsWith(suffix);
    return true;
  }
  const escaped = parts.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join(".*");
  return new RegExp(`^${escaped}$`).test(name);
}

// src/figma.ts
function string(figmaProperty) {
  return {
    __type: "figma-string",
    figmaProperty
  };
}
function boolean(figmaProperty, valueMapping) {
  return {
    __type: "figma-boolean",
    figmaProperty,
    valueMapping
  };
}
function enumValue(figmaProperty, valueMapping) {
  return {
    __type: "figma-enum",
    figmaProperty,
    valueMapping
  };
}
function instance(figmaProperty) {
  return {
    __type: "figma-instance",
    figmaProperty
  };
}
function children(layers) {
  return {
    __type: "figma-children",
    layers
  };
}
function textContent(layer) {
  return {
    __type: "figma-text-content",
    layer
  };
}
var figma = {
  string,
  boolean,
  enum: enumValue,
  instance,
  children,
  textContent
};
function isFigmaPropMapping(value) {
  if (typeof value !== "object" || value === null || !("__type" in value)) {
    return false;
  }
  const typeValue = value.__type;
  return typeof typeValue === "string" && typeValue.startsWith("figma-");
}
function resolveFigmaMapping(mapping, figmaValues) {
  switch (mapping.__type) {
    case "figma-string":
      return figmaValues[mapping.figmaProperty] ?? "";
    case "figma-boolean": {
      const boolValue = figmaValues[mapping.figmaProperty];
      if (mapping.valueMapping) {
        return boolValue ? mapping.valueMapping.true : mapping.valueMapping.false;
      }
      return boolValue;
    }
    case "figma-enum": {
      const enumKey = figmaValues[mapping.figmaProperty];
      return mapping.valueMapping[enumKey] ?? enumKey;
    }
    case "figma-instance":
      return figmaValues[mapping.figmaProperty];
    case "figma-children":
      return mapping.layers.map((layer) => figmaValues[layer]);
    case "figma-text-content":
      return figmaValues[mapping.layer] ?? "";
    default:
      return void 0;
  }
}

// src/tokens/dtcg.ts
var DTCG_META_KEYS = /* @__PURE__ */ new Set([
  "$type",
  "$value",
  "$description",
  "$deprecated",
  "$extensions",
  "$extends"
]);
var MAX_ALIAS_DEPTH = 10;
function isDTCGFile(filePath) {
  return filePath.endsWith(".tokens.json") || filePath.endsWith(".tokens") || filePath.endsWith("design-tokens.json") || filePath.endsWith("tokens.json");
}
function mapDTCGTypeToCategory(type, tokenPath) {
  switch (type) {
    case "color":
      return "colors";
    case "dimension":
      if (/radius/i.test(tokenPath)) return "radius";
      return "spacing";
    case "fontFamily":
    case "fontWeight":
      return "typography";
    case "shadow":
      return "shadows";
    case "border":
      return "borders";
    case "duration":
    case "cubicBezier":
    case "transition":
      return "transitions";
    case "typography":
      return "typography";
    case "gradient":
      return "colors";
    case "strokeStyle":
      return "borders";
    case "number":
      return "other";
    default:
      return "other";
  }
}
function colorValueToCSS(value) {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const obj = value;
    if (typeof obj.hex === "string") {
      if (obj.alpha !== void 0 && typeof obj.alpha === "number" && obj.alpha < 1) {
        const rgb = hexToRgb(obj.hex);
        if (rgb) {
          return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${obj.alpha})`;
        }
      }
      return obj.hex;
    }
    if (Array.isArray(obj.components)) {
      const comps = obj.components;
      if (comps.length >= 3) {
        const alpha = obj.alpha ?? (comps.length >= 4 ? comps[3] : 1);
        if (typeof alpha === "number" && alpha < 1) {
          return `rgba(${Math.round(comps[0] * 255)}, ${Math.round(comps[1] * 255)}, ${Math.round(comps[2] * 255)}, ${alpha})`;
        }
        if (comps.every((c) => c <= 1)) {
          return `rgb(${Math.round(comps[0] * 255)}, ${Math.round(comps[1] * 255)}, ${Math.round(comps[2] * 255)})`;
        }
        return `rgb(${comps[0]}, ${comps[1]}, ${comps[2]})`;
      }
    }
  }
  return String(value);
}
function dimensionValueToCSS(value) {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const obj = value;
    if (typeof obj.value === "number" && typeof obj.unit === "string") {
      return `${obj.value}${obj.unit}`;
    }
  }
  return String(value);
}
function shadowValueToCSS(value) {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map((v) => shadowSingleToCSS(v)).join(", ");
  }
  return shadowSingleToCSS(value);
}
function shadowSingleToCSS(value) {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const obj = value;
    const parts = [];
    if (obj.inset) parts.push("inset");
    parts.push(dimensionValueToCSS(obj.offsetX));
    parts.push(dimensionValueToCSS(obj.offsetY));
    parts.push(dimensionValueToCSS(obj.blur));
    if (obj.spread !== void 0) parts.push(dimensionValueToCSS(obj.spread));
    parts.push(colorValueToCSS(obj.color));
    return parts.join(" ");
  }
  return String(value);
}
function borderValueToCSS(value) {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const obj = value;
    return `${dimensionValueToCSS(obj.width)} ${obj.style ?? "solid"} ${colorValueToCSS(obj.color)}`;
  }
  return String(value);
}
function typographyValueToCSS(value) {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const obj = value;
    const parts = [];
    if (obj.fontWeight) parts.push(String(obj.fontWeight));
    if (obj.fontSize) parts.push(dimensionValueToCSS(obj.fontSize));
    if (obj.lineHeight) parts.push(`/ ${obj.lineHeight}`);
    if (obj.fontFamily) {
      const family = Array.isArray(obj.fontFamily) ? obj.fontFamily.join(", ") : String(obj.fontFamily);
      parts.push(family);
    }
    return parts.join(" ");
  }
  return String(value);
}
function cubicBezierValueToCSS(value) {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length === 4) {
    return `cubic-bezier(${value.join(", ")})`;
  }
  return String(value);
}
function transitionValueToCSS(value) {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const obj = value;
    const parts = [];
    if (obj.duration) parts.push(String(obj.duration));
    if (obj.timingFunction) parts.push(cubicBezierValueToCSS(obj.timingFunction));
    if (obj.delay) parts.push(String(obj.delay));
    return parts.join(" ");
  }
  return String(value);
}
function gradientValueToCSS(value) {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    const stops = value.map((stop) => {
      if (stop && typeof stop === "object") {
        const s = stop;
        const color = colorValueToCSS(s.color);
        const position = typeof s.position === "number" ? ` ${s.position * 100}%` : "";
        return `${color}${position}`;
      }
      return String(stop);
    });
    return `linear-gradient(${stops.join(", ")})`;
  }
  return String(value);
}
function fontFamilyValueToCSS(value) {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}
function valueToCSS(type, value) {
  switch (type) {
    case "color":
      return colorValueToCSS(value);
    case "dimension":
      return dimensionValueToCSS(value);
    case "shadow":
      return shadowValueToCSS(value);
    case "border":
      return borderValueToCSS(value);
    case "typography":
      return typographyValueToCSS(value);
    case "cubicBezier":
      return cubicBezierValueToCSS(value);
    case "transition":
      return transitionValueToCSS(value);
    case "gradient":
      return gradientValueToCSS(value);
    case "fontFamily":
      return fontFamilyValueToCSS(value);
    case "fontWeight":
    case "duration":
    case "number":
      return String(value);
    case "strokeStyle":
      return typeof value === "string" ? value : String(value);
    default:
      return String(value);
  }
}
function isAlias(value) {
  return typeof value === "string" && /^\{.+\}$/.test(value);
}
function resolveAliasPath(alias) {
  return alias.slice(1, -1);
}
function resolveAlias(alias, root, visited, depth) {
  if (depth > MAX_ALIAS_DEPTH) {
    throw new Error(`Circular alias detected: ${alias} (max depth ${MAX_ALIAS_DEPTH} reached)`);
  }
  const path = resolveAliasPath(alias);
  if (visited.has(path)) {
    throw new Error(`Circular alias detected: ${[...visited, path].join(" -> ")}`);
  }
  visited.add(path);
  const parts = path.split(".");
  let current = root;
  for (const part of parts) {
    if (current && typeof current === "object" && part in current) {
      current = current[part];
    } else {
      throw new Error(`Alias reference "${alias}" could not be resolved: "${part}" not found in path "${path}"`);
    }
  }
  if (current && typeof current === "object" && "$value" in current) {
    const resolvedValue = current.$value;
    if (isAlias(resolvedValue)) {
      return resolveAlias(resolvedValue, root, visited, depth + 1);
    }
    return resolvedValue;
  }
  if (isAlias(current)) {
    return resolveAlias(current, root, visited, depth + 1);
  }
  return current;
}
function resolveExtends(group, root, visited) {
  if (!group.$extends) return group;
  const extendsPath = group.$extends;
  if (visited.has(extendsPath)) {
    throw new Error(`Circular $extends detected: ${[...visited, extendsPath].join(" -> ")}`);
  }
  visited.add(extendsPath);
  const parts = extendsPath.split(".");
  let parent = root;
  for (const part of parts) {
    if (parent && typeof parent === "object" && part in parent) {
      parent = parent[part];
    } else {
      throw new Error(`$extends reference "${extendsPath}" could not be resolved`);
    }
  }
  if (!parent || typeof parent !== "object") {
    throw new Error(`$extends target "${extendsPath}" is not a group`);
  }
  const resolvedParent = resolveExtends(parent, root, visited);
  const merged = { ...resolvedParent };
  for (const [key, value] of Object.entries(group)) {
    if (key === "$extends") continue;
    merged[key] = value;
  }
  return merged;
}
function walkTokenTree(node, root, path, inheritedType, tokens) {
  const resolved = resolveExtends(node, root, /* @__PURE__ */ new Set());
  const currentType = resolved.$type ?? inheritedType;
  for (const [key, child] of Object.entries(resolved)) {
    if (DTCG_META_KEYS.has(key)) continue;
    if (typeof child !== "object" || child === null) continue;
    const childObj = child;
    const childPath = [...path, key];
    if ("$value" in childObj) {
      const tokenType = childObj.$type ?? currentType;
      if (!tokenType) continue;
      let rawValue = childObj.$value;
      if (isAlias(rawValue)) {
        try {
          rawValue = resolveAlias(rawValue, root, /* @__PURE__ */ new Set(), 0);
        } catch {
        }
      }
      tokens.push({
        path: childPath.join("."),
        type: tokenType,
        rawValue,
        cssValue: valueToCSS(tokenType, rawValue),
        description: childObj.$description,
        deprecated: childObj.$deprecated,
        extensions: childObj.$extensions
      });
    } else {
      walkTokenTree(childObj, root, childPath, currentType, tokens);
    }
  }
}
function detectDTCGPrefix(tokens, root) {
  const extensions = root.$extensions;
  if (extensions) {
    const fragmentsExt = extensions["com.usefragments"];
    if (fragmentsExt?.prefix && typeof fragmentsExt.prefix === "string") {
      const p = fragmentsExt.prefix.replace(/-$/, "");
      return `--${p}-`;
    }
  }
  const topLevelKeys = Object.keys(root).filter((k) => !DTCG_META_KEYS.has(k));
  if (topLevelKeys.length === 1) {
    return `--${topLevelKeys[0]}-`;
  }
  if (topLevelKeys.length > 1) {
    return "--";
  }
  if (tokens.length === 0) return "--";
  const firstParts = tokens[0].path.split(".");
  if (firstParts.length > 0) {
    return `--${firstParts[0]}-`;
  }
  return "--";
}
function tokenPathToCSSName(path, prefix) {
  const suffix = path.replace(/\./g, "-");
  const normalizedPrefix = prefix.endsWith("-") ? prefix : `${prefix}-`;
  const prefixBase = normalizedPrefix.replace(/^--/, "").replace(/-$/, "");
  if (suffix.startsWith(prefixBase + "-") || suffix === prefixBase) {
    return `--${suffix}`;
  }
  return `${normalizedPrefix}${suffix}`;
}
function parseDtcgTokens(content, filePath = "tokens.tokens.json") {
  const root = JSON.parse(content);
  const resolvedTokens = [];
  walkTokenTree(root, root, [], void 0, resolvedTokens);
  const prefix = detectDTCGPrefix(resolvedTokens, root);
  const categories = {};
  for (const token of resolvedTokens) {
    const category = mapDTCGTypeToCategory(token.type, token.path);
    const parsed = {
      name: tokenPathToCSSName(token.path, prefix),
      value: token.cssValue,
      category,
      description: token.description
    };
    categories[category] ??= [];
    categories[category].push(parsed);
  }
  return {
    prefix,
    categories,
    total: resolvedTokens.length
  };
}

// src/tokens/categories.ts
var TOKEN_CATEGORY_PATTERNS = [
  { pattern: /color|bg|background|border-color|fill|stroke/i, category: "color" },
  { pattern: /spacing|margin|padding|gap|space|inset/i, category: "spacing" },
  { pattern: /font|text|line-height|letter-spacing|typography/i, category: "typography" },
  { pattern: /radius|rounded|corner/i, category: "radius" },
  { pattern: /shadow|elevation/i, category: "shadow" },
  { pattern: /size|width|height|min|max/i, category: "sizing" },
  { pattern: /border(?!-color)|stroke-width|outline/i, category: "border" },
  { pattern: /animation|transition|duration|timing|delay/i, category: "animation" },
  { pattern: /z-index|layer|stack/i, category: "z-index" }
];
var TOKEN_GROUP_PATTERNS = [
  { pattern: /--(?:[\w]+-)?(?:font|line-height|letter-spacing|typography)-/, category: "typography" },
  { pattern: /--(?:[\w]+-)?(?:space|spacing|padding|gap|margin|inset)-/, category: "spacing" },
  { pattern: /--(?:[\w]+-)?(?:radius|rounded|corner)-/, category: "radius" },
  { pattern: /--(?:[\w]+-)?color-/, category: "colors" },
  { pattern: /--(?:[\w]+-)?(?:bg|background)-/, category: "surfaces" },
  { pattern: /--(?:[\w]+-)?text-/, category: "text" },
  { pattern: /--(?:[\w]+-)?border/, category: "borders" },
  { pattern: /--(?:[\w]+-)?(?:shadow|elevation)-/, category: "shadows" },
  { pattern: /--(?:[\w]+-)?focus-/, category: "focus" },
  { pattern: /--(?:[\w]+-)?(?:transition|animation|duration|timing|delay)-/, category: "transitions" },
  { pattern: /--(?:[\w]+-)?scrollbar-/, category: "scrollbar" },
  { pattern: /--(?:[\w]+-)?z-index/, category: "z-index" },
  { pattern: /--(?:[\w]+-)?(?:button|input|touch)-/, category: "component-sizing" },
  { pattern: /--(?:[\w]+-)?(?:appshell|header)-/, category: "layout" },
  { pattern: /--(?:[\w]+-)?code-/, category: "code" },
  { pattern: /--(?:[\w]+-)?tooltip-/, category: "tooltip" },
  { pattern: /--(?:[\w]+-)?hero-/, category: "marketing" }
];
function inferTokenCategory(name) {
  const lowerName = name.toLowerCase();
  for (const { pattern, category } of TOKEN_CATEGORY_PATTERNS) {
    if (pattern.test(lowerName)) {
      return category;
    }
  }
  return "other";
}
function inferTokenGroup(name) {
  const lowerName = name.toLowerCase();
  for (const { pattern, category: category2 } of TOKEN_GROUP_PATTERNS) {
    if (pattern.test(lowerName)) {
      return category2;
    }
  }
  const category = inferTokenCategory(name);
  switch (category) {
    case "color":
      return "colors";
    case "shadow":
      return "shadows";
    case "border":
      return "borders";
    case "animation":
      return "transitions";
    default:
      return category;
  }
}
function normalizeTokenGroupComment(comment) {
  const text = comment.trim().replace(/^\/\/\s*/, "").replace(/^\/\*+\s*/, "").replace(/\s*\*+\/$/, "").trim().toLowerCase();
  const mappings = {
    "base configuration": "base",
    typography: "typography",
    "spacing (micro)": "spacing",
    spacing: "spacing",
    "density padding": "spacing",
    "border radius": "radius",
    transitions: "transitions",
    colors: "colors",
    surfaces: "surfaces",
    text: "text",
    borders: "borders",
    shadows: "shadows",
    focus: "focus",
    scrollbar: "scrollbar",
    "component heights": "component-sizing",
    "appshell layout": "layout",
    codeblock: "code",
    tooltip: "tooltip",
    "hero/marketing gradient": "marketing"
  };
  return mappings[text] ?? text.replace(/\s+/g, "-");
}

// src/tokens/scss.ts
function detectTokenPrefix(names) {
  if (names.length === 0) return "--";
  const stripped = names.map((name) => name.slice(2));
  let prefix = "";
  const first = stripped[0] ?? "";
  for (let i = 0; i < first.length; i++) {
    const ch = first[i];
    if (stripped.every((s) => s[i] === ch)) {
      prefix += ch;
    } else {
      break;
    }
  }
  const lastHyphen = prefix.lastIndexOf("-");
  if (lastHyphen > 0) {
    prefix = prefix.slice(0, lastHyphen + 1);
  }
  return `--${prefix}`;
}
function parseScssVariables(content) {
  const vars = /* @__PURE__ */ new Map();
  const scssVarRegex = /^\s*(\$[\w-]+)\s*:\s*(.+?)\s*(?:!default\s*)?;/gm;
  let match;
  while ((match = scssVarRegex.exec(content)) !== null) {
    const name = match[1];
    const value = match[2].replace(/\s*\/\/.*$/, "").trim();
    if (!vars.has(name)) {
      vars.set(name, value);
    }
  }
  return vars;
}
function resolveTokenValue(rawValue, scssVars, cssVarValues, depth = 0) {
  if (depth > 5) return rawValue;
  let resolved = rawValue;
  resolved = resolved.replace(/#\{(\$[\w-]+)\}/g, (_, varName) => {
    const val = scssVars.get(varName);
    return val !== void 0 ? resolveTokenValue(val, scssVars, cssVarValues, depth + 1) : `#{${varName}}`;
  });
  resolved = resolved.replace(/(?<![#\{])(\$[\w-]+)/g, (_, varName) => {
    const val = scssVars.get(varName);
    return val !== void 0 ? resolveTokenValue(val, scssVars, cssVarValues, depth + 1) : varName;
  });
  resolved = resolved.replace(
    /var\((--[\w-]+)(?:\s*,\s*(.+?))?\)/g,
    (original, tokenName, fallback) => {
      const tokenVal = cssVarValues.get(tokenName);
      if (tokenVal !== void 0) {
        return resolveTokenValue(tokenVal, scssVars, cssVarValues, depth + 1);
      }
      if (fallback) {
        return resolveTokenValue(fallback.trim(), scssVars, cssVarValues, depth + 1);
      }
      return original;
    }
  );
  return resolved;
}
function parseScssTokens(content, filePath = "tokens.scss") {
  const lines = content.split("\n");
  const tokens = [];
  const seenNames = /* @__PURE__ */ new Set();
  let currentCategory = "other";
  let hasCommentCategories = false;
  const scssVars = parseScssVariables(content);
  const varDeclRegex = /^\s*(--[\w-]+)\s*:\s*(.+?)\s*;/;
  const sectionCommentRegex = /^\s*\/\/\s+([A-Z].+)$/;
  for (const line of lines) {
    const commentMatch = line.match(sectionCommentRegex);
    if (commentMatch) {
      const normalized = normalizeTokenGroupComment(commentMatch[0]);
      if (normalized) {
        currentCategory = normalized;
        hasCommentCategories = true;
      }
      continue;
    }
    const varMatch = line.match(varDeclRegex);
    if (varMatch) {
      const name = varMatch[1];
      const rawValue = varMatch[2];
      if (seenNames.has(name)) continue;
      seenNames.add(name);
      const inlineComment = line.match(/\/\/\s*(.+)$/);
      const description = inlineComment ? inlineComment[1].trim() : void 0;
      const cleanValue = rawValue.replace(/\s*\/\/.*$/, "").trim();
      tokens.push({
        name,
        value: cleanValue || void 0,
        category: hasCommentCategories ? currentCategory : inferTokenGroup(name),
        description
      });
    }
  }
  for (const [name, value] of scssVars) {
    if (seenNames.has(name)) continue;
    seenNames.add(name);
    tokens.push({
      name,
      value,
      category: hasCommentCategories ? currentCategory : inferTokenGroup(name)
    });
  }
  const cssVarValues = /* @__PURE__ */ new Map();
  for (const token of tokens) {
    if (token.value) {
      cssVarValues.set(token.name, token.value);
    }
  }
  for (const token of tokens) {
    if (token.value) {
      const resolved = resolveTokenValue(token.value, scssVars, cssVarValues);
      if (resolved !== token.value && !resolved.includes("#{") && !resolved.includes("$")) {
        token.resolvedValue = resolved;
      }
    }
  }
  const categories = {};
  for (const token of tokens) {
    categories[token.category] ??= [];
    categories[token.category].push(token);
  }
  return {
    prefix: detectTokenPrefix(tokens.map((token) => token.name)),
    categories,
    total: tokens.length
  };
}
function parseCssTokens(content, filePath = "tokens.css") {
  return parseScssTokens(content, filePath);
}
var parseTokenFile = parseScssTokens;

// src/tokens/tailwind-v4.ts
var CUSTOM_PROPERTY_PATTERN = /^\s*(--[\w-]+)\s*:\s*(.+?)\s*;?\s*$/;
function containsTailwindV4Theme(content) {
  return /^@theme\b/m.test(content);
}
function parseTailwindV4Theme(cssContent, filePath) {
  const startTime = performance.now();
  const tokens = [];
  const errors = [];
  const warnings = [];
  const source = filePath ?? "unknown";
  const cleaned = stripBlockComments(cssContent);
  const lines = cleaned.split("\n");
  let insideTheme = false;
  let themeDepth = 0;
  let pendingTheme = false;
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const lineNumber = i + 1;
    const singleCommentIdx = line.indexOf("//");
    if (singleCommentIdx !== -1) {
      const beforeComment = line.slice(0, singleCommentIdx);
      const singleQuotes = (beforeComment.match(/'/g) || []).length;
      const doubleQuotes = (beforeComment.match(/"/g) || []).length;
      if (singleQuotes % 2 === 0 && doubleQuotes % 2 === 0) {
        line = beforeComment;
      }
    }
    const trimmed = line.trim();
    if (trimmed === "") continue;
    if (!insideTheme && !pendingTheme && /^@theme\b/.test(trimmed)) {
      pendingTheme = true;
      const braceIdx = trimmed.indexOf("{");
      if (braceIdx !== -1) {
        pendingTheme = false;
        insideTheme = true;
        themeDepth = 1;
        const afterBrace = trimmed.slice(braceIdx + 1);
        for (const ch of afterBrace) {
          if (ch === "{") themeDepth++;
          else if (ch === "}") themeDepth--;
        }
        const inlineContent = afterBrace.trim();
        if (inlineContent && themeDepth > 0) {
          processThemeLine(inlineContent, lineNumber, source, tokens, warnings);
        }
        if (themeDepth <= 0) {
          insideTheme = false;
        }
      }
      continue;
    }
    if (pendingTheme) {
      const braceIdx = trimmed.indexOf("{");
      if (braceIdx !== -1) {
        pendingTheme = false;
        insideTheme = true;
        themeDepth = 1;
        const afterBrace = trimmed.slice(braceIdx + 1);
        for (const ch of afterBrace) {
          if (ch === "{") themeDepth++;
          else if (ch === "}") themeDepth--;
        }
        const inlineContent = afterBrace.trim();
        if (inlineContent && themeDepth > 0) {
          processThemeLine(inlineContent, lineNumber, source, tokens, warnings);
        }
        if (themeDepth <= 0) {
          insideTheme = false;
        }
      }
      continue;
    }
    if (insideTheme) {
      for (const ch of trimmed) {
        if (ch === "{") {
          themeDepth++;
        } else if (ch === "}") {
          themeDepth--;
          if (themeDepth <= 0) {
            insideTheme = false;
            break;
          }
        }
      }
      if (insideTheme || themeDepth > 0) {
        const contentLine = trimmed.replace(/\}/g, "").trim();
        if (contentLine) {
          processThemeLine(contentLine, lineNumber, source, tokens, warnings);
        }
      }
    }
  }
  if (pendingTheme) {
    warnings.push(`@theme keyword found but no opening brace - incomplete block in ${source}`);
  }
  if (insideTheme) {
    warnings.push(`Unclosed @theme block - missing closing brace in ${source}`);
  }
  return {
    tokens,
    errors,
    warnings,
    parseTimeMs: performance.now() - startTime
  };
}
function stripBlockComments(css) {
  return css.replace(/\/\*[\s\S]*?\*\//g, "");
}
function processThemeLine(line, lineNumber, source, tokens, warnings) {
  const match = line.match(CUSTOM_PROPERTY_PATTERN);
  if (!match) {
    const partialMatch = line.match(/^\s*(--[\w-]+)\s*:\s*(.+?)\s*$/);
    if (partialMatch) {
      const [, name2, rawValue2] = partialMatch;
      tokens.push(buildToken(name2, rawValue2, lineNumber, source));
      return;
    }
    if (/^\s*--/.test(line)) {
      warnings.push(
        `Line ${lineNumber}: Could not parse custom property declaration: ${line.trim()}`
      );
    }
    return;
  }
  const [, name, rawValue] = match;
  tokens.push(buildToken(name, rawValue, lineNumber, source));
}
function buildToken(name, rawValue, lineNumber, source) {
  const value = rawValue.trim();
  return {
    name,
    rawValue: value,
    resolvedValue: value,
    category: inferTokenCategory(name),
    level: /var\(/.test(value) ? 2 : 1,
    referenceChain: [],
    sourceFile: source,
    lineNumber,
    theme: "default",
    selector: "@theme"
  };
}

// src/tokens/design-token-parser.ts
var TOKEN_DECLARATION_PATTERN = /--([a-zA-Z0-9_-]+)\s*:\s*([^;]+);/g;
var VAR_REFERENCE_PATTERN = /var\(\s*--([a-zA-Z0-9_-]+)(?:\s*,\s*([^)]+))?\s*\)/;
function parseDesignTokenContent(content, options = {}) {
  const startTime = performance.now();
  const tokens = [];
  const errors = [];
  const warnings = [];
  const filePath = options.filePath ?? "unknown";
  const themeSelectors = options.themeSelectors ?? { ":root": "default" };
  const includeTailwindV4 = options.includeTailwindV4 ?? true;
  if (includeTailwindV4 && containsTailwindV4Theme(content)) {
    const v4Result = parseTailwindV4Theme(content, filePath);
    tokens.push(...v4Result.tokens);
    warnings.push(...v4Result.warnings);
    for (const err of v4Result.errors) {
      errors.push({ message: err, file: filePath });
    }
  }
  const tokensByName = /* @__PURE__ */ new Map();
  const lines = content.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNumber = i + 1;
    const tokenMatches = [...line.matchAll(TOKEN_DECLARATION_PATTERN)];
    for (const match of tokenMatches) {
      const [, name, rawValue] = match;
      const fullName = `--${name}`;
      tokensByName.set(fullName, { rawValue: rawValue.trim(), line: lineNumber });
    }
  }
  for (const [name, { rawValue, line }] of tokensByName) {
    const selector = findSelectorForLine(content, line ?? 1);
    const theme = themeSelectors[selector] || "default";
    const { resolvedValue, chain, hasCircular, unresolvedRef } = resolveDesignTokenValue(
      rawValue,
      tokensByName
    );
    if (hasCircular) {
      warnings.push(`Circular reference detected for ${name} at line ${line}`);
    }
    if (unresolvedRef) {
      warnings.push(`Unresolved reference in ${name}: ${unresolvedRef}`);
    }
    tokens.push({
      name,
      rawValue,
      resolvedValue,
      category: inferTokenCategory(name),
      level: inferTokenLevel(name, rawValue, chain),
      referenceChain: chain,
      sourceFile: filePath,
      lineNumber: line,
      theme,
      selector,
      description: extractDescription(content, line ?? 1)
    });
  }
  return {
    tokens,
    errors,
    warnings,
    parseTimeMs: performance.now() - startTime
  };
}
function resolveDesignTokenValue(rawValue, tokensByName, visited = /* @__PURE__ */ new Set()) {
  const chain = [];
  let current = rawValue;
  let hasCircular = false;
  let unresolvedRef;
  const maxIterations = 20;
  let iterations = 0;
  while (iterations < maxIterations) {
    iterations++;
    const varMatch = current.match(VAR_REFERENCE_PATTERN);
    if (!varMatch) break;
    const [, refName, fallback] = varMatch;
    const fullRefName = `--${refName}`;
    if (visited.has(fullRefName)) {
      hasCircular = true;
      break;
    }
    visited.add(fullRefName);
    chain.push(fullRefName);
    const refToken = tokensByName.get(fullRefName);
    if (refToken) {
      current = current.replace(varMatch[0], refToken.rawValue);
    } else if (fallback) {
      current = current.replace(varMatch[0], fallback.trim());
    } else {
      unresolvedRef = fullRefName;
      break;
    }
  }
  return {
    resolvedValue: normalizeTokenValue(current.trim()),
    chain,
    hasCircular,
    unresolvedRef
  };
}
function normalizeTokenValue(value) {
  value = value.replace(/#[0-9a-fA-F]+/g, (match) => match.toLowerCase());
  value = value.replace(/\s+/g, " ").trim();
  value = value.replace(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/g,
    (_, r, g2, b, a) => a !== void 0 ? `rgba(${r}, ${g2}, ${b}, ${a})` : `rgb(${r}, ${g2}, ${b})`
  );
  return value;
}
function inferTokenLevel(name, rawValue, referenceChain) {
  const lowerName = name.toLowerCase();
  if (/btn|button|input|card|modal|dialog|menu|nav|header|footer|table|form/i.test(lowerName)) {
    return 3;
  }
  if (referenceChain.length > 0) {
    return 2;
  }
  if (rawValue.match(/^#[0-9a-fA-F]+$/) || rawValue.match(/^\d+(\.\d+)?(px|rem|em|%|vh|vw)?$/)) {
    return 1;
  }
  return 2;
}
function findSelectorForLine(content, targetLine) {
  const lines = content.split("\n");
  let currentSelector = ":root";
  let braceDepth = 0;
  for (let i = 0; i < Math.min(targetLine, lines.length); i++) {
    const line = lines[i];
    const selectorMatch = line.match(/^\s*([^{]+)\s*\{/);
    if (selectorMatch) {
      const selector = selectorMatch[1].trim();
      if ((line.match(/\{/g) || []).length > (line.match(/\}/g) || []).length) {
        currentSelector = selector;
      }
    }
    braceDepth += (line.match(/\{/g) || []).length;
    braceDepth -= (line.match(/\}/g) || []).length;
    if (braceDepth === 0) {
      currentSelector = ":root";
    }
  }
  return currentSelector;
}
function extractDescription(content, line) {
  const lines = content.split("\n");
  if (line <= 1) return void 0;
  const prevLine = lines[line - 2]?.trim();
  const singleLineMatch = prevLine?.match(/\/\/\s*(.+)$/);
  if (singleLineMatch) {
    return singleLineMatch[1].trim();
  }
  const multiLineMatch = prevLine?.match(/\*\s*(.+)\s*\*\//);
  if (multiLineMatch) {
    return multiLineMatch[1].trim();
  }
  const inlineMatch = prevLine?.match(/\/\*\s*(.+)\s*\*\//);
  if (inlineMatch) {
    return inlineMatch[1].trim();
  }
  return void 0;
}

// src/tokens/index.ts
function parseTokens(content, formatOrOptions = "auto") {
  const options = typeof formatOrOptions === "string" ? { format: formatOrOptions } : formatOrOptions;
  const format = options.format ?? "auto";
  const filePath = options.filePath ?? "";
  if (format === "dtcg" || format === "auto" && isDTCGFile(filePath)) {
    return parseDtcgTokens(content, filePath);
  }
  if (format === "tailwind-v4" || format === "auto" && containsTailwindV4Theme(content)) {
    return designTokensToParseOutput(parseTailwindV4Theme(content, filePath).tokens);
  }
  if (format === "css") {
    return parseCssTokens(content, filePath);
  }
  return parseScssTokens(content, filePath);
}
function designTokensToParseOutput(tokens) {
  const categories = {};
  for (const token of tokens) {
    const category = tokenCategoryToGroup(token.category);
    categories[category] ??= [];
    categories[category].push({
      name: token.name,
      value: token.rawValue,
      resolvedValue: token.resolvedValue !== token.rawValue ? token.resolvedValue : void 0,
      category,
      description: token.description
    });
  }
  return {
    prefix: detectTokenPrefix(tokens.map((token) => token.name)),
    categories,
    total: tokens.length
  };
}
function tokenCategoryToGroup(category) {
  switch (category) {
    case "color":
      return "colors";
    case "shadow":
      return "shadows";
    case "border":
      return "borders";
    case "animation":
      return "transitions";
    default:
      return category;
  }
}

// src/dtcg.ts
import { z as z4 } from "zod";
var dtcgTokenTypeSchema = z4.enum([
  "color",
  "dimension",
  "fontFamily",
  "fontWeight",
  "duration",
  "cubicBezier",
  "number",
  "shadow",
  "border",
  "strokeStyle",
  "transition",
  "gradient",
  "typography"
]);
var dtcgColorValueSchema = z4.union([
  z4.string(),
  z4.object({
    colorSpace: z4.string().default("srgb"),
    components: z4.array(z4.number()),
    hex: z4.string().optional(),
    alpha: z4.number().optional()
  })
]);
var dtcgDimensionValueSchema = z4.union([
  z4.string(),
  z4.object({
    value: z4.number(),
    unit: z4.enum(["px", "rem"])
  })
]);
var dtcgShadowValueSchema = z4.object({
  color: dtcgColorValueSchema,
  offsetX: dtcgDimensionValueSchema,
  offsetY: dtcgDimensionValueSchema,
  blur: dtcgDimensionValueSchema,
  spread: dtcgDimensionValueSchema.optional(),
  inset: z4.boolean().optional()
});
var dtcgBorderValueSchema = z4.object({
  color: dtcgColorValueSchema,
  width: dtcgDimensionValueSchema,
  style: z4.string()
});
var dtcgTypographyValueSchema = z4.object({
  fontFamily: z4.union([z4.string(), z4.array(z4.string())]),
  fontSize: dtcgDimensionValueSchema,
  fontWeight: z4.union([z4.number(), z4.string()]),
  letterSpacing: dtcgDimensionValueSchema.optional(),
  lineHeight: z4.union([z4.string(), z4.number()]).optional()
});
var dtcgTransitionValueSchema = z4.object({
  duration: z4.string(),
  delay: z4.string().optional(),
  timingFunction: z4.array(z4.number()).length(4)
});
var dtcgGradientStopSchema = z4.object({
  color: dtcgColorValueSchema,
  position: z4.number()
});
var dtcgCubicBezierValueSchema = z4.tuple([
  z4.number(),
  z4.number(),
  z4.number(),
  z4.number()
]);
var dtcgTokenSchema = z4.object({
  $value: z4.unknown(),
  $type: dtcgTokenTypeSchema.optional(),
  $description: z4.string().optional(),
  $deprecated: z4.union([z4.boolean(), z4.string()]).optional(),
  $extensions: z4.record(z4.string(), z4.unknown()).optional()
}).passthrough();
var dtcgTokenFileSchema = z4.record(z4.string(), z4.unknown()).refine(
  (data) => {
    return data !== null && typeof data === "object" && !Array.isArray(data);
  },
  { message: "DTCG token file must be a JSON object" }
);

// src/component-contract.ts
import { z as z5 } from "zod";
var contractPropSchema = z5.object({
  type: z5.string(),
  values: z5.array(z5.string()).optional(),
  default: z5.unknown().optional(),
  description: z5.string(),
  required: z5.boolean().optional(),
  constraints: z5.array(z5.string()).optional()
});
var contractUsageSchema = z5.object({
  when: z5.array(z5.string()),
  whenNot: z5.array(z5.string()),
  guidelines: z5.array(z5.string()).optional(),
  accessibility: z5.array(z5.string()).optional()
});
var contractExampleSchema = z5.object({
  name: z5.string(),
  description: z5.string(),
  code: z5.string(),
  args: z5.record(z5.string(), z5.unknown()).optional()
});
var contractRelationSchema = z5.object({
  component: z5.string(),
  relationship: z5.enum(["alternative", "parent", "child", "sibling", "composition", "complementary", "used-by"]),
  note: z5.string()
});
var contractContractSchema = z5.object({
  propsSummary: z5.array(z5.string()).optional(),
  scenarioTags: z5.array(z5.string()).optional(),
  a11yRules: z5.array(z5.string()).optional(),
  bans: z5.array(z5.object({
    pattern: z5.string(),
    message: z5.string()
  })).optional(),
  compoundChildren: z5.record(z5.string(), z5.object({
    required: z5.boolean().optional(),
    accepts: z5.array(z5.string()).optional(),
    description: z5.string().optional()
  })).optional(),
  canonicalUsage: z5.array(z5.string()).optional(),
  performanceBudget: z5.number().optional()
});
var contractAiSchema = z5.object({
  compositionPattern: z5.enum(["compound", "simple", "controlled", "wrapper"]).optional(),
  subComponents: z5.array(z5.string()).optional(),
  requiredChildren: z5.array(z5.string()).optional(),
  commonPatterns: z5.array(z5.string()).optional()
});
var contractPreviewSchema = z5.object({
  setupModule: z5.string().optional(),
  wrapperModule: z5.string().optional(),
  wrapperExport: z5.string().optional(),
  css: z5.array(z5.string()).optional(),
  theme: z5.enum(["light", "dark"]).optional()
});
var contractProvenanceSchema = z5.object({
  source: z5.enum(["manual", "extracted", "merged", "migrated"]),
  verified: z5.boolean(),
  frameworkSupport: z5.enum(["native", "manual-only"]).optional(),
  sourceHash: z5.string().optional(),
  extractedAt: z5.string().optional()
});
var contractFigmaSchema = z5.object({
  nodeUrl: z5.string().optional(),
  propMappings: z5.record(z5.string(), z5.object({
    type: z5.enum(["string", "boolean", "enum", "instance", "children", "textContent"]),
    figmaProperty: z5.string(),
    values: z5.record(z5.string(), z5.string()).optional()
  })).optional()
});
var componentContractSchema = z5.object({
  $schema: z5.string(),
  name: z5.string(),
  description: z5.string(),
  category: z5.string(),
  tags: z5.array(z5.string()).optional(),
  status: z5.enum(["stable", "beta", "deprecated", "experimental"]).optional(),
  framework: z5.enum(["react", "vue", "svelte", "web-components", "angular"]).optional(),
  sourcePath: z5.string(),
  exportName: z5.string(),
  propsSummary: z5.array(z5.string()),
  props: z5.record(z5.string(), contractPropSchema),
  usage: contractUsageSchema,
  examples: z5.array(contractExampleSchema).optional(),
  relations: z5.array(contractRelationSchema).optional(),
  contract: contractContractSchema.optional(),
  ai: contractAiSchema.optional(),
  preview: contractPreviewSchema.optional(),
  provenance: contractProvenanceSchema,
  tokens: z5.array(z5.string()).optional(),
  figma: contractFigmaSchema.optional()
});

// src/contract-parser.ts
function isContractFile(filePath) {
  return filePath.endsWith(".contract.json");
}
function parseComponentContract(content, filePath) {
  const raw = JSON.parse(content);
  const validated = componentContractSchema.parse(raw);
  return {
    filePath,
    meta: {
      name: validated.name,
      description: validated.description,
      category: validated.category,
      tags: validated.tags,
      status: validated.status,
      figma: validated.figma?.nodeUrl,
      figmaProps: validated.figma?.propMappings
    },
    usage: validated.usage,
    props: validated.props,
    relations: validated.relations ?? [],
    variants: (validated.examples ?? []).map((ex) => ({
      name: ex.name,
      description: ex.description,
      code: ex.code,
      args: ex.args
    })),
    contract: validated.contract ? {
      ...validated.contract,
      // Merge top-level propsSummary into contract.propsSummary if not already set
      propsSummary: validated.contract.propsSummary ?? validated.propsSummary
    } : {
      propsSummary: validated.propsSummary
    },
    framework: validated.framework,
    ai: validated.ai,
    propsSummary: validated.propsSummary,
    provenance: validated.provenance,
    sourcePath: validated.sourcePath,
    exportName: validated.exportName
  };
}

// src/dtcg-generators.ts
function parseToFlatTokens(tokens, prefix) {
  const parsed = parseDtcgTokens(JSON.stringify(tokens), "tokens.tokens.json");
  const effectivePrefix = prefix ? `--${prefix.replace(/^--/, "").replace(/-$/, "")}-` : parsed.prefix;
  const result = [];
  for (const [category, categoryTokens] of Object.entries(parsed.categories)) {
    for (const token of categoryTokens) {
      let cssName = token.name;
      if (prefix && token.name.startsWith(parsed.prefix)) {
        cssName = effectivePrefix + token.name.slice(parsed.prefix.length);
      }
      result.push({
        cssName,
        cssValue: token.value ?? "",
        category,
        description: token.description
      });
    }
  }
  return result;
}
function generateCSSCustomProperties(tokens, options) {
  const selector = options?.selector ?? ":root";
  const flatTokens = parseToFlatTokens(tokens, options?.prefix);
  const lines = [];
  lines.push(`${selector} {`);
  const grouped = /* @__PURE__ */ new Map();
  for (const token of flatTokens) {
    const group = grouped.get(token.category) ?? [];
    group.push(token);
    grouped.set(token.category, group);
  }
  for (const [category, categoryTokens] of grouped) {
    lines.push(`  /* ${category} */`);
    for (const token of categoryTokens) {
      if (token.description) {
        lines.push(`  /* ${token.description} */`);
      }
      lines.push(`  ${token.cssName}: ${token.cssValue};`);
    }
    lines.push("");
  }
  lines.push("}");
  return lines.join("\n");
}
function generateSCSSVariables(tokens, options) {
  const flatTokens = parseToFlatTokens(tokens, options?.prefix);
  const lines = [];
  lines.push("// Auto-generated from DTCG token file");
  lines.push("// Do not edit directly \u2014 modify the .tokens.json source");
  lines.push("");
  const grouped = /* @__PURE__ */ new Map();
  for (const token of flatTokens) {
    const group = grouped.get(token.category) ?? [];
    group.push(token);
    grouped.set(token.category, group);
  }
  for (const [category, categoryTokens] of grouped) {
    lines.push(`// ${category}`);
    for (const token of categoryTokens) {
      const scssName = token.cssName.replace(/^--/, "$");
      lines.push(`${scssName}: ${token.cssValue} !default;`);
    }
    lines.push("");
  }
  return lines.join("\n");
}
function generateTailwindConfig(tokens) {
  const flatTokens = parseToFlatTokens(tokens);
  const theme = {};
  for (const token of flatTokens) {
    const tailwindKey = categoryToTailwindKey(token.category);
    if (!tailwindKey) continue;
    if (!theme[tailwindKey]) {
      theme[tailwindKey] = {};
    }
    const parts = token.cssName.replace(/^--[\w]+-/, "").split("-");
    const tokenKey = parts.join("-") || token.cssName;
    theme[tailwindKey][tokenKey] = `var(${token.cssName})`;
  }
  return {
    theme: {
      extend: theme
    }
  };
}
function categoryToTailwindKey(category) {
  switch (category) {
    case "colors":
    case "surfaces":
    case "text":
      return "colors";
    case "spacing":
      return "spacing";
    case "radius":
      return "borderRadius";
    case "typography":
      return "fontFamily";
    case "shadows":
      return "boxShadow";
    case "borders":
      return "borderWidth";
    case "transitions":
      return "transitionDuration";
    case "z-index":
      return "zIndex";
    default:
      return void 0;
  }
}
function generateFigmaVariables(tokens) {
  const flatTokens = parseToFlatTokens(tokens);
  const collections = /* @__PURE__ */ new Map();
  for (const token of flatTokens) {
    const collectionName = categoryToFigmaCollection(token.category);
    const vars = collections.get(collectionName) ?? [];
    vars.push({
      name: token.cssName.replace(/^--/, "").replace(/-/g, "/"),
      type: categoryToFigmaType(token.category),
      value: parseFigmaValue(token.cssValue, token.category),
      description: token.description,
      scopes: categoryToFigmaScopes(token.category)
    });
    collections.set(collectionName, vars);
  }
  return Array.from(collections.entries()).map(([name, variables]) => ({
    name,
    modes: [{ name: "Default", modeId: "default" }],
    variables
  }));
}
function categoryToFigmaCollection(category) {
  switch (category) {
    case "colors":
    case "surfaces":
    case "text":
      return "Colors";
    case "spacing":
    case "radius":
      return "Dimensions";
    case "typography":
      return "Typography";
    case "shadows":
      return "Effects";
    default:
      return "Other";
  }
}
function categoryToFigmaType(category) {
  switch (category) {
    case "colors":
    case "surfaces":
    case "text":
      return "COLOR";
    case "spacing":
    case "radius":
    case "z-index":
      return "FLOAT";
    default:
      return "STRING";
  }
}
function categoryToFigmaScopes(category) {
  switch (category) {
    case "colors":
      return ["ALL_FILLS", "STROKE_COLOR"];
    case "surfaces":
      return ["FRAME_FILL", "SHAPE_FILL"];
    case "text":
      return ["TEXT_FILL"];
    case "spacing":
      return ["GAP", "WIDTH_HEIGHT"];
    case "radius":
      return ["CORNER_RADIUS"];
    default:
      return ["ALL_SCOPES"];
  }
}
function parseFigmaValue(cssValue, category) {
  if (category === "colors" || category === "surfaces" || category === "text") {
    const color = parseColor(cssValue);
    if (color) {
      return {
        r: color.r / 255,
        g: color.g / 255,
        b: color.b / 255,
        a: color.a ?? 1
      };
    }
  }
  if (category === "spacing" || category === "radius") {
    const numMatch = cssValue.match(/^(\d+(?:\.\d+)?)/);
    if (numMatch) {
      return parseFloat(numMatch[1]);
    }
  }
  return cssValue;
}

// src/composition.ts
var CATEGORY_AFFINITIES = {
  forms: ["feedback"],
  actions: ["feedback"]
};
function analyzeComposition(fragments, componentNames, _context, options) {
  const allNames = new Set(Object.keys(fragments));
  const components = [];
  const unknown = [];
  for (const name of componentNames) {
    if (allNames.has(name)) {
      components.push(name);
    } else {
      unknown.push(name);
    }
  }
  const selectedSet = new Set(components);
  const warnings = [];
  const suggestions = [];
  const guidelines = [];
  const suggestedSet = /* @__PURE__ */ new Set();
  for (const name of components) {
    const fragment = fragments[name];
    if (fragment.relations) {
      for (const rel of fragment.relations) {
        switch (rel.relationship) {
          case "parent":
            if (!selectedSet.has(rel.component)) {
              warnings.push({
                type: "missing_parent",
                component: name,
                message: `"${name}" expects to be wrapped by "${rel.component}"${rel.note ? `: ${rel.note}` : ""}`,
                relatedComponent: rel.component
              });
            }
            break;
          case "child":
            if (!selectedSet.has(rel.component) && !suggestedSet.has(rel.component)) {
              suggestions.push({
                component: rel.component,
                reason: `"${name}" typically contains "${rel.component}"${rel.note ? `: ${rel.note}` : ""}`,
                relationship: "child",
                sourceComponent: name
              });
              suggestedSet.add(rel.component);
            }
            break;
          case "composition":
            if (!selectedSet.has(rel.component)) {
              warnings.push({
                type: "missing_composition",
                component: name,
                message: `"${name}" is typically used together with "${rel.component}"${rel.note ? `: ${rel.note}` : ""}`,
                relatedComponent: rel.component
              });
            }
            break;
          case "sibling":
            if (!selectedSet.has(rel.component) && !suggestedSet.has(rel.component)) {
              suggestions.push({
                component: rel.component,
                reason: `"${rel.component}" is a sibling of "${name}"${rel.note ? `: ${rel.note}` : ""}`,
                relationship: "sibling",
                sourceComponent: name
              });
              suggestedSet.add(rel.component);
            }
            break;
          case "alternative":
            if (selectedSet.has(rel.component)) {
              warnings.push({
                type: "redundant_alternative",
                component: name,
                message: `"${name}" and "${rel.component}" are alternatives \u2014 using both may be redundant${rel.note ? `: ${rel.note}` : ""}`,
                relatedComponent: rel.component
              });
            }
            break;
        }
      }
    }
    if (fragment.usage?.whenNot) {
      for (const whenNotEntry of fragment.usage.whenNot) {
        const lower = whenNotEntry.toLowerCase();
        for (const other of components) {
          if (other !== name && lower.includes(other.toLowerCase())) {
            guidelines.push({
              component: name,
              guideline: `Potential conflict with "${other}": ${whenNotEntry}`
            });
          }
        }
      }
    }
    if (fragment.meta.status === "deprecated") {
      warnings.push({
        type: "deprecated",
        component: name,
        message: fragment.meta.description ? `"${name}" is deprecated: ${fragment.meta.description}` : `"${name}" is deprecated`
      });
    } else if (fragment.meta.status === "experimental") {
      warnings.push({
        type: "experimental",
        component: name,
        message: `"${name}" is experimental and may change without notice`
      });
    }
  }
  const selectedCategories = new Set(
    components.map((name) => fragments[name].meta.category)
  );
  for (const [category, affinities] of Object.entries(CATEGORY_AFFINITIES)) {
    if (!selectedCategories.has(category)) continue;
    for (const neededCategory of affinities) {
      if (selectedCategories.has(neededCategory)) continue;
      const candidate = findBestCategoryCandidate(
        fragments,
        neededCategory,
        selectedSet,
        suggestedSet
      );
      if (candidate) {
        suggestions.push({
          component: candidate,
          reason: `Compositions using "${category}" components often benefit from a "${neededCategory}" component`,
          relationship: "category_gap",
          sourceComponent: components.find(
            (n) => fragments[n].meta.category === category
          )
        });
        suggestedSet.add(candidate);
      }
    }
  }
  if (options?.graph) {
    const engine = new ComponentGraphEngine(options.graph);
    for (const name of components) {
      const deps = engine.dependencies(name, ["imports", "hook-depends"]);
      for (const dep of deps) {
        if (!selectedSet.has(dep.target) && !suggestedSet.has(dep.target) && allNames.has(dep.target)) {
          suggestions.push({
            component: dep.target,
            reason: `"${name}" ${dep.type === "hook-depends" ? "uses a hook from" : "imports"} "${dep.target}"`,
            relationship: "composition",
            sourceComponent: name
          });
          suggestedSet.add(dep.target);
        }
      }
    }
    for (const name of components) {
      const blocks = engine.blocksUsing(name);
      for (const blockName of blocks) {
        const blockComps = options.graph.edges.filter(
          (e) => e.type === "composes" && e.provenance === `block:${blockName}` && (e.source === name || e.target === name)
        ).map((e) => e.source === name ? e.target : e.source);
        for (const comp of blockComps) {
          if (!selectedSet.has(comp) && !suggestedSet.has(comp) && allNames.has(comp)) {
            suggestions.push({
              component: comp,
              reason: `"${name}" and "${comp}" are used together in the "${blockName}" block`,
              relationship: "composition",
              sourceComponent: name
            });
            suggestedSet.add(comp);
          }
        }
      }
    }
  }
  return { components, unknown, warnings, suggestions, guidelines };
}
function findBestCategoryCandidate(fragments, category, selectedSet, suggestedSet) {
  let best = null;
  let bestScore = -1;
  for (const [name, fragment] of Object.entries(fragments)) {
    if (fragment.meta.category !== category) continue;
    if (selectedSet.has(name) || suggestedSet.has(name)) continue;
    const status = fragment.meta.status ?? "stable";
    let score = 0;
    if (status === "stable") score = 3;
    else if (status === "beta") score = 2;
    else if (status === "experimental") score = 1;
    if (score > bestScore) {
      bestScore = score;
      best = name;
    }
  }
  return best;
}

// src/preview-runtime.tsx
import { useEffect, useState } from "react";
import { Fragment, jsx } from "react/jsx-runtime";
var EMPTY_STATE = {
  content: null,
  isLoading: false,
  error: null,
  loadedData: void 0
};
function toError(error) {
  return error instanceof Error ? error : new Error(String(error));
}
async function executeVariantLoaders(loaders, loadedData) {
  const hasLoaders = !!loaders && loaders.length > 0;
  if (!hasLoaders) {
    return loadedData;
  }
  const results = await Promise.all(loaders.map((loader) => loader()));
  const mergedFromLoaders = results.reduce(
    (acc, result) => ({ ...acc, ...result }),
    {}
  );
  return loadedData ? { ...mergedFromLoaders, ...loadedData } : mergedFromLoaders;
}
async function resolvePreviewRuntimeState(options) {
  const { variant, loadedData } = options;
  if (!variant) {
    return EMPTY_STATE;
  }
  try {
    const mergedLoadedData = await executeVariantLoaders(variant.loaders, loadedData);
    const content = variant.render({ loadedData: mergedLoadedData });
    return {
      content,
      isLoading: false,
      error: null,
      loadedData: mergedLoadedData
    };
  } catch (error) {
    return {
      content: null,
      isLoading: false,
      error: toError(error),
      loadedData: void 0
    };
  }
}
function usePreviewVariantRuntime(options) {
  const { variant, loadedData } = options;
  const [state, setState] = useState(EMPTY_STATE);
  useEffect(() => {
    let cancelled = false;
    if (!variant) {
      setState(EMPTY_STATE);
      return () => {
        cancelled = true;
      };
    }
    const hasLoaders = !!variant.loaders && variant.loaders.length > 0;
    setState({
      content: null,
      isLoading: hasLoaders,
      error: null,
      loadedData: void 0
    });
    resolvePreviewRuntimeState({ variant, loadedData }).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [variant, loadedData]);
  return state;
}
function PreviewVariantRuntime({
  variant,
  loadedData,
  children: children2
}) {
  const state = usePreviewVariantRuntime({ variant, loadedData });
  return /* @__PURE__ */ jsx(Fragment, { children: children2(state) });
}

// src/component-discovery.ts
function isReactComponent(value) {
  if (!value) return false;
  if (typeof value === "function") {
    return true;
  }
  if (typeof value === "object") {
    const obj = value;
    if (obj.$$typeof) return true;
  }
  return false;
}
function discoverComponents(moduleExports, options) {
  const exclude = options?.exclude;
  const names = [];
  for (const [exportName, exportValue] of Object.entries(moduleExports)) {
    if (exclude?.has(exportName)) continue;
    if (!isReactComponent(exportValue)) continue;
    if (exportName[0] !== exportName[0].toUpperCase()) continue;
    names.push(exportName);
    const componentObj = exportValue;
    for (const key of Object.keys(componentObj)) {
      if (key.startsWith("_") || key.startsWith("$")) continue;
      if (key === "displayName" || key === "propTypes" || key === "defaultProps") continue;
      if (key === "render" || key === "Tabbed") continue;
      const subComponent = componentObj[key];
      if (isReactComponent(subComponent)) {
        names.push(`${exportName}.${key}`);
      }
    }
  }
  return names.sort();
}

// src/rules/finding.ts
function makeFinding(input) {
  if (input.evidence.length === 0) {
    throw new Error(`makeFinding(${input.ruleId}): findings must carry at least one evidence fact`);
  }
  const fingerprint = hash64Hex(
    canonicalJson({ ruleId: input.ruleId, ...input.fingerprintIdentity })
  );
  const code = byRuleId.get(input.ruleId);
  return normalizeFinding({
    ruleId: input.ruleId,
    ruleVersion: input.ruleVersion,
    severity: normalizeSeverity(input.severity),
    code: code?.code,
    helpUrl: code?.explainUrl,
    message: input.message,
    fingerprint,
    location: input.location,
    evidence: input.evidence,
    fix: input.fix,
    attributes: input.attributes
  });
}

// src/rules/utils.ts
function indexPropsByNodeId(ix) {
  const out = /* @__PURE__ */ new Map();
  for (const fact of ix.byKind("usage_prop_resolved")) {
    let bucket = out.get(fact.nodeId);
    if (!bucket) {
      bucket = [];
      out.set(fact.nodeId, bucket);
    }
    bucket.push(fact);
  }
  return out;
}
function indexTextChildrenByNodeId(ix) {
  const out = /* @__PURE__ */ new Map();
  for (const fact of ix.byKind("usage_text_child")) {
    let bucket = out.get(fact.nodeId);
    if (!bucket) {
      bucket = [];
      out.set(fact.nodeId, bucket);
    }
    bucket.push(fact);
  }
  return out;
}
function indexComponentByNodeId(ix) {
  const out = /* @__PURE__ */ new Map();
  for (const fact of ix.byKind("usage_component")) {
    out.set(fact.nodeId, fact);
  }
  return out;
}
function readUsageNode(ix, id) {
  const fact = ix.get(id);
  return fact && fact.kind === "usage_node" ? fact : void 0;
}
var UNIVERSAL_JSX_PROPS = /* @__PURE__ */ new Set([
  "key",
  "ref",
  "children",
  "className",
  "style",
  "id",
  "role",
  "tabIndex",
  "title",
  "slot",
  "lang",
  "dir",
  "hidden",
  "draggable",
  "spellCheck",
  "translate",
  "contentEditable",
  "suppressContentEditableWarning",
  "suppressHydrationWarning"
]);
function isUniversalJsxProp(prop) {
  if (UNIVERSAL_JSX_PROPS.has(prop)) return true;
  if (prop.startsWith("aria-")) return true;
  if (prop.startsWith("data-")) return true;
  if (prop.startsWith("on") && prop.length > 2 && prop[2] === prop[2].toUpperCase()) {
    return true;
  }
  return false;
}
function parseLengthValue(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const match = /^(-?\d+(?:\.\d+)?)(px|rem|em)?$/i.exec(trimmed);
  if (!match) return null;
  const value = Number.parseFloat(match[1]);
  if (!Number.isFinite(value)) return null;
  const rawUnit = match[2]?.toLowerCase();
  const unit = rawUnit === "px" || rawUnit === "rem" || rawUnit === "em" ? rawUnit : null;
  return { value, unit };
}
function matchesScale(value, allowed) {
  const magnitude = Math.abs(value);
  if (magnitude === 0) return allowed.includes(0);
  return allowed.includes(magnitude);
}
function normalizeLengthForScale(parsed, scale) {
  const unit = parsed.unit ?? scale.unit;
  if (unit === scale.unit) {
    return { value: parsed.value, deterministicFix: true };
  }
  if (scale.unit === "px" && unit === "rem") {
    const explicit = scale.rootFontSizePx !== void 0;
    const rootFontSizePx = scale.rootFontSizePx ?? 16;
    return {
      value: parsed.value * rootFontSizePx,
      deterministicFix: explicit,
      assumedRootFontSizePx: explicit ? void 0 : rootFontSizePx
    };
  }
  if (scale.unit === "px" && unit === "em") {
    const explicit = scale.emBasePx !== void 0;
    const emBasePx = scale.emBasePx ?? scale.rootFontSizePx ?? 16;
    return {
      value: parsed.value * emBasePx,
      deterministicFix: explicit,
      assumedEmBasePx: explicit ? void 0 : emBasePx
    };
  }
  if (scale.unit === "rem" && unit === "px") {
    const explicit = scale.rootFontSizePx !== void 0;
    if (!explicit) return null;
    return {
      value: parsed.value / scale.rootFontSizePx,
      deterministicFix: true
    };
  }
  return null;
}
function nearestScaleValue(value, allowed) {
  if (allowed.length === 0) return null;
  let best = allowed[0];
  let bestDelta = Math.abs(value - best);
  for (let i = 1; i < allowed.length; i++) {
    const candidate = allowed[i];
    const delta = Math.abs(value - candidate);
    if (delta < bestDelta || delta === bestDelta && candidate < best) {
      best = candidate;
      bestDelta = delta;
    }
  }
  return best;
}
function nearestSignedScaleValue(value, allowed) {
  const nearest = nearestScaleValue(Math.abs(value), allowed);
  if (nearest === null) return null;
  if (nearest === 0) return 0;
  return value < 0 ? -nearest : nearest;
}
function detectRawColor(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const scanned = stripVarExpressions(trimmed);
  if (!scanned.trim()) return null;
  const hex = /#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})\b/i.exec(scanned);
  if (hex) return hex[0];
  const fn = /(rgb|rgba|hsl|hsla)\s*\([^)]*\)/i.exec(scanned);
  if (fn) return fn[0];
  for (const part of scanned.split(/\s+/)) {
    const lower = part.toLowerCase();
    if (/^[a-z]+$/i.test(lower) && CSS_NAMED_COLORS.has(lower)) {
      return lower;
    }
  }
  return null;
}
function stripVarExpressions(value) {
  let out = value;
  const innermost = /var\([^()]*\)/gi;
  for (let i = 0; i < 10 && innermost.test(out); i += 1) {
    out = out.replace(innermost, " ");
    innermost.lastIndex = 0;
  }
  return out;
}
var CSS_NAMED_COLORS = /* @__PURE__ */ new Set([
  "aliceblue",
  "antiquewhite",
  "aqua",
  "aquamarine",
  "azure",
  "beige",
  "bisque",
  "black",
  "blanchedalmond",
  "blue",
  "blueviolet",
  "brown",
  "burlywood",
  "cadetblue",
  "chartreuse",
  "chocolate",
  "coral",
  "cornflowerblue",
  "cornsilk",
  "crimson",
  "cyan",
  "darkblue",
  "darkcyan",
  "darkgoldenrod",
  "darkgray",
  "darkgreen",
  "darkgrey",
  "darkkhaki",
  "darkmagenta",
  "darkolivegreen",
  "darkorange",
  "darkorchid",
  "darkred",
  "darksalmon",
  "darkseagreen",
  "darkslateblue",
  "darkslategray",
  "darkslategrey",
  "darkturquoise",
  "darkviolet",
  "deeppink",
  "deepskyblue",
  "dimgray",
  "dimgrey",
  "dodgerblue",
  "firebrick",
  "floralwhite",
  "forestgreen",
  "fuchsia",
  "gainsboro",
  "ghostwhite",
  "gold",
  "goldenrod",
  "gray",
  "green",
  "greenyellow",
  "grey",
  "honeydew",
  "hotpink",
  "indianred",
  "indigo",
  "ivory",
  "khaki",
  "lavender",
  "lavenderblush",
  "lawngreen",
  "lemonchiffon",
  "lightblue",
  "lightcoral",
  "lightcyan",
  "lightgoldenrodyellow",
  "lightgray",
  "lightgreen",
  "lightgrey",
  "lightpink",
  "lightsalmon",
  "lightseagreen",
  "lightskyblue",
  "lightslategray",
  "lightslategrey",
  "lightsteelblue",
  "lightyellow",
  "lime",
  "limegreen",
  "linen",
  "magenta",
  "maroon",
  "mediumaquamarine",
  "mediumblue",
  "mediumorchid",
  "mediumpurple",
  "mediumseagreen",
  "mediumslateblue",
  "mediumspringgreen",
  "mediumturquoise",
  "mediumvioletred",
  "midnightblue",
  "mintcream",
  "mistyrose",
  "moccasin",
  "navajowhite",
  "navy",
  "oldlace",
  "olive",
  "olivedrab",
  "orange",
  "orangered",
  "orchid",
  "palegoldenrod",
  "palegreen",
  "paleturquoise",
  "palevioletred",
  "papayawhip",
  "peachpuff",
  "peru",
  "pink",
  "plum",
  "powderblue",
  "purple",
  "rebeccapurple",
  "red",
  "rosybrown",
  "royalblue",
  "saddlebrown",
  "salmon",
  "sandybrown",
  "seagreen",
  "seashell",
  "sienna",
  "silver",
  "skyblue",
  "slateblue",
  "slategray",
  "slategrey",
  "snow",
  "springgreen",
  "steelblue",
  "tan",
  "teal",
  "thistle",
  "tomato",
  "turquoise",
  "violet",
  "wheat",
  "white",
  "whitesmoke",
  "yellow",
  "yellowgreen"
]);
var COLOR_KEYWORD_EXEMPTIONS = /* @__PURE__ */ new Set([
  "transparent",
  "currentcolor",
  "inherit",
  "initial",
  "unset"
]);
function isExemptColor(value, except) {
  const lower = value.trim().toLowerCase();
  if (COLOR_KEYWORD_EXEMPTIONS.has(lower)) return true;
  for (const ex of except) {
    if (ex.toLowerCase() === lower) return true;
  }
  return false;
}
function tokenReference(name) {
  if (name.startsWith("$")) return name;
  const cssVarName = name.startsWith("--") ? name : `--${name.replace(/\./g, "-")}`;
  return `var(${cssVarName})`;
}
function isApplicableTokenReference(referenceFormat) {
  return referenceFormat === void 0 || referenceFormat === "css-var" || referenceFormat === "scss-var";
}

// src/rules/a11y-required-accessible-name.ts
var RULE_ID = "a11y/required-accessible-name";
var RULE_VERSION = "1";
var NAME_SOURCE_PROPS = /* @__PURE__ */ new Set(["aria-label", "aria-labelledby", "title"]);
function ruleA11yRequiredAccessibleName(ix) {
  const findings = [];
  const propsByNode = indexPropsByNodeId(ix);
  const textByNode = indexTextChildrenByNodeId(ix);
  for (const usageComponent of ix.byKind("usage_component")) {
    const policy = ix.policy.a11yNameRequired(usageComponent.componentId);
    if (!policy) continue;
    const node = readUsageNode(ix, usageComponent.nodeId);
    if (!node) continue;
    const props = propsByNode.get(node.id) ?? [];
    if (props.some(isAriaHidden)) continue;
    if (hasAccessibleName(props, textByNode.get(node.id) ?? [])) continue;
    findings.push(
      makeFinding({
        ruleId: RULE_ID,
        ruleVersion: RULE_VERSION,
        severity: policy.severity,
        message: `<${node.element}> has no accessible name. ${policy.because}`.trim(),
        location: node.location,
        evidence: ix.evidence([node.id, usageComponent.id, policy.id]),
        fingerprintIdentity: {
          file: node.file,
          componentId: usageComponent.componentId,
          element: node.element,
          nodePath: node.nodePath
        },
        attributes: {
          componentId: usageComponent.componentId,
          element: node.element
        }
      })
    );
  }
  return findings;
}
function hasAccessibleName(props, textChildren) {
  for (const child of textChildren) {
    if (typeof child.text === "string" && child.text.trim().length > 0) return true;
  }
  for (const prop of props) {
    if (!NAME_SOURCE_PROPS.has(prop.prop)) continue;
    if (prop.resolution === "spread") continue;
    if (prop.resolution === "dynamic") return true;
    if (prop.resolution === "static") {
      if (typeof prop.value === "string" && prop.value.trim().length > 0) return true;
      if (prop.value !== void 0 && prop.value !== "" && prop.value !== null) return true;
    }
  }
  return false;
}
function isAriaHidden(prop) {
  if (prop.prop !== "aria-hidden") return false;
  if (prop.resolution !== "static") return false;
  return prop.value === true || prop.value === "true";
}

// src/rules/components-prefer-library.ts
var RULE_ID2 = "components/prefer-library";
var RULE_VERSION2 = "1";
function ruleComponentsPreferLibrary(ix) {
  const policy = ix.policy.ruleConfig(RULE_ID2);
  if (!policy?.enabled) return [];
  const sources = canonicalSourcesFromPolicy(policy.options?.canonicalSources);
  const mappings = canonicalMappingsFromPolicy(policy.options?.canonicalMappings);
  if (sources.length === 0 && mappings.length === 0) return [];
  const importsByLocal = indexImportsByLocal(ix);
  const findings = [];
  const seenImportFixes = /* @__PURE__ */ new Set();
  for (const node of ix.byKind("usage_node")) {
    if (node.element.includes(".")) continue;
    const imported = importsByLocal.get(node.element);
    const mapped = findMapping(node.element, mappings);
    if (mapped && imported && isMappedCanonicalImport(imported, mapped)) continue;
    if (imported && isCanonicalImport(imported, node.element, sources)) continue;
    if (mapped) {
      if (!shouldSuggestMapped(node, imported, mapped)) continue;
      const importPath = mapped.importPath;
      const suggestedComponent2 = mapped.name;
      const suggestedImport2 = importPath ?? "the canonical library";
      const propMapping = mapped.propMapping ?? [];
      if (imported && importPath && imported.source !== importPath && node.element === suggestedComponent2) {
        const key = `${imported.file}\0${imported.local}\0${imported.source}\0${importPath}`;
        if (seenImportFixes.has(key)) continue;
        seenImportFixes.add(key);
        findings.push(
          makeFinding({
            ruleId: RULE_ID2,
            ruleVersion: RULE_VERSION2,
            severity: policy.severity ?? "warn",
            message: `<${node.element}> should import from ${importPath}.`,
            location: imported.location,
            evidence: ix.evidence([node.id, imported.id, policy.id]),
            fingerprintIdentity: {
              file: imported.file,
              local: imported.local,
              from: imported.source,
              to: importPath
            },
            fix: {
              kind: "replaceImport",
              title: `Replace import path with "${importPath}"`,
              from: imported.source,
              to: importPath,
              deterministic: true
            },
            attributes: {
              rawValue: imported.source,
              suggestedComponent: suggestedComponent2,
              suggestedImport: importPath,
              canonical: mapped.canonical,
              confidence: mapped.confidence
            }
          })
        );
        continue;
      }
      findings.push(
        makeFinding({
          ruleId: RULE_ID2,
          ruleVersion: RULE_VERSION2,
          severity: policy.severity ?? "warn",
          message: node.element === "button" ? `Bespoke <button> has a library equivalent. Swap to <${suggestedComponent2}> from ${suggestedImport2}.` : `Bespoke <${node.element}> should use <${suggestedComponent2}> from ${suggestedImport2}.`,
          location: node.location,
          evidence: ix.evidence(
            imported ? [node.id, imported.id, policy.id] : [node.id, policy.id]
          ),
          fingerprintIdentity: {
            file: node.file,
            nodePath: node.nodePath,
            element: node.element,
            suggestedComponent: suggestedComponent2,
            suggestedImport: suggestedImport2
          },
          ...isIdentifier(node.element) && isIdentifier(suggestedComponent2) ? {
            fix: {
              kind: "replaceComponent",
              title: `Replace <${node.element}> with <${suggestedComponent2}>`,
              from: node.element,
              to: suggestedComponent2,
              deterministic: true
            }
          } : {},
          attributes: {
            rawValue: node.element,
            suggestedComponent: suggestedComponent2,
            suggestedImport: suggestedImport2,
            canonical: mapped.canonical,
            confidence: mapped.confidence,
            propMapping
          }
        })
      );
      continue;
    }
    const suggestion = findSuggestionSource(
      node.element === "button" ? "Button" : node.element,
      sources
    );
    if (!suggestion) continue;
    if (!shouldSuggest(node, imported)) continue;
    const suggestedComponent = node.element === "button" ? "Button" : node.element;
    const suggestedImport = sourceLabel(suggestion);
    findings.push(
      makeFinding({
        ruleId: RULE_ID2,
        ruleVersion: RULE_VERSION2,
        severity: policy.severity ?? "warn",
        message: node.element === "button" ? `Bespoke <button> has a library equivalent. Swap to <Button> from ${suggestedImport}.` : `Bespoke <${node.element}> should use the canonical library component from ${suggestedImport}.`,
        location: node.location,
        evidence: ix.evidence(imported ? [node.id, imported.id, policy.id] : [node.id, policy.id]),
        fingerprintIdentity: {
          file: node.file,
          nodePath: node.nodePath,
          element: node.element,
          suggestedComponent,
          suggestedImport
        },
        attributes: {
          rawValue: node.element,
          suggestedComponent,
          suggestedImport,
          suggestedImportSourceKind: suggestion.kind
        }
      })
    );
  }
  return findings;
}
function shouldSuggestMapped(node, imported, mapping) {
  if (node.element === mapping.htmlEquivalent) return true;
  if (node.element === mapping.name || node.element === mapping.canonical) {
    return imported !== void 0;
  }
  return false;
}
function shouldSuggest(node, imported) {
  if (node.element === "button") return true;
  if (!/^[A-Z]/.test(node.element)) return false;
  return imported !== void 0;
}
function indexImportsByLocal(ix) {
  const out = /* @__PURE__ */ new Map();
  for (const usage of ix.byKind("usage_import")) out.set(usage.local, usage);
  return out;
}
function canonicalSourcesFromPolicy(value) {
  if (!Array.isArray(value)) return [];
  return value.filter(isCanonicalSource);
}
function canonicalMappingsFromPolicy(value) {
  if (!Array.isArray(value)) return [];
  return value.filter(isCanonicalMappingOption);
}
function isCanonicalMappingOption(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value;
  if (typeof record.name !== "string" || !isIdentifier(record.name)) return false;
  if (record.canonical !== void 0 && typeof record.canonical !== "string") return false;
  if (record.htmlEquivalent !== void 0 && typeof record.htmlEquivalent !== "string") {
    return false;
  }
  if (record.importPath !== void 0 && typeof record.importPath !== "string") return false;
  if (record.confidence !== void 0 && typeof record.confidence !== "number") return false;
  if (record.propMapping !== void 0) {
    if (!Array.isArray(record.propMapping)) return false;
    if (!record.propMapping.every(isPropMappingOption)) return false;
  }
  return true;
}
function isPropMappingOption(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value;
  return typeof record.rawProp === "string" && typeof record.canonicalProp === "string" && (record.valueMap === void 0 || isStringRecord(record.valueMap));
}
function isStringRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return Object.values(value).every(
    (entry) => typeof entry === "string"
  );
}
function isCanonicalSource(value) {
  if (!value || typeof value !== "object") return false;
  const record = value;
  if (record.kind === "npm") return typeof record.specifier === "string";
  if (record.kind === "directory") return typeof record.path === "string";
  return false;
}
function findMapping(element, mappings) {
  return mappings.find((mapping) => {
    if (mapping.htmlEquivalent === element) return true;
    if (mapping.name === element) return true;
    return mapping.canonical === element;
  });
}
function isMappedCanonicalImport(usage, mapping) {
  return Boolean(mapping.importPath && usage.source === mapping.importPath);
}
function isCanonicalImport(usage, componentName, sources) {
  return sources.some((source) => {
    if (source.exclude?.includes(componentName)) return false;
    if (source.kind === "npm") {
      return usage.source === source.specifier || usage.source.startsWith(`${source.specifier}/`);
    }
    return normalizePath(usage.source).startsWith(normalizePath(source.path));
  });
}
function findSuggestionSource(componentName, sources) {
  return sources.find((source) => !source.exclude?.includes(componentName));
}
function sourceLabel(source) {
  return source.kind === "npm" ? source.specifier : source.path;
}
function normalizePath(path) {
  return path.replace(/\\/g, "/").replace(/^\.\//, "");
}
function isIdentifier(value) {
  return /^[A-Za-z_$][\w$]*$/.test(value);
}

// src/rules/components-forbidden-prop-value.ts
var RULE_ID3 = "components/forbidden-prop-value";
var RULE_VERSION3 = "1";
function ruleComponentsForbiddenPropValue(ix) {
  const findings = [];
  const componentByNode = indexComponentByNodeId(ix);
  const propsByNode = indexPropsByNodeId(ix);
  for (const [nodeId, propUsages] of propsByNode) {
    const usageComponent = componentByNode.get(nodeId);
    if (!usageComponent) continue;
    const node = readUsageNode(ix, nodeId);
    if (!node) continue;
    for (const propUsage of propUsages) {
      if (propUsage.resolution !== "static") continue;
      if (propUsage.value === void 0) continue;
      const matched = ix.policy.forbiddenPropValues(usageComponent.componentId, {
        prop: propUsage.prop,
        value: propUsage.value,
        path: node.file
      });
      if (matched.length === 0) continue;
      const policy = matched[0];
      const replacement = policy.replaceWith;
      const fix = replacement !== void 0 ? {
        kind: "replacePropValue",
        title: `Replace ${propUsage.prop} with ${formatValue(replacement)}`,
        prop: propUsage.prop,
        value: replacement,
        deterministic: true
      } : void 0;
      findings.push(
        makeFinding({
          ruleId: RULE_ID3,
          ruleVersion: RULE_VERSION3,
          severity: policy.severity,
          message: buildMessage(
            node.element,
            propUsage.prop,
            propUsage.value,
            policy.because,
            replacement
          ),
          location: node.location,
          evidence: ix.evidence([node.id, usageComponent.id, propUsage.id, policy.id]),
          fingerprintIdentity: {
            file: node.file,
            componentId: usageComponent.componentId,
            element: node.element,
            nodePath: node.nodePath,
            prop: propUsage.prop,
            value: propUsage.value
          },
          fix,
          attributes: {
            componentId: usageComponent.componentId,
            prop: propUsage.prop,
            rawValue: propUsage.value,
            suggestedValue: replacement
          }
        })
      );
    }
  }
  return findings;
}
function formatValue(value) {
  if (typeof value === "string") return `"${value}"`;
  return String(value);
}
function buildMessage(element, prop, value, because, replacement) {
  const head = `<${element}> ${prop}=${formatValue(value)} is forbidden.`;
  const reason = because ? ` ${because.trim()}` : "";
  const suggestion = replacement !== void 0 ? ` Use ${formatValue(replacement)}.` : "";
  return `${head}${reason}${suggestion}`.trim();
}

// src/rules/components-unknown-prop.ts
var RULE_ID4 = "components/unknown-prop";
var RULE_VERSION4 = "1";
function ruleComponentsUnknownProp(ix) {
  const policy = ix.policy.jsxUnknownPropsForbidden();
  if (!policy) return [];
  const findings = [];
  const componentByNode = indexComponentByNodeId(ix);
  const propsByNode = indexPropsByNodeId(ix);
  for (const [nodeId, propUsages] of propsByNode) {
    const usageComponent = componentByNode.get(nodeId);
    if (!usageComponent) continue;
    const knownProps = ix.components.propsOf(usageComponent.componentId);
    if (knownProps.length === 0) continue;
    const knownNames = new Set(knownProps.map((p) => p.prop));
    const node = readUsageNode(ix, nodeId);
    if (!node) continue;
    for (const propUsage of propUsages) {
      if (propUsage.resolution === "spread") continue;
      if (isUniversalJsxProp(propUsage.prop)) continue;
      if (knownNames.has(propUsage.prop)) continue;
      findings.push(
        makeFinding({
          ruleId: RULE_ID4,
          ruleVersion: RULE_VERSION4,
          severity: policy.severity,
          message: `Unknown prop "${propUsage.prop}" on <${node.element}>. Allowed props: ${formatPropList(knownNames)}.`,
          location: node.location,
          evidence: ix.evidence([node.id, usageComponent.id, propUsage.id, policy.id]),
          fingerprintIdentity: {
            file: node.file,
            componentId: usageComponent.componentId,
            element: node.element,
            nodePath: node.nodePath,
            prop: propUsage.prop
          },
          attributes: {
            componentId: usageComponent.componentId,
            prop: propUsage.prop
          }
        })
      );
    }
  }
  return findings;
}
function formatPropList(names) {
  if (names.size === 0) return "(none)";
  const sorted = [...names].sort();
  if (sorted.length <= 6) return sorted.join(", ");
  return `${sorted.slice(0, 6).join(", ")}, \u2026`;
}

// src/rules/jsx-preferred-component.ts
var RULE_ID5 = "components/preferred-component";
var RULE_VERSION5 = "1";
function ruleJsxPreferredComponent(ix) {
  const policies = ix.policy.jsxComponentPreferred();
  if (policies.length === 0) return [];
  const findings = [];
  const componentByNode = indexComponentByNodeId(ix);
  for (const [nodeId, usageComponent] of componentByNode) {
    const node = readUsageNode(ix, nodeId);
    if (!node) continue;
    if (node.element.includes(".")) continue;
    for (const policy of policies) {
      if (usageComponent.componentId !== policy.from) continue;
      const replacement = componentNameFromId(policy.to);
      if (!replacement) continue;
      findings.push(
        makeFinding({
          ruleId: RULE_ID5,
          ruleVersion: RULE_VERSION5,
          severity: policy.severity,
          message: `<${node.element}> should use <${replacement}>.`,
          location: node.location,
          evidence: ix.evidence([node.id, usageComponent.id, policy.id]),
          fingerprintIdentity: {
            file: node.file,
            nodePath: node.nodePath,
            from: policy.from,
            to: policy.to
          },
          fix: {
            kind: "replaceComponent",
            title: policy.because ?? `Replace <${node.element}> with <${replacement}>`,
            from: node.element,
            to: replacement,
            deterministic: true
          },
          attributes: {
            componentId: usageComponent.componentId,
            rawValue: node.element,
            suggestedComponent: replacement
          }
        })
      );
    }
  }
  return findings;
}
function componentNameFromId(componentId2) {
  const hash = componentId2.lastIndexOf("#");
  const name = hash >= 0 ? componentId2.slice(hash + 1) : componentId2;
  if (!/^[A-Za-z_$][\w$]*$/.test(name)) return void 0;
  return name;
}

// src/rules/jsx-preferred-import-path.ts
var RULE_ID6 = "imports/preferred-path";
var RULE_VERSION6 = "1";
function ruleJsxPreferredImportPath(ix) {
  const policies = ix.policy.jsxImportPathPreferred();
  if (policies.length === 0) return [];
  const findings = [];
  const seen = /* @__PURE__ */ new Set();
  for (const usage of ix.byKind("usage_import")) {
    const matches = policies.filter((policy) => {
      if (policy.from !== usage.source) return false;
      if (policy.imported !== void 0 && policy.imported !== usage.imported) return false;
      return true;
    });
    for (const policy of matches) {
      const importedIdentity = policy.imported ?? "*";
      const key = `${policy.id}\0${usage.file}\0${usage.source}\0${usage.location.line}\0${usage.location.column}\0${importedIdentity}`;
      if (seen.has(key)) continue;
      seen.add(key);
      findings.push(
        makeFinding({
          ruleId: RULE_ID6,
          ruleVersion: RULE_VERSION6,
          severity: policy.severity,
          message: `Import from "${usage.source}" should use "${policy.to}".`,
          location: usage.location,
          evidence: ix.evidence([usage.id, policy.id]),
          fingerprintIdentity: {
            file: usage.file,
            from: policy.from,
            to: policy.to,
            imported: policy.imported,
            line: usage.location.line,
            column: usage.location.column
          },
          fix: {
            kind: "replaceImport",
            title: policy.because ?? `Replace import path with "${policy.to}"`,
            from: policy.from,
            to: policy.to,
            deterministic: true
          },
          attributes: {
            importPath: usage.source,
            suggestedImport: policy.to,
            imported: usage.imported,
            local: usage.local,
            rawValue: usage.source
          }
        })
      );
    }
  }
  return findings;
}

// src/rules/props-invalid-value.ts
var RULE_ID7 = "props/invalid-value";
var RULE_VERSION7 = "1";
function rulePropsInvalidValue(ix) {
  const policy = ix.policy.ruleConfig(RULE_ID7);
  if (!policy?.enabled) return [];
  const findings = [];
  const componentByNode = indexComponentByNodeId(ix);
  const propsByNode = indexPropsByNodeId(ix);
  for (const [nodeId, propUsages] of propsByNode) {
    const usageComponent = componentByNode.get(nodeId);
    if (!usageComponent) continue;
    const node = readUsageNode(ix, nodeId);
    if (!node) continue;
    for (const propUsage of propUsages) {
      if (propUsage.resolution !== "static") continue;
      if (propUsage.value === void 0) continue;
      if (isUniversalJsxProp(propUsage.prop)) continue;
      const metadata = ix.components.propOf(usageComponent.componentId, propUsage.prop);
      if (!metadata?.values?.length) continue;
      const allowedValues = metadata.values;
      const rawValue = toComparableValue(propUsage.value);
      if (rawValue === null) continue;
      if (allowedValues.includes(rawValue)) continue;
      const fix = allowedValues.length === 1 ? {
        kind: "replacePropValue",
        title: `Replace ${propUsage.prop} with ${formatValue2(allowedValues[0])}`,
        prop: propUsage.prop,
        value: allowedValues[0],
        deterministic: true
      } : void 0;
      findings.push(
        makeFinding({
          ruleId: RULE_ID7,
          ruleVersion: RULE_VERSION7,
          severity: policy.severity ?? "warn",
          message: buildMessage2(node.element, propUsage.prop, propUsage.value, allowedValues),
          location: propUsage.location ?? node.location,
          evidence: ix.evidence([node.id, usageComponent.id, propUsage.id, metadata.id, policy.id]),
          fingerprintIdentity: {
            file: node.file,
            componentId: usageComponent.componentId,
            element: node.element,
            nodePath: node.nodePath,
            prop: propUsage.prop,
            value: propUsage.value
          },
          fix,
          attributes: {
            componentId: usageComponent.componentId,
            prop: propUsage.prop,
            rawValue: propUsage.value,
            allowedValues
          }
        })
      );
    }
  }
  return findings;
}
function toComparableValue(value) {
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value === null) return "null";
  return null;
}
function formatValue2(value) {
  if (typeof value === "string") return `"${value}"`;
  return String(value);
}
function buildMessage2(element, prop, value, allowedValues) {
  const allowed = allowedValues.map(formatValue2).join(", ");
  return `<${element}> ${prop}=${formatValue2(value)} is not an allowed value. Allowed values: ${allowed}.`;
}

// src/rules/styles-no-raw-color.ts
var RULE_ID8 = "styles/no-raw-color";
var RULE_VERSION8 = "1";
function ruleStylesNoRawColor(ix) {
  const policy = ix.policy.rawColorPolicy();
  if (!policy) return [];
  const colorTokens = ix.tokens.byCategory("color");
  const findings = [];
  for (const decl of ix.byKind("style_declaration")) {
    const color = detectRawColor(decl.value);
    if (!color) continue;
    if (isExemptColor(color, policy.except)) continue;
    const tokenMatch = findTokenMatch(decl.property, color, colorTokens);
    const evidenceIds = tokenMatch ? [decl.id, policy.id, tokenMatch.token.id] : [decl.id, policy.id];
    findings.push(
      makeFinding({
        ruleId: RULE_ID8,
        ruleVersion: RULE_VERSION8,
        severity: policy.severity,
        message: `Raw color ${color} on \`${decl.property}\`. Use a ${policy.prefer === "token" ? "design token" : "CSS variable"} instead.`,
        location: decl.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "style_declaration",
          file: decl.file,
          selector: decl.selector,
          declarationPath: decl.declarationPath,
          property: decl.property,
          value: color
        },
        fix: tokenMatch ? buildTokenFix(decl.property, decl.value, color, tokenMatch) : void 0,
        attributes: {
          property: decl.property,
          rawValue: decl.value,
          color,
          source: "css",
          suggestedToken: tokenMatch?.token.name
        }
      })
    );
  }
  const componentByNode = indexComponentByNodeId(ix);
  for (const inline of ix.byKind("usage_inline_style")) {
    if (inline.valueKind === "css-variable") continue;
    const color = detectRawColor(inline.value);
    if (!color) continue;
    if (isExemptColor(color, policy.except)) continue;
    const node = readUsageNode(ix, inline.nodeId);
    if (!node) continue;
    const componentEvidenceId = componentByNode.get(node.id)?.id;
    const tokenMatch = findTokenMatch(inline.property, color, colorTokens);
    const baseEvidence = componentEvidenceId ? [node.id, componentEvidenceId, inline.id, policy.id] : [node.id, inline.id, policy.id];
    const evidenceIds = tokenMatch ? [...baseEvidence, tokenMatch.token.id] : baseEvidence;
    findings.push(
      makeFinding({
        ruleId: RULE_ID8,
        ruleVersion: RULE_VERSION8,
        severity: policy.severity,
        message: `Raw color ${color} on inline \`${inline.property}\`. Use a ${policy.prefer === "token" ? "design token" : "CSS variable"} instead.`,
        location: node.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "usage_inline_style",
          file: node.file,
          nodePath: node.nodePath,
          element: node.element,
          property: inline.property,
          value: color
        },
        fix: tokenMatch ? buildTokenFix(inline.property, inline.value, color, tokenMatch) : void 0,
        attributes: {
          property: inline.property,
          rawValue: inline.value,
          color,
          source: "jsx",
          suggestedToken: tokenMatch?.token.name
        }
      })
    );
  }
  return findings;
}
function findTokenMatch(property, raw, tokens) {
  const target = normalizeColor2(raw);
  const matches = tokens.filter((token) => normalizeColor2(token.value) === target);
  if (matches.length === 0) return null;
  const role = colorRoleForProperty(property);
  if (matches.length === 1) {
    const conflicts = role ? tokenRoleConflicts(matches[0].name, role) : false;
    return { token: matches[0], ambiguous: conflicts };
  }
  const roleMatch = role ? matches.find((token) => tokenNameMatchesRole(token.name, role)) : void 0;
  if (roleMatch) return { token: roleMatch, ambiguous: false };
  return { token: matches[0], ambiguous: true };
}
function tokenRoleConflicts(name, role) {
  if (tokenNameMatchesRole(name, role)) return false;
  const others = ["fg", "bg", "border"].filter((r) => r !== role);
  return others.some((other) => tokenNameMatchesRole(name, other));
}
function colorRoleForProperty(property) {
  const p = property.toLowerCase();
  if (p === "color" || p === "fill" || p === "stroke" || p.endsWith("text-color")) return "fg";
  if (p === "background" || p.startsWith("background") || p === "fill") return "bg";
  if (p.startsWith("border") || p.startsWith("outline") || p === "box-shadow") return "border";
  return null;
}
function tokenNameMatchesRole(name, role) {
  const n = name.toLowerCase();
  if (role === "fg")
    return /\b(text|fg|foreground|content|ink|on-|label)\b|text|foreground/.test(n);
  if (role === "bg")
    return /\b(bg|background|surface|fill|elevated|canvas)\b|background|surface/.test(n);
  return /\b(border|outline|stroke|divider|ring)\b|border|outline/.test(n);
}
function normalizeColor2(value) {
  const lower = value.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3})$/.exec(lower);
  if (hex) {
    const [r, g2, b] = hex[1];
    return `#${r}${r}${g2}${g2}${b}${b}`;
  }
  const hex4 = /^#([0-9a-f]{4})$/.exec(lower);
  if (hex4) {
    const [r, g2, b, a] = hex4[1];
    return `#${r}${r}${g2}${g2}${b}${b}${a}${a}`;
  }
  return lower;
}
function buildTokenFix(property, rawValue, rawColor, match) {
  const reference = tokenReference(match.token.name);
  const value = replaceRawColor(rawValue, rawColor, reference);
  return {
    kind: "replaceStyleValue",
    title: `Replace ${property} with ${value}`,
    property,
    value,
    // Apply automatically only when the swap is unambiguous AND the reference
    // resolves verbatim. An ambiguous match (several tokens share the value,
    // none role-matching) or a token that can't be referenced from CSS (a Sass
    // *map* member, or a DTCG JSON token with no emitted custom property) is a
    // suggestion — applying it would cross roles or write non-compiling code.
    deterministic: !match.ambiguous && isApplicableTokenReference(match.token.referenceFormat)
  };
}
function replaceRawColor(rawValue, rawColor, replacement) {
  if (rawValue === rawColor) return replacement;
  if (rawValue.includes(rawColor)) return rawValue.replace(rawColor, replacement);
  const pattern = new RegExp(escapeRegExp(rawColor), "i");
  return rawValue.replace(pattern, replacement);
}
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// src/rules/styles-no-raw-dimensions.ts
var RULE_ID9 = "styles/no-raw-dimensions";
var RULE_VERSION9 = "1";
function ruleStylesNoRawDimensions(ix) {
  const policy = ix.policy.rawDimensionPolicy();
  if (!policy) return [];
  const appliesTo = new Set(policy.appliesTo);
  const spacingTokens = ix.tokens.byCategory("spacing");
  const radiusTokens = ix.tokens.byCategory("radius");
  const preferLabel = policy.prefer === "token" ? "design token" : "CSS variable";
  const findings = [];
  for (const decl of ix.byKind("style_declaration")) {
    const property = toKebabCase(decl.property);
    if (!appliesTo.has(property)) continue;
    const tokens = tokensForProperty(property, spacingTokens, radiusTokens);
    const resolution = resolveDimension(decl.value, tokens);
    if (!resolution) continue;
    const tokenIds = resolutionTokenIds(resolution);
    const evidenceIds = tokenIds.length ? [decl.id, policy.id, ...tokenIds] : [decl.id, policy.id];
    findings.push(
      makeFinding({
        ruleId: RULE_ID9,
        ruleVersion: RULE_VERSION9,
        severity: policy.severity,
        message: dimensionMessage(decl.value, decl.property, preferLabel, resolution, false),
        location: decl.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "style_declaration",
          file: decl.file,
          selector: decl.selector,
          declarationPath: decl.declarationPath,
          property: decl.property,
          value: decl.value
        },
        fix: dimensionFix(decl.property, resolution),
        attributes: {
          property: decl.property,
          rawValue: decl.value,
          source: "css",
          suggestedToken: resolutionSuggestedToken(resolution)
        }
      })
    );
  }
  const componentByNode = indexComponentByNodeId(ix);
  for (const inline of ix.byKind("usage_inline_style")) {
    if (inline.valueKind === "css-variable") continue;
    const property = toKebabCase(inline.property);
    if (!appliesTo.has(property)) continue;
    const node = readUsageNode(ix, inline.nodeId);
    if (!node) continue;
    const componentEvidenceId = componentByNode.get(node.id)?.id;
    const tokens = tokensForProperty(property, spacingTokens, radiusTokens);
    const resolution = resolveDimension(inline.value, tokens);
    if (!resolution) continue;
    const baseEvidence = componentEvidenceId ? [node.id, componentEvidenceId, inline.id, policy.id] : [node.id, inline.id, policy.id];
    const tokenIds = resolutionTokenIds(resolution);
    const evidenceIds = tokenIds.length ? [...baseEvidence, ...tokenIds] : baseEvidence;
    findings.push(
      makeFinding({
        ruleId: RULE_ID9,
        ruleVersion: RULE_VERSION9,
        severity: policy.severity,
        message: dimensionMessage(inline.value, inline.property, preferLabel, resolution, true),
        location: node.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "usage_inline_style",
          file: node.file,
          nodePath: node.nodePath,
          element: node.element,
          property: inline.property,
          value: inline.value
        },
        fix: dimensionFix(inline.property, resolution),
        attributes: {
          property: inline.property,
          rawValue: inline.value,
          source: "jsx",
          suggestedToken: resolutionSuggestedToken(resolution)
        }
      })
    );
  }
  return findings;
}
function toKebabCase(property) {
  return property.replace(/([A-Z])/g, "-$1").toLowerCase();
}
function tokensForProperty(property, spacingTokens, radiusTokens) {
  return property.includes("radius") ? radiusTokens : spacingTokens;
}
function resolveDimension(raw, tokens) {
  const exact = findDimensionFixMatch(raw, tokens);
  if (exact) {
    return {
      kind: "exact",
      tokens: exact.tokens,
      fixValue: exact.fixValue,
      suggestedToken: exact.suggestedToken
    };
  }
  if (!isRawDimensionValue(raw)) return null;
  const nearest = findNearestDimensionToken(raw, tokens);
  if (nearest) return { kind: "nearest", ...nearest };
  return { kind: "raw" };
}
function resolutionTokenIds(resolution) {
  if (resolution.kind === "exact") return resolution.tokens.map((token) => token.id);
  if (resolution.kind === "nearest") return [resolution.token.id];
  return [];
}
function resolutionSuggestedToken(resolution) {
  if (resolution.kind === "exact") return resolution.suggestedToken;
  if (resolution.kind === "nearest") return resolution.suggestedToken;
  return void 0;
}
function dimensionMessage(rawValue, property, preferLabel, resolution, inline) {
  const where = inline ? `inline \`${property}\`` : `\`${property}\``;
  if (resolution.kind === "nearest") {
    return `Raw dimension ${rawValue} on ${where}. Closest token is \`${resolution.suggestedToken}\` (${resolution.token.value}); use a ${preferLabel} instead.`;
  }
  return `Raw dimension ${rawValue} on ${where}. Use a ${preferLabel} instead.`;
}
function dimensionFix(property, resolution) {
  if (resolution.kind === "exact") {
    return {
      kind: "replaceStyleValue",
      title: `Replace ${property} with ${resolution.fixValue}`,
      property,
      value: resolution.fixValue,
      deterministic: true
    };
  }
  return void 0;
}
function findDimensionMatch(raw, tokens) {
  const target = normalizeDimension(raw);
  if (target === null) return null;
  for (const token of tokens) {
    if (normalizeDimension(token.value) === target) return token;
  }
  return null;
}
function findDimensionFixMatch(raw, tokens) {
  const single = findDimensionMatch(raw, tokens);
  if (single) {
    return {
      tokens: [single],
      fixValue: tokenVar(single),
      suggestedToken: single.name
    };
  }
  const parts = raw.trim().split(/\s+/);
  if (parts.length < 2 || parts.length > 4) return null;
  const matches = [];
  for (const part of parts) {
    const match = findDimensionMatch(part, tokens);
    if (!match) return null;
    matches.push(match);
  }
  return {
    tokens: matches,
    fixValue: matches.map(tokenVar).join(" "),
    suggestedToken: matches.map((token) => token.name).join(" ")
  };
}
function findNearestDimensionToken(raw, tokens) {
  const parsed = parseLengthValue(raw);
  if (!parsed) return null;
  const rawUnit = parsed.unit ?? "px";
  let best = null;
  for (const token of tokens) {
    const tokenLength = parseLengthValue(token.value);
    if (!tokenLength) continue;
    const tokenUnit = tokenLength.unit ?? "px";
    if (tokenUnit !== rawUnit) continue;
    if (tokenLength.value === parsed.value) continue;
    if (tokenLength.value === 0 && parsed.value !== 0) continue;
    if (best === null) {
      best = { token, value: tokenLength.value };
      continue;
    }
    const delta = Math.abs(parsed.value - tokenLength.value);
    const bestDelta = Math.abs(parsed.value - best.value);
    if (delta < bestDelta || delta === bestDelta && tokenLength.value < best.value) {
      best = { token, value: tokenLength.value };
    }
  }
  if (!best) return null;
  return {
    token: best.token,
    suggestedToken: best.token.name
  };
}
function isRawDimensionValue(raw) {
  if (normalizeDimension(raw) !== null) return true;
  const parts = raw.trim().split(/\s+/);
  return parts.length >= 2 && parts.length <= 4 && parts.every((part) => normalizeDimension(part) !== null);
}
function normalizeDimension(value) {
  const parsed = parseLengthValue(value);
  if (!parsed) return null;
  return `${parsed.value}${parsed.unit ?? "px"}`;
}
function tokenVar(token) {
  if (token.name.startsWith("$")) return token.name;
  const cssVarName = token.name.startsWith("--") ? token.name : `--${token.name.replace(/\./g, "-")}`;
  return `var(${cssVarName})`;
}

// src/rules/styles-no-raw-spacing.ts
var RULE_ID10 = "styles/no-raw-spacing";
var RULE_VERSION10 = "1";
function ruleStylesNoRawSpacing(ix) {
  const findings = [];
  const lookupCache = /* @__PURE__ */ new Map();
  const lookupFor = (scale) => {
    let lookup = lookupCache.get(scale.name);
    if (!lookup) {
      lookup = buildSpacingTokenLookup(ix, scale);
      lookupCache.set(scale.name, lookup);
    }
    return lookup;
  };
  for (const decl of ix.byKind("style_declaration")) {
    const finding = checkDeclaration(ix, decl, lookupFor);
    if (finding) findings.push(finding);
  }
  const componentByNode = indexComponentByNodeId(ix);
  for (const inline of ix.byKind("usage_inline_style")) {
    const finding = checkInlineStyle(ix, inline, componentByNode, lookupFor);
    if (finding) findings.push(finding);
  }
  return findings;
}
function buildSpacingTokenLookup(ix, scale) {
  const out = /* @__PURE__ */ new Map();
  for (const token of ix.tokens.byCategory("spacing")) {
    const parsed = parseLengthValue(token.value);
    if (!parsed) continue;
    const normalized = normalizeLengthForScale(parsed, scale);
    if (normalized === null) continue;
    const key = Math.abs(normalized.value);
    if (!out.has(key)) out.set(key, token);
  }
  return out;
}
function cssPropertyName(property) {
  return property.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}
function checkDeclaration(ix, decl, lookupFor) {
  const policy = ix.policy.propertyScale(decl.property);
  if (!policy) return null;
  const scale = ix.policy.scale(policy.scale);
  if (!scale) return null;
  const allowed = ix.policy.scaleValues(policy.scale).map((v) => v.value);
  if (allowed.length === 0) return null;
  const checked = checkSpacingValue(decl.value, allowed, scale, lookupFor(scale));
  if (!checked) return null;
  return makeFinding({
    ruleId: RULE_ID10,
    ruleVersion: RULE_VERSION10,
    severity: policy.severity,
    message: spacingMessage(decl.property, decl.value, allowed, scale.unit, checked),
    location: decl.location,
    evidence: ix.evidence([decl.id, policy.id, scale.id]),
    fingerprintIdentity: {
      source: "style_declaration",
      file: decl.file,
      selector: decl.selector,
      declarationPath: decl.declarationPath,
      property: decl.property,
      value: decl.value
    },
    fix: buildSpacingFix(decl.property, checked),
    attributes: {
      property: decl.property,
      rawValue: decl.value,
      scale: scale.name,
      allowed,
      suggestedValue: checked.suggestedValue,
      matchedToken: checked.matchedToken,
      normalizedValue: checked.normalizedValue,
      normalizedUnit: scale.unit,
      assumedRootFontSizePx: checked.assumedRootFontSizePx,
      assumedEmBasePx: checked.assumedEmBasePx,
      source: "css"
    }
  });
}
function checkInlineStyle(ix, inline, componentByNode, lookupFor) {
  const policy = ix.policy.propertyScale(cssPropertyName(inline.property));
  if (!policy) return null;
  const scale = ix.policy.scale(policy.scale);
  if (!scale) return null;
  const allowed = ix.policy.scaleValues(policy.scale).map((v) => v.value);
  if (allowed.length === 0) return null;
  if (inline.valueKind === "css-variable") return null;
  const checked = checkSpacingValue(
    inline.value,
    allowed,
    scale,
    lookupFor(scale),
    inline.valueKind === "number"
  );
  if (!checked) return null;
  const node = readUsageNode(ix, inline.nodeId);
  if (!node) return null;
  const componentEvidenceId = componentByNode.get(node.id)?.id;
  const evidenceIds = componentEvidenceId ? [node.id, componentEvidenceId, inline.id, policy.id, scale.id] : [node.id, inline.id, policy.id, scale.id];
  return makeFinding({
    ruleId: RULE_ID10,
    ruleVersion: RULE_VERSION10,
    severity: policy.severity,
    message: spacingMessage(inline.property, inline.value, allowed, scale.unit, checked),
    location: node.location,
    evidence: ix.evidence(evidenceIds),
    fingerprintIdentity: {
      source: "usage_inline_style",
      file: node.file,
      nodePath: node.nodePath,
      element: node.element,
      property: inline.property,
      value: inline.value
    },
    fix: buildSpacingFix(inline.property, checked),
    attributes: {
      property: inline.property,
      rawValue: inline.value,
      scale: scale.name,
      allowed,
      suggestedValue: checked.suggestedValue,
      matchedToken: checked.matchedToken,
      normalizedValue: checked.normalizedValue,
      normalizedUnit: scale.unit,
      assumedRootFontSizePx: checked.assumedRootFontSizePx,
      assumedEmBasePx: checked.assumedEmBasePx,
      source: "jsx"
    }
  });
}
function checkSpacingValue(raw, allowed, scale, tokens, bareNumberFix = false) {
  const parts = parseLengthParts(raw);
  if (!parts) return null;
  let firstViolation = null;
  const suggestedParts = [];
  let anyViolation = false;
  let anyOffScale = false;
  let canFixAll = true;
  let reliableSuggestion = true;
  let allApplicableValuePreserving = true;
  for (const part of parts) {
    const normalized = normalizeLengthForScale(part.parsed, scale);
    if (normalized === null) return null;
    const magnitude = Math.abs(normalized.value);
    const onScale = matchesScale(normalized.value, allowed);
    const exactToken = tokens.get(magnitude);
    if (onScale) {
      if (!exactToken) {
        suggestedParts.push(part.raw);
        continue;
      }
      anyViolation = true;
      const reference = tokenReference(exactToken.name);
      const applicable = isApplicableTokenReference(exactToken.referenceFormat) && normalized.deterministicFix;
      if (!applicable) allApplicableValuePreserving = false;
      suggestedParts.push(reference);
      if (!firstViolation) {
        firstViolation = {
          normalizedValue: normalized.value,
          suggestedValue: reference,
          fixEmittable: true,
          deterministicFix: applicable,
          reason: "token-equivalent",
          matchedToken: exactToken.name,
          assumedRootFontSizePx: normalized.assumedRootFontSizePx,
          assumedEmBasePx: normalized.assumedEmBasePx
        };
      }
      continue;
    }
    anyViolation = true;
    anyOffScale = true;
    allApplicableValuePreserving = false;
    const nearest = nearestSignedScaleValue(normalized.value, allowed);
    const snapsToZero = nearest === 0 && normalized.value !== 0;
    let suggestedPart;
    if (nearest === null || snapsToZero) {
      suggestedPart = void 0;
      canFixAll = false;
      suggestedParts.push(part.raw);
    } else {
      const nearestToken = tokens.get(Math.abs(nearest));
      suggestedPart = nearestToken ? tokenReference(nearestToken.name) : bareNumberFix ? `${nearest}` : `${nearest}${scale.unit}`;
      suggestedParts.push(suggestedPart);
      if (!normalized.deterministicFix) reliableSuggestion = false;
    }
    if (!firstViolation) {
      firstViolation = {
        normalizedValue: normalized.value,
        suggestedValue: suggestedPart,
        fixEmittable: false,
        deterministicFix: false,
        reason: "off-scale",
        assumedRootFontSizePx: normalized.assumedRootFontSizePx,
        assumedEmBasePx: normalized.assumedEmBasePx
      };
    }
  }
  if (!firstViolation || !anyViolation) return null;
  const joined = suggestedParts.join(" ");
  const changes = joined !== raw.trim().replace(/\s+/g, " ");
  const fixEmittable = canFixAll && reliableSuggestion && changes;
  const deterministic = fixEmittable && !anyOffScale && allApplicableValuePreserving;
  return {
    ...firstViolation,
    reason: anyOffScale ? "off-scale" : "token-equivalent",
    fixEmittable,
    deterministicFix: deterministic,
    // Always report the best-effort suggestion for context, even when no fix is
    // attached (e.g. an assumed em/rem base makes the px snap unreliable).
    suggestedValue: canFixAll ? joined : firstViolation.suggestedValue
  };
}
function buildSpacingFix(property, checked) {
  if (!checked.fixEmittable || checked.suggestedValue === void 0) return void 0;
  return {
    kind: "replaceStyleValue",
    title: `Replace ${property} with ${checked.suggestedValue}`,
    property,
    value: checked.suggestedValue,
    deterministic: checked.deterministicFix
  };
}
function parseLengthParts(raw) {
  const trimmed = raw.trim();
  if (!trimmed || /var\(|calc\(|min\(|max\(|clamp\(|,/i.test(trimmed)) return null;
  const parts = trimmed.split(/\s+/);
  if (parts.length < 1 || parts.length > 4) return null;
  const parsed = parts.map((part) => {
    const length = parseLengthValue(part);
    return length ? { raw: part, parsed: length } : null;
  });
  if (parsed.some((part) => part === null)) return null;
  return parsed;
}
function spacingMessage(property, value, allowed, unit, checked) {
  if (checked.reason === "token-equivalent" && checked.matchedToken) {
    return `\`${property}: ${value}\` is a raw literal that matches token \`${checked.matchedToken}\`. Reference the token instead.`;
  }
  const sample = allowed.slice().sort((a, b) => a - b).slice(0, 6);
  const suffix = `${sample.join(unit + ", ")}${unit}`;
  const ellipsis = allowed.length > sample.length ? ", \u2026" : "";
  return `\`${property}: ${value}\` is not on the spacing scale. Allowed: ${suffix}${ellipsis}.`;
}

// src/rules/styles-no-raw-typography.ts
var RULE_ID11 = "styles/no-raw-typography";
var RULE_VERSION11 = "1";
var FONT_SIZE_PROPERTIES = /* @__PURE__ */ new Set(["font-size", "fontsize"]);
function ruleStylesNoRawTypography(ix) {
  const policy = ix.policy.fontSizeScale();
  if (!policy) return [];
  const scale = ix.policy.scale(policy.scale);
  if (!scale) return [];
  const allowed = ix.policy.scaleValues(policy.scale).map((v) => v.value);
  if (allowed.length === 0) return [];
  const tokens = buildTypographyTokenLookup(ix, scale);
  const findings = [];
  for (const decl of ix.byKind("style_declaration")) {
    if (decl.property.toLowerCase() !== "font-size") continue;
    const checked = checkFontSize(decl.value, allowed, scale, tokens);
    if (!checked) continue;
    findings.push(
      makeFinding({
        ruleId: RULE_ID11,
        ruleVersion: RULE_VERSION11,
        severity: policy.severity,
        message: typographyMessage(decl.property, decl.value, allowed, scale.unit, checked),
        location: decl.location,
        evidence: ix.evidence([decl.id, policy.id, scale.id]),
        fingerprintIdentity: {
          source: "style_declaration",
          file: decl.file,
          selector: decl.selector,
          declarationPath: decl.declarationPath,
          property: decl.property,
          value: decl.value
        },
        fix: buildFix(decl.property, checked),
        attributes: {
          property: decl.property,
          rawValue: decl.value,
          scale: scale.name,
          allowed,
          suggestedValue: checked.suggestedValue,
          matchedToken: checked.matchedToken,
          source: "css"
        }
      })
    );
  }
  const componentByNode = indexComponentByNodeId(ix);
  for (const inline of ix.byKind("usage_inline_style")) {
    if (inline.valueKind === "css-variable") continue;
    if (!FONT_SIZE_PROPERTIES.has(inline.property.toLowerCase())) continue;
    const checked = checkFontSize(inline.value, allowed, scale, tokens, inline.valueKind === "number");
    if (!checked) continue;
    const node = readUsageNode(ix, inline.nodeId);
    if (!node) continue;
    const componentEvidenceId = componentByNode.get(node.id)?.id;
    const evidenceIds = componentEvidenceId ? [node.id, componentEvidenceId, inline.id, policy.id, scale.id] : [node.id, inline.id, policy.id, scale.id];
    findings.push(
      makeFinding({
        ruleId: RULE_ID11,
        ruleVersion: RULE_VERSION11,
        severity: policy.severity,
        message: typographyMessage(inline.property, inline.value, allowed, scale.unit, checked),
        location: node.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "usage_inline_style",
          file: node.file,
          nodePath: node.nodePath,
          element: node.element,
          property: inline.property,
          value: inline.value
        },
        fix: buildFix(inline.property, checked),
        attributes: {
          property: inline.property,
          rawValue: inline.value,
          scale: scale.name,
          allowed,
          suggestedValue: checked.suggestedValue,
          matchedToken: checked.matchedToken,
          source: "jsx"
        }
      })
    );
  }
  return findings;
}
function buildTypographyTokenLookup(ix, scale) {
  const out = /* @__PURE__ */ new Map();
  for (const token of ix.tokens.byCategory("typography")) {
    const parsed = parseLengthValue(token.value);
    if (!parsed || parsed.unit === null) continue;
    const normalized = normalizeLengthForScale(parsed, scale);
    if (normalized === null) continue;
    const key = Math.abs(normalized.value);
    if (!out.has(key)) out.set(key, token);
  }
  return out;
}
function checkFontSize(raw, allowed, scale, tokens, bareNumberFix = false) {
  const trimmed = raw.trim();
  if (!trimmed || /var\(|calc\(|clamp\(|min\(|max\(|,|\s/.test(trimmed)) return null;
  const parsed = parseLengthValue(trimmed);
  if (!parsed) return null;
  const normalized = normalizeLengthForScale(parsed, scale);
  if (normalized === null) return null;
  const magnitude = Math.abs(normalized.value);
  const exactToken = tokens.get(magnitude);
  if (matchesScale(normalized.value, allowed)) {
    if (!exactToken) return null;
    const applicable = isApplicableTokenReference(exactToken.referenceFormat) && normalized.deterministicFix;
    return {
      suggestedValue: tokenReference(exactToken.name),
      fixEmittable: true,
      deterministicFix: applicable,
      reason: "token-equivalent",
      matchedToken: exactToken.name
    };
  }
  const nearest = nearestSignedScaleValue(normalized.value, allowed);
  if (nearest === null || nearest === 0 && normalized.value !== 0) {
    return { fixEmittable: false, deterministicFix: false, reason: "off-scale" };
  }
  const nearestToken = tokens.get(Math.abs(nearest));
  const suggestedValue = nearestToken ? tokenReference(nearestToken.name) : bareNumberFix ? `${nearest}` : `${nearest}${scale.unit}`;
  return {
    suggestedValue,
    fixEmittable: normalized.deterministicFix,
    deterministicFix: false,
    reason: "off-scale"
  };
}
function buildFix(property, checked) {
  if (!checked.fixEmittable || checked.suggestedValue === void 0) return void 0;
  return {
    kind: "replaceStyleValue",
    title: `Replace ${property} with ${checked.suggestedValue}`,
    property,
    value: checked.suggestedValue,
    deterministic: checked.deterministicFix
  };
}
function typographyMessage(property, value, allowed, unit, checked) {
  if (checked.reason === "token-equivalent" && checked.matchedToken) {
    return `\`${property}: ${value}\` is a raw literal that matches token \`${checked.matchedToken}\`. Reference the token instead.`;
  }
  const sample = allowed.slice().sort((a, b) => a - b).slice(0, 6);
  const suffix = `${sample.join(unit + ", ")}${unit}`;
  const ellipsis = allowed.length > sample.length ? ", \u2026" : "";
  return `\`${property}: ${value}\` is not on the typography scale. Allowed: ${suffix}${ellipsis}.`;
}

// src/rules/theme-no-theme-coupled-literal.ts
var RULE_ID12 = "theme/no-theme-coupled-literal";
var RULE_VERSION12 = "1";
var COLOR_LITERAL_GLOBAL = /#[0-9a-fA-F]{3,8}\b|\b(?:rgba?|hsla?|oklab|oklch|lab|lch)\(\s*[\d.%\s,\/]+\s*\)/g;
var WHITE_RGBA = /^rgba?\(\s*255\s*,\s*255\s*,\s*255/i;
var BLACK_RGBA = /^rgba?\(\s*0\s*,\s*0\s*,\s*0/i;
var BOX_SHADOW_PROP = /^(?:-webkit-|-moz-)?box-shadow$/i;
var NEUTRAL_SHADOW = /^(?:none|inherit|initial|unset|revert|revert-layer)\s*$/i;
function ruleThemeNoThemeCoupledLiteral(ix) {
  const policy = ix.policy.ruleConfig(RULE_ID12);
  if (!policy?.enabled) return [];
  const findings = [];
  for (const decl of ix.byKind("style_declaration")) {
    if (isTokenFile(decl.file)) continue;
    const hit = findThemeCoupledLiteral(decl.property, decl.value);
    if (!hit) continue;
    findings.push(
      makeFinding({
        ruleId: RULE_ID12,
        ruleVersion: RULE_VERSION12,
        severity: policy.severity ?? "warn",
        message: hit.message,
        location: decl.location,
        evidence: ix.evidence([decl.id, policy.id]),
        fingerprintIdentity: {
          file: decl.file,
          selector: decl.selector,
          declarationPath: decl.declarationPath,
          property: decl.property,
          rawValue: hit.rawValue,
          reason: hit.reason
        },
        attributes: {
          property: decl.property,
          rawValue: decl.value,
          color: hit.rawValue,
          suggestion: hit.suggestion,
          source: "css"
        }
      })
    );
  }
  return findings;
}
function findThemeCoupledLiteral(property, value) {
  COLOR_LITERAL_GLOBAL.lastIndex = 0;
  let match;
  while ((match = COLOR_LITERAL_GLOBAL.exec(value)) !== null) {
    const raw = match[0];
    if (insideLightDark(value, match.index)) continue;
    if (insideColorMix(value, match.index)) {
      return {
        rawValue: raw,
        reason: "color-mix",
        message: `color-mix() argument ${raw} is theme-coupled. Use a semantic token or wrap the declaration in light-dark(...).`,
        suggestion: `Replace ${raw} with a semantic token or light-dark(...)`
      };
    }
    if (BOX_SHADOW_PROP.test(property) && !NEUTRAL_SHADOW.test(value.trim())) {
      return {
        rawValue: raw,
        reason: "box-shadow",
        message: `Custom box-shadow with literal color ${raw} is theme-coupled. Use a shadow/elevation token or light-dark(...).`,
        suggestion: "Use a shadow/elevation token or light-dark(...)"
      };
    }
    if (WHITE_RGBA.test(raw) || BLACK_RGBA.test(raw)) {
      return {
        rawValue: raw,
        reason: "white-black-rgba",
        message: `Raw ${raw} assumes one theme. Wrap it in light-dark(...) or use a semantic token.`,
        suggestion: WHITE_RGBA.test(raw) ? `light-dark(/* TODO light-theme equivalent */, ${raw})` : `light-dark(${raw}, /* TODO dark-theme equivalent */)`
      };
    }
  }
  return null;
}
function insideLightDark(value, index) {
  return enclosingFunctionName(value, index) === "light-dark";
}
function insideColorMix(value, index) {
  return enclosingFunctionName(value, index) === "color-mix";
}
function enclosingFunctionName(value, index) {
  let depth = 0;
  for (let i = index - 1; i >= 0; i--) {
    const ch = value[i];
    if (ch === ")") {
      depth++;
      continue;
    }
    if (ch !== "(") continue;
    if (depth > 0) {
      depth--;
      continue;
    }
    const before = value.slice(0, i).match(/([a-z-]+)\s*$/i);
    return before?.[1]?.toLowerCase() ?? null;
  }
  return null;
}
function isTokenFile(filePath) {
  const normalized = filePath.replace(/\\/g, "/");
  const base = normalized.slice(normalized.lastIndexOf("/") + 1);
  if (base === "_tokens.scss" || base === "tokens.scss") return true;
  return base.startsWith("_") && /(^|\/)tokens\//.test(normalized);
}

// src/rules/tailwind-arbitrary-color.ts
var RULE_ID13 = "tailwind/arbitrary-color";
var RULE_VERSION13 = "1";
var COLOR_UTILITIES = /* @__PURE__ */ new Set([
  "accent",
  "bg",
  "border",
  "border-b",
  "border-e",
  "border-l",
  "border-r",
  "border-s",
  "border-t",
  "border-x",
  "border-y",
  "caret",
  "decoration",
  "divide",
  "fill",
  "from",
  "outline",
  "placeholder",
  "ring",
  "ring-offset",
  "shadow",
  "stroke",
  "text",
  "to",
  "via"
]);
function ruleTailwindArbitraryColor(ix) {
  const policy = ix.policy.rawColorPolicy();
  if (!policy) return [];
  const findings = [];
  for (const klass of ix.byKind("tailwind_class")) {
    if (klass.value.kind !== "arbitrary") continue;
    if (!COLOR_UTILITIES.has(klass.utility)) continue;
    const color = detectRawColor(klass.value.raw);
    if (!color) continue;
    if (isExemptColor(color, policy.except)) continue;
    const node = readUsageNode(ix, klass.nodeId);
    const evidenceIds = node ? [node.id, klass.id, policy.id] : [klass.id, policy.id];
    findings.push(
      makeFinding({
        ruleId: RULE_ID13,
        ruleVersion: RULE_VERSION13,
        severity: policy.severity,
        message: `\`${klass.raw}\` uses arbitrary raw color ${color}. Use a ${policy.prefer === "token" ? "design token" : "CSS variable"} instead.`,
        location: klass.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: fingerprintIdentity(klass, color),
        attributes: {
          rawClass: klass.raw,
          rawValue: klass.value.raw,
          color,
          utility: klass.utility,
          source: "tailwind"
        }
      })
    );
  }
  return findings;
}
function fingerprintIdentity(klass, color) {
  return {
    source: "tailwind_class",
    file: klass.file,
    nodeId: klass.nodeId,
    originPath: klass.originPath,
    raw: klass.raw,
    utility: klass.utility,
    value: color
  };
}

// src/rules/tailwind-arbitrary-spacing.ts
var RULE_ID14 = "tailwind/arbitrary-spacing";
var RULE_VERSION14 = "1";
var SPACING_UTILITY_TO_PROPERTY = /* @__PURE__ */ new Map([
  ["m", "margin"],
  ["mt", "margin"],
  ["mr", "margin"],
  ["mb", "margin"],
  ["ml", "margin"],
  ["ms", "margin"],
  ["me", "margin"],
  ["mx", "margin"],
  ["my", "margin"],
  ["p", "padding"],
  ["pt", "padding"],
  ["pr", "padding"],
  ["pb", "padding"],
  ["pl", "padding"],
  ["ps", "padding"],
  ["pe", "padding"],
  ["px", "padding"],
  ["py", "padding"],
  ["gap", "gap"],
  ["gap-x", "gap"],
  ["gap-y", "gap"],
  ["space-x", "gap"],
  ["space-y", "gap"]
]);
function ruleTailwindArbitrarySpacing(ix) {
  const findings = [];
  for (const klass of ix.byKind("tailwind_class")) {
    if (klass.value.kind !== "arbitrary") continue;
    const property = SPACING_UTILITY_TO_PROPERTY.get(klass.utility);
    if (!property) continue;
    const policy = ix.policy.propertyScale(property);
    if (!policy) continue;
    const scale = ix.policy.scale(policy.scale);
    if (!scale) continue;
    const allowed = ix.policy.scaleValues(policy.scale).map((v) => v.value);
    if (allowed.length === 0) continue;
    const parsed = parseLengthValue(klass.value.raw);
    const normalized = parsed ? normalizeLengthForScale(parsed, scale) : null;
    if (normalized && matchesScale(normalized.value, allowed)) continue;
    const node = readUsageNode(ix, klass.nodeId);
    const evidenceIds = node ? [node.id, klass.id, policy.id, scale.id] : [klass.id, policy.id, scale.id];
    const fix = buildFix2(klass);
    findings.push(
      makeFinding({
        ruleId: RULE_ID14,
        ruleVersion: RULE_VERSION14,
        severity: policy.severity,
        message: `\`${klass.raw}\` is an arbitrary spacing value not on the project's spacing scale.`,
        location: klass.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "tailwind_class",
          file: klass.file,
          nodeId: klass.nodeId,
          originPath: klass.originPath,
          raw: klass.raw,
          utility: klass.utility,
          value: klass.value.raw
        },
        fix,
        attributes: {
          rawClass: klass.raw,
          rawValue: klass.value.raw,
          utility: klass.utility,
          property,
          scale: scale.name,
          allowed,
          normalizedValue: normalized?.value,
          normalizedUnit: normalized ? scale.unit : void 0,
          assumedRootFontSizePx: normalized?.assumedRootFontSizePx,
          assumedEmBasePx: normalized?.assumedEmBasePx,
          source: "tailwind"
        }
      })
    );
  }
  return findings;
}
function buildFix2(klass) {
  return {
    kind: "replaceClassToken",
    title: `Review ${klass.raw}`,
    from: klass.raw,
    to: null,
    utility: klass.utility,
    deterministic: false
  };
}

// src/rules/tailwind-rule-utils.ts
var TAILWIND_COLOR_UTILITIES = /* @__PURE__ */ new Set([
  "accent",
  "bg",
  "border",
  "border-b",
  "border-e",
  "border-l",
  "border-r",
  "border-s",
  "border-t",
  "border-x",
  "border-y",
  "caret",
  "decoration",
  "divide",
  "fill",
  "from",
  "outline",
  "placeholder",
  "ring",
  "ring-offset",
  "shadow",
  "stroke",
  "text",
  "to",
  "via"
]);
var UNIVERSAL_TAILWIND_COLOR_TOKENS = /* @__PURE__ */ new Set([
  "white",
  "black",
  "transparent",
  "current",
  "inherit",
  "none"
]);
var TAILWIND_SPACING_UTILITY_TO_PROPERTY = /* @__PURE__ */ new Map([
  ["m", "margin"],
  ["mt", "margin"],
  ["mr", "margin"],
  ["mb", "margin"],
  ["ml", "margin"],
  ["ms", "margin"],
  ["me", "margin"],
  ["mx", "margin"],
  ["my", "margin"],
  ["p", "padding"],
  ["pt", "padding"],
  ["pr", "padding"],
  ["pb", "padding"],
  ["pl", "padding"],
  ["ps", "padding"],
  ["pe", "padding"],
  ["px", "padding"],
  ["py", "padding"],
  ["gap", "gap"],
  ["gap-x", "gap"],
  ["gap-y", "gap"],
  ["space-x", "gap"],
  ["space-y", "gap"]
]);
function indexResolvedTailwindTokens(ix) {
  const out = /* @__PURE__ */ new Map();
  for (const fact of ix.byKind("tailwind_token_resolved")) {
    out.set(resolvedKey(fact.utility, fact.token), fact);
  }
  return out;
}
function resolvedForClass(resolvedByKey, klass) {
  if (klass.value.kind !== "token") return void 0;
  return resolvedByKey.get(resolvedKey(klass.utility, klass.value.token));
}
function resolvedKey(utility, token) {
  return `${utility}\0${token}`;
}
function matchPattern(value, patterns) {
  for (const pattern of patterns) {
    if (matchesTokenGlob(value, pattern)) return pattern;
  }
  return null;
}
function matchesTokenGlob(value, pattern) {
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
  return new RegExp(`^${escaped}$`).test(value);
}

// src/rules/tailwind-forbidden-palette.ts
var RULE_ID15 = "tailwind/forbidden-palette";
var RULE_VERSION15 = "1";
function ruleTailwindForbiddenPalette(ix) {
  const deny = ix.policy.tailwindPaletteDeny();
  const allow = ix.policy.tailwindPaletteAllow();
  if (!deny && !allow) return [];
  const resolvedByKey = indexResolvedTailwindTokens(ix);
  const findings = [];
  for (const klass of ix.byKind("tailwind_class")) {
    if (klass.value.kind !== "token") continue;
    const token = klass.value.token;
    if (!TAILWIND_COLOR_UTILITIES.has(klass.utility)) continue;
    if (UNIVERSAL_TAILWIND_COLOR_TOKENS.has(token)) continue;
    const resolved = resolvedForClass(resolvedByKey, klass);
    if (!resolved || resolved.resolved.kind !== "color") continue;
    if (resolved.resolved.source === "theme-css") continue;
    const deniedPattern = deny ? matchPattern(token, deny.patterns) : null;
    const allowedPattern = allow ? matchPattern(token, allow.patterns) : null;
    const allowViolation = allow && !allowedPattern;
    if (!deniedPattern && !allowViolation) continue;
    const policy = deniedPattern ? deny : allow;
    const matchedPattern = deniedPattern ?? allowedPattern ?? null;
    const node = readUsageNode(ix, klass.nodeId);
    const evidenceIds = node ? [node.id, klass.id, resolved.id, policy.id] : [klass.id, resolved.id, policy.id];
    findings.push(
      makeFinding({
        ruleId: RULE_ID15,
        ruleVersion: RULE_VERSION15,
        severity: policy.severity,
        message: messageFor(klass, token, deniedPattern, allow?.patterns ?? []),
        location: klass.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "tailwind_class",
          file: klass.file,
          nodeId: klass.nodeId,
          originPath: klass.originPath,
          raw: klass.raw,
          utility: klass.utility,
          token,
          matchedPattern
        },
        fix: buildFix3(klass),
        attributes: {
          rawClass: klass.raw,
          utility: klass.utility,
          token,
          matchedPattern,
          allowedPatterns: allow?.patterns,
          source: resolved.resolved.source
        }
      })
    );
  }
  return findings;
}
function messageFor(klass, token, deniedPattern, allowedPatterns) {
  if (deniedPattern) {
    return `\`${klass.raw}\` uses the \`${token}\` palette token, which is forbidden by policy \`deny: ${deniedPattern}\`.`;
  }
  return `\`${klass.raw}\` uses the \`${token}\` palette token, which is outside the allowed Tailwind palette patterns [${allowedPatterns.join(", ")}].`;
}
function buildFix3(klass) {
  return {
    kind: "replaceClassToken",
    title: `Review ${klass.raw}`,
    from: klass.raw,
    to: null,
    utility: klass.utility,
    deterministic: false
  };
}

// src/rules/tailwind-off-scale-spacing-token.ts
var RULE_ID16 = "tailwind/off-scale-spacing-token";
var RULE_VERSION16 = "1";
function ruleTailwindOffScaleSpacingToken(ix) {
  const resolvedByKey = indexResolvedTailwindTokens(ix);
  const findings = [];
  for (const klass of ix.byKind("tailwind_class")) {
    if (klass.value.kind !== "token") continue;
    const property = TAILWIND_SPACING_UTILITY_TO_PROPERTY.get(klass.utility);
    if (!property) continue;
    const policy = ix.policy.propertyScale(property);
    if (!policy) continue;
    const scale = ix.policy.scale(policy.scale);
    if (!scale) continue;
    const allowed = ix.policy.scaleValues(policy.scale).map((v) => v.value);
    if (allowed.length === 0) continue;
    const resolved = resolvedForClass(resolvedByKey, klass);
    if (!resolved || resolved.resolved.kind !== "length") continue;
    const parsed = parseLengthValue(resolved.resolved.value);
    if (!parsed) continue;
    const normalized = normalizeLengthForScale(parsed, scale);
    if (!normalized) continue;
    const signedValue = klass.negative ? -normalized.value : normalized.value;
    if (matchesScale(signedValue, allowed)) continue;
    const node = readUsageNode(ix, klass.nodeId);
    const evidenceIds = node ? [node.id, klass.id, resolved.id, policy.id, scale.id] : [klass.id, resolved.id, policy.id, scale.id];
    findings.push(
      makeFinding({
        ruleId: RULE_ID16,
        ruleVersion: RULE_VERSION16,
        severity: policy.severity,
        message: `\`${klass.raw}\` resolves to ${formatResolvedValue(signedValue, scale.unit)}, which is not on the project's spacing scale [${allowed.join(", ")}].`,
        location: klass.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "tailwind_class",
          file: klass.file,
          nodeId: klass.nodeId,
          originPath: klass.originPath,
          raw: klass.raw,
          utility: klass.utility,
          token: klass.value.token,
          resolvedValue: signedValue,
          scale: scale.name
        },
        fix: buildFix4(klass.raw, klass.utility),
        attributes: {
          rawClass: klass.raw,
          utility: klass.utility,
          token: klass.value.token,
          property,
          scale: scale.name,
          allowed,
          resolvedValue: resolved.resolved.value,
          resolvedPx: scale.unit === "px" ? signedValue : void 0,
          resolvedUnit: scale.unit,
          source: resolved.resolved.source
        }
      })
    );
  }
  return findings;
}
function formatResolvedValue(value, unit) {
  return unit === "px" ? `${value}px` : `${value}${unit}`;
}
function buildFix4(raw, utility) {
  return {
    kind: "replaceClassToken",
    title: `Review ${raw}`,
    from: raw,
    to: null,
    utility,
    deterministic: false
  };
}

// src/rules/tailwind-raw-color-via-token.ts
var RULE_ID17 = "tailwind/raw-color-via-token";
var RULE_VERSION17 = "1";
function ruleTailwindRawColorViaToken(ix) {
  const allow = ix.policy.tailwindPaletteAllow();
  if (!allow || allow.patterns.length === 0) return [];
  const resolvedByKey = indexResolvedTailwindTokens(ix);
  const findings = [];
  for (const klass of ix.byKind("tailwind_class")) {
    if (klass.value.kind !== "token") continue;
    if (!TAILWIND_COLOR_UTILITIES.has(klass.utility)) continue;
    if (UNIVERSAL_TAILWIND_COLOR_TOKENS.has(klass.value.token)) continue;
    if (matchPattern(klass.value.token, allow.patterns)) continue;
    const resolved = resolvedForClass(resolvedByKey, klass);
    if (!resolved || resolved.resolved.kind !== "color") continue;
    if (resolved.resolved.source === "theme-css") continue;
    const color = detectRawColor(resolved.resolved.value);
    if (!color) continue;
    const node = readUsageNode(ix, klass.nodeId);
    const evidenceIds = node ? [node.id, klass.id, resolved.id, allow.id] : [klass.id, resolved.id, allow.id];
    findings.push(
      makeFinding({
        ruleId: RULE_ID17,
        ruleVersion: RULE_VERSION17,
        severity: allow.severity,
        message: `\`${klass.raw}\` resolves to raw color \`${color}\` (${sourceLabel2(resolved.resolved.source)}). Project policy requires \`${allow.patterns.join("`, `")}\` palette tokens.`,
        location: klass.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "tailwind_class",
          file: klass.file,
          nodeId: klass.nodeId,
          originPath: klass.originPath,
          raw: klass.raw,
          utility: klass.utility,
          token: klass.value.token,
          resolvedValue: color
        },
        attributes: {
          rawClass: klass.raw,
          utility: klass.utility,
          token: klass.value.token,
          resolvedValue: color,
          source: resolved.resolved.source,
          allowedPatterns: allow.patterns
        }
      })
    );
  }
  return findings;
}
function sourceLabel2(source) {
  return source === "default" ? "Tailwind default" : source;
}

// src/rules/tailwind-unknown-class.ts
var RULE_ID18 = "tailwind/unknown-class";
var RULE_VERSION18 = "1";
var UNIVERSAL_COLOR_KEYWORDS = /* @__PURE__ */ new Set([
  "white",
  "black",
  "transparent",
  "current",
  "currentcolor",
  "inherit",
  "none"
]);
function ruleTailwindUnknownClass(ix) {
  const policy = ix.policy.tailwindUnknownClassEnabled();
  if (!policy) return [];
  const resolvedByKey = indexResolvedTailwindTokens(ix);
  const findings = [];
  for (const klass of ix.byKind("tailwind_class")) {
    if (klass.utility === "unknown") {
      const node2 = readUsageNode(ix, klass.nodeId);
      const evidenceIds2 = node2 ? [node2.id, klass.id, policy.id] : [klass.id, policy.id];
      findings.push(
        makeFinding({
          ruleId: RULE_ID18,
          ruleVersion: RULE_VERSION18,
          severity: policy.severity,
          message: `\`${klass.raw}\` could not be parsed as a known Tailwind utility.`,
          location: klass.location,
          evidence: ix.evidence(evidenceIds2),
          fingerprintIdentity: {
            source: "tailwind_class",
            file: klass.file,
            nodeId: klass.nodeId,
            originPath: klass.originPath,
            raw: klass.raw,
            reason: "parse-failure"
          },
          attributes: {
            rawClass: klass.raw,
            utility: klass.utility,
            reason: "parse-failure"
          }
        })
      );
      continue;
    }
    if (klass.value.kind !== "token") continue;
    if (!TAILWIND_COLOR_UTILITIES.has(klass.utility)) continue;
    if (klass.utility === "shadow") continue;
    const token = klass.value.token;
    if (/^\d/.test(token)) continue;
    if (UNIVERSAL_COLOR_KEYWORDS.has(token.toLowerCase())) continue;
    const resolved = resolvedForClass(resolvedByKey, klass);
    if (!resolved || resolved.resolved.kind !== "unknown") continue;
    const node = readUsageNode(ix, klass.nodeId);
    const evidenceIds = node ? [node.id, klass.id, resolved.id, policy.id] : [klass.id, resolved.id, policy.id];
    findings.push(
      makeFinding({
        ruleId: RULE_ID18,
        ruleVersion: RULE_VERSION18,
        severity: policy.severity,
        message: `\`${klass.raw}\` did not resolve to any known palette token.`,
        location: klass.location,
        evidence: ix.evidence(evidenceIds),
        fingerprintIdentity: {
          source: "tailwind_class",
          file: klass.file,
          nodeId: klass.nodeId,
          originPath: klass.originPath,
          raw: klass.raw,
          utility: klass.utility,
          token: klass.value.token,
          reason: "resolution-miss"
        },
        attributes: {
          rawClass: klass.raw,
          utility: klass.utility,
          token: klass.value.token,
          reason: "resolution-miss"
        }
      })
    );
  }
  return findings;
}

// src/rules/tokens-require-dual-fallback.ts
var RULE_ID19 = "tokens/require-dual-fallback";
var RULE_VERSION19 = "1";
var CSS_VAR_WITH_OPTIONAL_FALLBACK = /var\(\s*(--[A-Za-z0-9_-]+)(\s*,\s*[^)]*)?\)/g;
var SASS_FILE = /\.(scss|sass)$/i;
function ruleTokensRequireDualFallback(ix) {
  const policy = ix.policy.ruleConfig(RULE_ID19);
  if (!policy?.enabled) return [];
  const knownTokenVars = cssVariableNames(ix.tokens.list());
  const findings = [];
  for (const decl of ix.byKind("style_declaration")) {
    if (!SASS_FILE.test(decl.file)) continue;
    CSS_VAR_WITH_OPTIONAL_FALLBACK.lastIndex = 0;
    let match;
    while ((match = CSS_VAR_WITH_OPTIONAL_FALLBACK.exec(decl.value)) !== null) {
      const tokenName = match[1];
      const hasFallback = match[2] !== void 0;
      if (hasFallback) continue;
      if (!knownTokenVars.has(tokenName) && !tokenName.startsWith("--fui-")) continue;
      const rawValue = match[0];
      const scssVar = tokenName.replace(/^--/, "$");
      const replacement = `var(${tokenName}, ${scssVar})`;
      const nextValue = decl.value.replace(rawValue, replacement);
      findings.push(
        makeFinding({
          ruleId: RULE_ID19,
          ruleVersion: RULE_VERSION19,
          severity: policy.severity ?? "warn",
          message: `var(${tokenName}) is missing an SCSS fallback. Use var(${tokenName}, ${scssVar}).`,
          location: decl.location,
          evidence: ix.evidence([decl.id, policy.id]),
          fingerprintIdentity: {
            file: decl.file,
            selector: decl.selector,
            declarationPath: decl.declarationPath,
            property: decl.property,
            tokenName
          },
          fix: {
            kind: "replaceStyleValue",
            title: `Add fallback for ${tokenName}`,
            property: decl.property,
            value: nextValue,
            deterministic: true
          },
          attributes: {
            property: decl.property,
            rawValue: decl.value,
            tokenName,
            suggestedValue: nextValue,
            source: "css"
          }
        })
      );
    }
  }
  return findings;
}
function cssVariableNames(tokens) {
  const names = /* @__PURE__ */ new Set();
  for (const token of tokens) {
    names.add(token.name.startsWith("--") ? token.name : `--${token.name}`);
  }
  return names;
}

// src/rules/index.ts
var RULES = [
  {
    id: "components/unknown-prop",
    version: "1",
    run: ruleComponentsUnknownProp
  },
  {
    id: "components/forbidden-prop-value",
    version: "1",
    run: ruleComponentsForbiddenPropValue
  },
  {
    id: "props/invalid-value",
    version: "1",
    run: rulePropsInvalidValue
  },
  {
    id: "imports/preferred-path",
    version: "1",
    run: ruleJsxPreferredImportPath
  },
  {
    id: "components/preferred-component",
    version: "1",
    run: ruleJsxPreferredComponent
  },
  {
    id: "components/prefer-library",
    version: "1",
    run: ruleComponentsPreferLibrary
  },
  {
    id: "styles/no-raw-color",
    version: "1",
    run: ruleStylesNoRawColor
  },
  {
    id: "styles/no-raw-dimensions",
    version: "1",
    run: ruleStylesNoRawDimensions
  },
  {
    id: "styles/no-raw-spacing",
    version: "1",
    run: ruleStylesNoRawSpacing
  },
  {
    id: "styles/no-raw-typography",
    version: "1",
    run: ruleStylesNoRawTypography
  },
  {
    id: "tailwind/arbitrary-color",
    version: "1",
    run: ruleTailwindArbitraryColor
  },
  {
    id: "tailwind/arbitrary-spacing",
    version: "1",
    run: ruleTailwindArbitrarySpacing
  },
  {
    id: "tailwind/forbidden-palette",
    version: "1",
    run: ruleTailwindForbiddenPalette
  },
  {
    id: "tailwind/off-scale-spacing-token",
    version: "1",
    run: ruleTailwindOffScaleSpacingToken
  },
  {
    id: "tailwind/raw-color-via-token",
    version: "1",
    run: ruleTailwindRawColorViaToken
  },
  {
    id: "tailwind/unknown-class",
    version: "1",
    run: ruleTailwindUnknownClass
  },
  {
    id: "tokens/require-dual-fallback",
    version: "1",
    run: ruleTokensRequireDualFallback
  },
  {
    id: "theme/no-theme-coupled-literal",
    version: "1",
    run: ruleThemeNoThemeCoupledLiteral
  },
  {
    id: "a11y/required-accessible-name",
    version: "1",
    run: ruleA11yRequiredAccessibleName
  }
];
function runRules(ix) {
  const findings = [];
  for (const rule of RULES) findings.push(...rule.run(ix));
  return sortFindings(findings);
}
function sortFindings(findings) {
  return findings.slice().sort((a, b) => {
    const fileCmp = a.location.file.localeCompare(b.location.file);
    if (fileCmp !== 0) return fileCmp;
    if (a.location.line !== b.location.line) return a.location.line - b.location.line;
    if (a.location.column !== b.location.column) return a.location.column - b.location.column;
    return a.fingerprint.localeCompare(b.fingerprint);
  });
}

// src/contract/hash.ts
function normalizeNumber(value) {
  if (Number.isNaN(value)) return '"@num:nan"';
  if (value === Infinity) return '"@num:+inf"';
  if (value === -Infinity) return '"@num:-inf"';
  if (Object.is(value, -0)) return "0";
  return JSON.stringify(value);
}
function canonicalPreimage(value) {
  if (value === void 0 || value === null) return "null";
  const type = typeof value;
  if (type === "number") return normalizeNumber(value);
  if (type === "string" || type === "boolean") return JSON.stringify(value);
  if (type === "bigint") return JSON.stringify(value.toString());
  if (Array.isArray(value)) {
    return `[${value.map(canonicalPreimage).join(",")}]`;
  }
  if (type !== "object") {
    return "null";
  }
  const entries = Object.entries(value).filter(([, v]) => v !== void 0).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalPreimage(v)}`).join(",")}}`;
}
var K = new Uint32Array([
  1116352408,
  1899447441,
  3049323471,
  3921009573,
  961987163,
  1508970993,
  2453635748,
  2870763221,
  3624381080,
  310598401,
  607225278,
  1426881987,
  1925078388,
  2162078206,
  2614888103,
  3248222580,
  3835390401,
  4022224774,
  264347078,
  604807628,
  770255983,
  1249150122,
  1555081692,
  1996064986,
  2554220882,
  2821834349,
  2952996808,
  3210313671,
  3336571891,
  3584528711,
  113926993,
  338241895,
  666307205,
  773529912,
  1294757372,
  1396182291,
  1695183700,
  1986661051,
  2177026350,
  2456956037,
  2730485921,
  2820302411,
  3259730800,
  3345764771,
  3516065817,
  3600352804,
  4094571909,
  275423344,
  430227734,
  506948616,
  659060556,
  883997877,
  958139571,
  1322822218,
  1537002063,
  1747873779,
  1955562222,
  2024104815,
  2227730452,
  2361852424,
  2428436474,
  2756734187,
  3204031479,
  3329325298
]);
function rotr(value, bits) {
  return (value >>> bits | value << 32 - bits) >>> 0;
}
function sha256Hex(message) {
  const bytes = new TextEncoder().encode(message);
  const length = bytes.length;
  const bitLength = length * 8;
  const withMarker = length + 1;
  const zeroPad = (56 - withMarker % 64 + 64) % 64;
  const total = withMarker + zeroPad + 8;
  const buffer = new Uint8Array(total);
  buffer.set(bytes, 0);
  buffer[length] = 128;
  const view = new DataView(buffer.buffer);
  view.setUint32(total - 8, Math.floor(bitLength / 4294967296));
  view.setUint32(total - 4, bitLength >>> 0);
  let h0 = 1779033703;
  let h1 = 3144134277;
  let h2 = 1013904242;
  let h3 = 2773480762;
  let h4 = 1359893119;
  let h5 = 2600822924;
  let h6 = 528734635;
  let h7 = 1541459225;
  const w = new Uint32Array(64);
  for (let chunk = 0; chunk < total; chunk += 64) {
    for (let t = 0; t < 16; t++) w[t] = view.getUint32(chunk + t * 4);
    for (let t = 16; t < 64; t++) {
      const w15 = w[t - 15];
      const w2 = w[t - 2];
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ w15 >>> 3;
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ w2 >>> 10;
      w[t] = w[t - 16] + s0 + w[t - 7] + s1 >>> 0;
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g2 = h6;
    let h = h7;
    for (let t = 0; t < 64; t++) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = e & f ^ ~e & g2;
      const temp1 = h + s1 + ch + K[t] + w[t] >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = a & b ^ a & c ^ b & c;
      const temp2 = s0 + maj >>> 0;
      h = g2;
      g2 = f;
      f = e;
      e = d + temp1 >>> 0;
      d = c;
      c = b;
      b = a;
      a = temp1 + temp2 >>> 0;
    }
    h0 = h0 + a >>> 0;
    h1 = h1 + b >>> 0;
    h2 = h2 + c >>> 0;
    h3 = h3 + d >>> 0;
    h4 = h4 + e >>> 0;
    h5 = h5 + f >>> 0;
    h6 = h6 + g2 >>> 0;
    h7 = h7 + h >>> 0;
  }
  return [h0, h1, h2, h3, h4, h5, h6, h7].map((value) => value.toString(16).padStart(8, "0")).join("");
}
function contractHash(body) {
  return sha256Hex(canonicalPreimage(body));
}
export {
  BRAND,
  CODES,
  DEFAULTS,
  DEFAULT_ENHANCED_STYLE_PROPERTIES,
  DEFAULT_STYLE_PROPERTIES,
  EXPLAIN_URL_BASE,
  FactIndex,
  PRESET_NAMES,
  PreviewVariantRuntime,
  RULES,
  SEVERITIES,
  SEVERITY_RANK,
  SEVERITY_WEIGHTS,
  agentFormatSchema,
  aiMetadataSchema,
  analyzeComposition,
  asComponentId,
  blockDefinitionSchema,
  bridgeSourceViolation,
  bridgeSourceViolations,
  budgetBar,
  buildAgentFormat,
  bundleArtifactMetadataSchema,
  bundleComponentShardSchema,
  bundleManifestComponentEntrySchema,
  bundleManifestSchema,
  bundleSchemaVersionSchema,
  bundleTargetSchema,
  bundleTokenFileSchema,
  byCode,
  byRuleId,
  calculateDeltaE,
  canonicalJson,
  canonicalPreimage,
  checkStoryExclusion,
  classifyComplexity,
  colorSimilarity,
  compareColors,
  compareNumericValues,
  compareSeverity,
  compareStyleValue,
  compareStyles,
  compareStylesWithTokens,
  compileBlock,
  compileComponentFacts,
  compileFragment,
  compileGlobalGovernanceFacts,
  compileRecipe,
  componentContractSchema,
  componentGovernanceRecordSchema,
  componentGovernanceRecordsSchema,
  componentId,
  componentRelationSchema,
  compositionMetadataSchema,
  containsTailwindV4Theme,
  contractHash,
  defineBlock,
  defineConfig,
  defineFragment,
  defineRecipe,
  detectSubComponentPaths,
  discoverComponents,
  dtcgTokenFileSchema,
  executeVariantLoaders,
  explainUrlForCode,
  factEvidenceSchema,
  factId,
  factLocationSchema,
  familyDistance,
  figma,
  figmaPropMappingSchema,
  findingFixSchema,
  findingReplaceClassTokenFixSchema,
  findingReplaceComponentFixSchema,
  findingReplaceImportFixSchema,
  findingReplacePropValueFixSchema,
  findingReplaceStyleValueFixSchema,
  findingSchema,
  fixSchema,
  formatBytes,
  formatTokenSummary,
  fragmentBanSchema,
  fragmentContractSchema,
  fragmentDefinitionSchema,
  fragmentDefinitionV2Schema,
  fragmentGeneratedSchema,
  fragmentMetaSchema,
  fragmentProvenanceSchema,
  fragmentUsageSchema,
  fragmentVariantSchema,
  fragmentsConfigSchema,
  g,
  generateCSSCustomProperties,
  generateContext,
  generateFigmaVariables,
  generateSCSSVariables,
  generateTailwindConfig,
  getComplianceBadge,
  getPreviewConfig,
  globalGovernanceRecordSchema,
  globalJsxGovernanceRecordSchema,
  globalStyleGovernanceRecordSchema,
  governanceConfigSchema,
  governanceSeveritySchema,
  governanceVerdictMetadataSchema,
  governanceVerdictSchema,
  governedFragmentDefinitionSchema,
  hash64Hex,
  hexToRgb,
  inferTokenCategory,
  inferTokenGroup,
  isColorLike,
  isConfigExcluded,
  isContractFile,
  isDTCGFile,
  isExportStory,
  isFigmaPropMapping,
  isForceIncluded,
  isReactComponent,
  makeA11yNameRequiredFact,
  makeCanonicalCandidateFact,
  makeCanonicalMappingFact,
  makeCanonicalReplacementFact,
  makeClassNameDynamicFact,
  makeClassNameLiteralFact,
  makeComponentCapabilityFact,
  makeComponentMetadataFact,
  makeFinding,
  makeJsxComponentPreferredFact,
  makeJsxImportPathPreferredFact,
  makeJsxInlineStyleForbiddenRawFact,
  makeJsxUnknownPropsForbiddenFact,
  makePropMetadataFact,
  makePropValueAvoidedFact,
  makePropValueForbiddenFact,
  makeScaleFact,
  makeScaleValueFact,
  makeStructuralConfidenceFact,
  makeStyleCssVarsMustBeDefinedFact,
  makeStyleDeclarationFact,
  makeStyleFontSizeScaleFact,
  makeStylePropertyScaleFact,
  makeStyleRawColorForbiddenFact,
  makeStyleUnsupportedFact,
  makeTailwindClassFact,
  makeTailwindPaletteAllowFact,
  makeTailwindPaletteDenyFact,
  makeTailwindTokenResolvedFact,
  makeTailwindUnknownClassEnabledFact,
  makeThemeDeclarationFact,
  makeTokenDefinitionFact,
  makeUsageComponentFact,
  makeUsageImportFact,
  makeUsageInlineStyleFact,
  makeUsageNodeFact,
  makeUsagePropResolvedFact,
  makeUsageTextChildFact,
  matchesGlob,
  maxSeverity,
  mcpBlockSchema,
  mcpCapabilitySchema,
  mcpComponentExampleSchema,
  mcpComponentGuidanceSchema,
  mcpComponentMetadataSchema,
  mcpComponentPropSchema,
  mcpComponentRelationSchema,
  mcpComponentSchema,
  mcpCompoundChildSchema,
  mcpGuidancePatternSchema,
  mcpPerformanceDataSchema,
  mcpPerformanceImportSchema,
  mcpPerformanceSummarySchema,
  mcpSnapshotMetadataSchema,
  mcpSnapshotSchema,
  mcpSnapshotSchemaVersionSchema,
  mcpSourceTypeSchema,
  mcpTokenDataSchema,
  mcpTokenSchema,
  nearestByDeltaE,
  normalizeColor,
  normalizeFinding,
  normalizeGovernanceConfig,
  normalizeSeverity,
  normalizeStyleValue,
  normalizeToV1,
  normalizeTokenGroupComment,
  normalizeTokenValue,
  normalizeViolation,
  parseColor,
  parseColorToRgb,
  parseComponentContract,
  parseCssTokens,
  parseDtcgTokens as parseDTCGFile,
  parseDesignTokenContent,
  parseDtcgTokens,
  parseRgb,
  parseScssTokens,
  parseScssVariables,
  parseTailwindV4Theme,
  parseTokenFile,
  parseTokens,
  propDefinitionSchema,
  recipeDefinitionSchema,
  resolveArea,
  resolveDesignTokenValue,
  resolveFigmaMapping,
  resolvePerformanceConfig,
  resolvePreviewRuntimeState,
  resolveTokenValue,
  rgbToHex,
  ruleA11yRequiredAccessibleName,
  ruleComponentsForbiddenPropValue,
  ruleComponentsPreferLibrary,
  ruleComponentsUnknownProp,
  ruleJsxPreferredComponent,
  ruleJsxPreferredImportPath,
  rulePropsInvalidValue,
  ruleStylesNoRawColor,
  ruleStylesNoRawDimensions,
  ruleStylesNoRawSpacing,
  ruleStylesNoRawTypography,
  ruleTailwindArbitraryColor,
  ruleTailwindArbitrarySpacing,
  ruleTailwindForbiddenPalette,
  ruleTailwindOffScaleSpacingToken,
  ruleTailwindRawColorViaToken,
  ruleTailwindUnknownClass,
  ruleThemeNoThemeCoupledLiteral,
  ruleTokensRequireDualFallback,
  runRules,
  scaleGovernanceRecordSchema,
  setPreviewConfig,
  severityLevel,
  severitySchema,
  sha256Hex,
  sortBySeverity,
  storyModuleToFragment,
  storyNameFromExport,
  suppressionDirectiveSchema,
  toId,
  usePreviewVariantRuntime,
  validatorResultSchema,
  violationSchema
};
//# sourceMappingURL=index.js.map